import { reportBelongsToSite } from '../../lib/reportSiteIdentity';
import { resolveSiteDocumentFileName, withResolvedSiteDocumentFileName } from '../../lib/site/resolveSiteDocuments';
import { latestExecutiveSummaryForDecision } from '../../lib/savedReportDecisionSource';
import { ensureCanvasAssumptionBridge } from '../../store/canvasAssumptionBridge';
import { isDataCenterFinance } from '../../lib/dataCenterFinance';
import { buildDataCenterFinancialAnalysis } from '../../lib/buildDataCenterFinancialAnalysis';
import { DataCenterFinancialSnapshot } from './financial/DataCenterFinancialSnapshot';
import { DataCenterFinancialScenariosPanel } from './financial/DataCenterFinancialScenariosPanel';
import { UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT } from '../../lib/portfolio/compositeScorer';
import { applySiteStudyArea } from '../../lib/siteStudyArea';
import { intentEditorFieldId } from '../../lib/coverage/intentEditorFieldId';
import { awaitAbortableRead } from '../../lib/abortableRead';
/**
 * SiteLandingPage — single site overview with tabs for docs, reports, simulations.
 */
import React, { lazy, Suspense, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import type { Edge, Node } from 'reactflow';
// RWX-5 — used by the AutoRerunBanner trigger handler below (line
// ~3669) to navigate to the canonical rerun route, AND (C3 fix) by
// the main SiteLandingPage `handleViewReport` wrapper so opening a
// report card also pushes the `/sites/:id/reports/:reportId` URL —
// otherwise useUrlSiteHydration clears the freshly-set activeReportId.
// Coordination lock: do NOT extend usage beyond these two callsites
// (EDX owns the Site Evidence / Documents tab JSX + the FocusKind union).
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  legacyPathToGoRedirectTab,
  parseSiteAppPath,
  reportAuditPath,
  reportReaderPath,
  siteOverviewPath,
  studioFullPath,
} from '../../lib/siteNavigation';
import { isParcelStudioV2Enabled, isPageCoverageMatchEnabled, isBomSimulationGenerationEnabled } from '../../lib/runtimeFlags';
import { isEoEngineActive } from '../../lib/sim/eo/eoEngineFlag';
import { siteOwnerUid } from '../../lib/siteOwnerPath';
import { normalizeSiteEntity } from '../../lib/siteReportLink';
import { useStore } from '../../store';
import { useStableCallback } from '../../hooks/useStableCallback';
import { useShallowStableRecord } from '../../hooks/useShallowStableRecord';
import { structuralEqual } from '../../lib/structuralEqual';
import { openActiveCanvasFromSite } from '../../store/simulationSiteLaunch';
import { siteLaunchLinkNotice } from '../../lib/sim/canvas/siteLaunchLink';
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import { MapPin, FileText, BarChart3, Activity, FolderOpen, Plus, ChevronRight, ChevronLeft, Trash2, CheckCircle2, AlertTriangle, XCircle, Pencil, Check, X, Play, Leaf, Building2, Zap, Droplets, Cable, MoreVertical, Loader2, RefreshCw, Cloud, CloudDownload, CloudOff, Info, Upload, BookOpen, ShieldCheck, GitBranch, Package, Grid3x3, Database, ArrowLeftRight, History, Settings2, ListChecks, Lock, Home, Microscope, Factory, type LucideIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ResponsiveTabStrip } from '../ui/responsive-tab-strip';
import { ReportTypeLaunchCard } from './ReportTypeLaunchCard';
import { ReportGalleryGrid } from './reports/ReportGalleryGrid';
import { NewReportWizard } from './NewReportWizard';
const GenerateSimulationWizard = React.lazy(() =>
  import('./GenerateSimulationWizard').then((m) => ({ default: m.GenerateSimulationWizard }))
);
import { DeleteReportDialog } from './DeleteReportDialog';
import { DeleteSiteDialog } from './DeleteSiteDialog';
import { SiteLockControls, canEditLockedSite } from './SiteLockControls';
import { SiteLocationBoundaryEditor, type SiteLocationBoundaryMode } from './SiteLocationBoundaryEditor';
import { SiteLayerEditor } from './SiteLayerEditor';
import { isSiteLayerSetWriteReady } from '../../lib/siteLayers/canonicalLayerResolution';
import { SED_SECTION_IDS } from '../../types/siteEvidence';
import { RegionalLayersFetchDialog } from './RegionalLayersFetchDialog';
import { AddMapLayersDialog } from './AddMapLayersDialog';
import { SiteMapTab } from './SiteMapTab';
import { SiteHomeTab } from './overview/SiteHomeTab';
import { IntentScreeningRecommendations } from './overview/IntentScreeningRecommendations';
import { FindingsDossier } from './findings';
import { selectDecisionStageReports } from '../../lib/findings/decisionStageReports';
import { resolveCurrentScreeningRecommendationReport } from '../../lib/findings/callReadyFindings';
import type { BriefScreeningReviewData } from './findings/BriefScreeningDecision';
import {
  DecideSurface,
  DECIDE_SURFACE_ID,
  DECIDE_SURFACE_TITLE_ID,
  type DecidePrerequisitePhaseId,
  type DecidePrerequisiteRoute,
  type DecisionHistoryStatus,
} from './decision/DecideSurface';
import {
  ContractorOutreachDialog,
  outreachDocTypeLabel,
  outreachSiteContext,
} from './ContractorOutreachDialog';
import type { SaveAmendmentResult, SEDAmendment } from '../../types/sedAmendments';
import { useEconomicEvidenceResearch } from './findings/useEconomicEvidenceResearch';
import {
  shouldWithholdEconomicWorkforceProfile,
  useEconomicWorkforceProfile,
  verifiedWorkforceProfileFips,
} from './findings/useEconomicWorkforceProfile';
import {
  type EconomicResearchImprovementNotice,
  useEconomicResearchImprovementNotification,
} from './findings/useEconomicResearchImprovementNotification';
import { buildExecutiveDecisionRollup } from '../../lib/executiveDecisionRollup';
import { SiteConfigurationPanel } from './SiteConfigurationPanel';
import { Stage2DecisionsPanel, Stage2ReadyBanner } from './stage2/Stage2DecisionsPanel';
import type { BtmStrategy, Stage2DecisionReview } from '../../lib/stage2/stage2Types';
import { stage2FocusField } from '../../lib/stage2/stage2Catalogue';
import { ReportPreferencesDialog } from './overview/ReportPreferencesDialog';
import { ArchetypeImpactDisclosure } from './ArchetypeImpactDisclosure';
import { latestReportByType } from './SiteCardReportRag';
import { OverviewFindingItem } from './OverviewFindingItem';
import { coverageMatchForDocuments, siteReportTypeScope, useSiteCoverageMatch } from './useSiteCoverageMatch';
import { resolvePathToGoPlannedTypes } from '../../lib/pathToGo/pathToGoPlannedTypes';
import { REPORT_TYPE_REGISTRY, getOfferableReportTypes, type ReportTypeId } from '../../config/reportTypes';
import {
  estimatedReportGenerationDurationMs,
  resolveGenerationCountdownStartedAt,
} from '../../lib/reportGeneration/generationEta';
import { useIsDatacrateAdmin } from '../../lib/isDatacrateAdmin';
import { getDocumentChecklistAcross, type RankedChecklistMatch, type ChecklistItem } from '../../config/documentChecklists';
import { requirementIdForSedField, requirementIdForBareFocusId, requirementFieldPathToVariablePath } from '../../config/requirementToFields';
import { tabStatus, type TabStatusInputs } from '../../lib/pathToGo/tabStatus';
import { resolveAuditClean } from '../../lib/pathToGo/auditStatus';
import {
  buildPathToGoState,
  type PathToGoPhaseId,
} from '../../lib/pathToGo/pathToGoOrchestrator';
import { resolveNextAction } from '../../lib/pathToGo/nextAction';
import { resolveSiteDecisionStage } from '../../lib/stage2/siteDecisionStage';
import type {
  PathAction,
  PathToGoAnalysisSnapshot,
  ReportRuntimeLifecycle,
} from '../../lib/pathToGo/reportPlan';
import { deriveReportRuntimeByType, mergeDecisionRuntimeByType } from '../../lib/pathToGo/reportRuntime';
import {
  buildPathToGoDecisionEvidenceSnapshot,
  pathToGoPositiveDecisionAllowed,
} from '../../lib/pathToGo/decisionSnapshot';
import {
  listPathToGoDecisions,
  reviewPathToGoDecision,
  submitPathToGoDecision,
} from '../../services/pathToGoDecisionRecords';
import type { PathToGoDecisionRecord } from '../../types/pathToGoDecision';
import { buildScreeningDecisionEvidenceSnapshot, screeningPositiveDecisionAllowed } from '../../lib/pathToGo/screeningDecisionSnapshot';
import type { ScreeningDecisionDraft } from './decision/ScreeningDecisionRecordForm';
import {
  type DecisionAnalysisDomain,
  type DecisionAnalysisInputEnvelopeV1,
} from '../../types/decisionAnalysis';
import {
  loadCurrentDecisionAnalyses,
  type CurrentDecisionAnalysis,
} from '../../services/decisionAnalysisClient';
import { loadDecisionAnalysisRevisionCached } from '../../services/decisionAnalysisRevisionCache';
import {
  currentAnalysisInputsFromSnapshots,
  latestAttemptJudgedOnInput,
  pointerReportIdOwnerAnalysisId,
  projectDecisionAnalysesIntoReports,
  revisionWithOperationalRefresh,
  type BrowserDecisionAnalysisSnapshot,
} from '../../lib/decisionAnalysis/browserProjection';
import {
  ANALYSIS_OPS_POLL_INTERVAL_MS,
  ANALYSIS_OPS_STALE_IN_FLIGHT_MS,
  shouldPollAnalysisOperations,
} from '../../lib/decisionAnalysis/analysisOpsPoll';
import { useGatedPoll } from '../../hooks/useGatedPoll';
import { siteHasBoundary } from '../../lib/siteBoundaryGeometry';
import { isSupplyChainPlanUserReady } from '../../lib/supplyChainPlanReady';
import { evaluatePersistedTestFitWorkflow } from '../../lib/generationImpact/testFitWorkflow';
import { roadmapProgress } from '../../lib/audit/recommendationStatus';
import { reportHasOpenAuditFlag } from '../../lib/audit/openAuditFlag';
import { ExportAuditWarningDialog } from './report/ReportHeaderStrip';
import {
  isCallReadyFindingsV1Enabled,
  isEconomicContributionV1Enabled,
  isDecisionFindingsV1Enabled,
  isUnifiedEvidencePanelEnabled,
} from '../../lib/runtimeFlags';
import { groupReportsByLineage, type ReportLineage, countDistinctReportTypes, formatReportTypeCountLabel } from '../../lib/reportLineage';
import { REPORT_CARD_TITLE_CLASS } from './reportCardTypography';
import { resolveSiteCardBranding, DEFAULT_COMPANY_NAME } from '../../lib/reportBranding';
import { useCompanyBranding } from '../../lib/companyBranding';
import { resolveUploadedLogo } from '../../lib/resolveEffectiveLogo';
import { resolveActiveReportTemplate } from '../../lib/resolveActiveReportTemplate';
import { useEffectiveReportTheme } from '../../lib/useEffectiveReportTheme';
import { LogoBackingChip } from './LogoBackingChip';
import { readReportFindings, findingsLabel, readReportStrengths, strengthsLabel } from '../../lib/reportFindings';
import { buildSitePreloadData } from '../../lib/simulation/sitePreload';
import { screenEconomicContributionForSite } from '../../services/economicContributionClient';
import { isFiniteLatLng } from '../../lib/siteBoundaryGeometry';
import { openGoogleEarthWeb, resolveSiteEarthViewCenter } from '../../lib/googleEarthWebUrl';
import { toast } from 'sonner';
import { ContextualGuideTip } from '../reportGuide/ContextualGuideTip';
import { consumeGuidePendingActionForSite } from '../reportGuide/deepLinkActions';
import { SITE_TAB_IDS, type SiteTabId } from './siteTabIds';
import { loadReportGuideProgress, type ReportGuideProgress } from '../reportGuide/reportGuideStore';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { relativeTime } from '../ui/SaveIndicator';
import { detectAutoRerunCandidates } from '../../lib/autoRerunSuggestion';
import { SEDVariableBrowser } from './SEDVariableBrowser';
import { SedRebuildBanner } from './SedRebuildBanner';
import {
  SedRebuildBlockedBanner,
  type SedRebuildBlockedState,
} from './SedRebuildBlockedBanner';
import { SedRebuildLandingCard } from './SedRebuildLandingCard';
import { SedVsContextDiffTab } from './SedVsContextDiffTab';
import type { SedContextDiffRow } from '../../services/siteContext/syncSedContextBindings';
import { CoverageMatrix } from './coverage/CoverageMatrix';
import {
  selectEvidenceSteps,
  type EvidenceVerbKind,
} from '../../lib/pathToGo/evidenceSteps';
import type { NextStep } from '../../lib/pathToGo/nextStep';
import { AuthorEvidenceDialog } from './coverage/AuthorEvidenceDialog';
import { WaiveGapDialog } from './coverage/WaiveGapDialog';
import { deriveIntentForSelectedAssessment } from '../../lib/generationImpact/selectedAssessmentIntent';

const EVIDENCE_READ_TIMEOUT_MS = 30_000;
const EVIDENCE_READ_TIMEOUT_MESSAGE = 'Reading saved evidence took too long. Reload evidence to try again.';

type CurrentEvidenceReadIdentity = {
  siteId: string;
  ownerUid: string;
  sedId: string;
  storagePath?: string;
  version?: number;
};

/**
 * Display names for waiver categories. The stored value is a machine key; a
 * reader of the evidence needs the sentence, because "data-unavailable" and
 * "not-applicable" look alike in a status column and mean very different things.
 */
const WAIVE_CATEGORY_LABELS: Record<string, string> = {
  'data-unavailable': 'The source has no record',
  'not-applicable': 'Does not apply to this site',
  'will-provide': 'Will be provided later',
  'accepted-gap': 'Accepted as a known gap',
};
import { getRequirementLineage } from '../../lib/evidenceLineage/requirementLineage';
// DC-1 — DAM type pulled in for the lazy hydration that backfills the
// `dam` prop on `<CoverageMatrix>`. The DAM is fetched once per
// SiteEvidenceTab mount via the existing `loadCurrentDam` reader
// (Firestore-cached) — mirrors `DamLineageChip`'s lazy-load pattern.
import type { DatacrateModel } from '../../types/datacrateModel';
import { EvidenceConflictsPanel } from './coverage/EvidenceConflictsPanel';
import { evidenceSourceScope } from '../../lib/coverage/evidenceSourceScope';
import type { SiteEvidenceDocument, SedSectionId } from '../../types/siteEvidence';
import { getCachedSedById } from '../../services/sedLoader';
import { siteReportBodiesPending } from '../../lib/reportListHydration';
import {
  selectRetryableSections,
  applyPerVisitCap,
  type RetryableSection,
} from '../../lib/preRetrySweep';
import { DataReadinessPill } from './DataReadinessPill';
import { BootstrapWaitBanner } from './BootstrapWaitBanner';
import { InflightRunProgressStrip } from './InflightRunProgressStrip';
import { findReportDialogDraftsForSite, clearReportDialogDraft } from '../../lib/reportDialogDraft';
import { COMPANY_ARCHETYPES, type CompanyArchetype } from '../../types/company';
import { RefreshEvidencePanel } from './RefreshEvidencePanel';
import { useUrlSearchParamSync } from '../../lib/useUrlSearchParamSync';
import { readLastReportsAreaTab, writeLastReportsAreaTab, type ReportsAreaTab } from '../../lib/lastSubTabMemory';
import { SiteEvidenceControlPanel } from './SiteEvidenceControlPanel';
// SDL-3 — Delete SED confirmation modal. Host owns the show flag +
// the cascade-safety preview values (docCount, reportsPinnedCount).
import { DeleteSedDialog } from './DeleteSedDialog';
// NS-3 SDL-FOLLOWUP-AUTO-SUGGEST-EMPTY-SED — admin-only dismissable
// Alert that surfaces on empty sites (SED on file but zero docs +
// zero reports). Routes to the SDL-1 Delete SED button via scroll +
// focus — non-destructive; the SDL-3 dialog still gates the action.
import { DeleteSedSuggestionAlert } from './DeleteSedSuggestionAlert';
// SHV-2 — SiteHeaderStatusDot import removed (no remaining call site in
// this file). The component file itself is preserved for potential
// re-use in a future compact site-picker surface.
import { SiteLandingAtAGlance } from './SiteLandingAtAGlance';
import { RerunFastPathModal } from './rerun/RerunFastPathModal';
import { getRerunDefaults } from '../../lib/rerunDefaults';
import { SensitivityPanel } from './financial/SensitivityPanel';
import { ScenarioComparePanel } from './financial/ScenarioComparePanel';
import { CrossValidationStrip } from './financial/CrossValidationStrip';
// R-104 chunk-cycle fix (mirrors 5e802c50 / SleekTemplateStudio): FinancialModelEntryPanel
// is STATIC-imported, not lazy. As a lazy() leaf its emitted chunk landed in a circular
// chunk dependency, so in the prod build `import('./financial/FinancialModelEntryPanel')`
// resolved to `undefined` → `m.FinancialModelEntryPanel` threw "Cannot read properties of
// undefined" the moment the Financial tab mounted. Co-locating it in this chunk breaks the
// cross-chunk cycle (within-chunk circular imports are handled by ES live bindings, and the
// component is only read at render time). The R-104 smoke test enforces this stays static.
import { FinancialModelEntryPanel } from './financial/FinancialModelEntryPanel';
// R-104b (2026-06-17): PathwayComparisonSection hit the SAME chunk-cycle→undefined crash
// in v1.6.0 after the combines #81 fold — LcoeChartSection now eagerly pulls the shared
// finDecision/pathway modules into the report chunk, which reorganised the lazy financial
// chunk into a cross-chunk cycle, so `import('./financial/PathwayComparisonSection')`
// resolved `undefined` → `m.PathwayComparisonSection` threw the instant the Financial tab
// (or its pathway block) mounted. Same fix as FinancialModelEntryPanel: STATIC import, not
// lazy. Keep static (the R-104 smoke test guards this).
import { PathwayComparisonSection } from './financial/PathwayComparisonSection';
import { canonicalCore } from '../../lib/financialScenarios';
import { auth as faAuth } from '../../firebase';
import { requireAdminClaim } from '../../lib/requireAdminClaim';
import { SupplyChainPanel } from './supplyChain';
import { SiteAuditTab } from './SiteAuditTab';
import { SiteDeepDiveTab } from './deepdive/SiteDeepDiveTab';
import { RecommendationGraphView } from './RecommendationGraphView';
import { uploadLogo } from '../../services/reportThemeService';
import type { ReportAudienceMode } from '../../types/site';
import { DataConfidenceDrawer } from '../portfolio/DataConfidenceDrawer';
import { VERDICT_WITHHELD_LABEL } from '../../lib/portfolio/decisionBands';
import { deriveSiteVerdict } from '../../lib/portfolio/deriveSiteVerdict';
import {
  computeSiteComposite,
  isSiteCompositeStale,
} from '../../lib/portfolio/compositeScorer';
import { logAuditEvent } from '../../lib/telemetry/auditTelemetry';
import { EngineeringSimCard } from '../sim/EngineeringSimCard';
import { SiteTabSectionHeader } from './SiteTabSectionHeader';
import { EmptyState } from '../ui/aurora';
import { UnifiedLoader } from '../ui/UnifiedLoader';
import { RouteLoadingSkeleton } from '../ui/RouteLoadingSkeleton';
import { retryImport } from './docViewers/retryImport';
import { emptySiteContext } from '../../types/siteContext';
import type { ReadinessGapInputBase, ReadinessGapAction } from '../../lib/coverage/readinessGaps';
import { isStaleSed, computeSedSectionStatuses, SED_SECTION_LABELS, USER_REFRESHABLE_SECTIONS } from '../../lib/sedStaleness';
import { GenerationImpactWorkbench } from './generationImpact/GenerationImpactWorkbench';
import type { GenerationWorkflowStageId } from './generationImpact/GenerationWorkflowNavigator';
import {
  generationImpactTestFitScopeFromSite,
  requiresGenerationImpactTestFit,
} from '../../lib/generationImpact/testFitApplicability';

/**
 * Top-level tabs. Canonical id list + union live in siteTabIds.ts (a pure
 * const leaf shared with the Report Quality Guide's deep links) so the two
 * surfaces cannot drift apart again.
 */
type Tab = SiteTabId;
type CrossTabFocus = { kind: 'doc' | 'variable' | 'req' | 'upload-for' | 'replace' | 'conflicts'; id: string };

// Referentially-stable empty fallbacks for Zustand selectors. A selector's
// return value IS the useSyncExternalStore snapshot (compared via Object.is),
// so a fresh `{}`/`[]` built *inside* a selector flips equality on every
// getSnapshot call → "The result of getSnapshot should be cached" → infinite
// re-render (Maximum update depth). Share one stable instance instead; when the
// fallback must live outside a selector, apply it to the hook's return value.
const STABLE_EMPTY_RECORD: Record<string, never> = {};

/** Every valid top-level tab id (canonical funnel order). Module-stable. */
const ALL_TABS: readonly Tab[] = SITE_TAB_IDS;

const generationImpactEnabled = import.meta.env.DEV
  || import.meta.env.MODE === 'test'
  || import.meta.env.VITE_GENERATION_IMPACT === 'true';

const DocumentLibrary = lazy(retryImport(() =>
  import('./DocumentLibrary').then((m) => ({ default: m.DocumentLibrary })),
));
// FinancialModelEntryPanel + PathwayComparisonSection are statically imported above
// (R-104 / R-104b chunk-cycle fix) — NOT lazy. See the R-104b note at the import site.
/**
 * Resolve a raw `?tab=` / legacy `?view=` pair to a Tab (or null when
 * neither is meaningful). SL2b removed the `?view=` sub-tab param; a
 * copy-pasted `…?tab=reports&view=library` link is migrated to the
 * 'documents' tab and `…?tab=reports&view=evidence` to 'evidence'
 * (and a stray `view=generated` collapses to the 'reports' tab).
 *
 * The retired Path to GO tab (`?tab=pathtogo`, Path to GO removal stage 2)
 * is redirected PERMANENTLY by its `?phase=` — see legacyPathToGoRedirectTab:
 * define → Intent, build → Analyses, verify / decide / none → Decision (or
 * Analyses when the Decision tab is rolled back). The redirect is checked
 * before the visibility filter because the retired id is never visible.
 *
 * `?tab=decision` in a build where the promoted Decision tab is rolled back
 * lands on Analyses / Reports — where the Decide surface then renders
 * (`reports-decide-fallback`) — instead of silently on Home: guide deep
 * links, notifications and bookmarks name `decision` directly.
 */
function resolveTabFromParams(
  t: string | null,
  v: string | null,
  allowedTabs: readonly Tab[] = ALL_TABS,
  phase: string | null = null,
): Tab | null {
  if (t === 'pathtogo') {
    const target = legacyPathToGoRedirectTab(phase, allowedTabs.includes('decision'));
    return allowedTabs.includes(target) ? target : null;
  }
  if (t === 'decision' && !allowedTabs.includes('decision')) {
    return allowedTabs.includes('reports') ? 'reports' : null;
  }
  if (t === 'reports') {
    if (v === 'library') return 'documents';
    if (v === 'evidence') return 'evidence';
    if (v === 'generated' || v == null) return 'reports';
  }
  return t && allowedTabs.includes(t as Tab) ? (t as Tab) : null;
}

/**
 * Pre-Demo 06 (2026-05-26) — Custom Brief cards render the user-defined
 * briefName instead of the generic "CUSTOM BRIEF" label. Falls back to
 * the generic shortLabel when briefName is missing or empty.
 */
function lineageCardTitle(
  typeId: ReportTypeId,
  report: SiteRagReport,
  shortLabel: string,
): string {
  if (typeId === 'custom-brief') {
    const briefName = report.customBriefName?.trim();
    if (briefName) return briefName;
  }
  return shortLabel;
}

/**
 * v1.3.4 (#24) — ONE definitive report per report TYPE.
 *
 * `groupReportsByLineage` returns one ReportLineage per `parentReportId`
 * chain. A single report TYPE can host MORE than one chain when a
 * regeneration produced a fresh root instead of a versioned update
 * (e.g. a `mode: 'add-another'` regen, or a QA-batch run that minted a
 * second job doc with no parent link). Those sibling chains used to
 * render as parallel "Alt #N" cards — so a site showed both
 * "Supply Chain v1.0" AND "Supply Chain v1.0 ALT #2".
 *
 * William's intent: regenerating a type yields a NEW VERSION of the SAME
 * report, not a parallel ALT. So we collapse ALL lineages of a type into
 * ONE card: the freshest report across every chain becomes `latest`, and
 * EVERY other report (siblings included) folds into `history`,
 * newest-first. No report is dropped — genuine version history is fully
 * preserved and reachable from the single card's "N older" expander. The
 * collapsed lineage keeps the root id of the chain that owns `latest` as
 * its stable `lineageId`, so expansion / rerun-highlight / menu state key
 * cleanly.
 *
 * Pure + exported so the Reports-tab grouping test can pin "2 generations
 * of one type → 1 card, 2 versions" without mounting the page.
 */
export function collapseTypeLineagesToDefinitive(
  lineages: ReportLineage[],
): ReportLineage | null {
  if (lineages.length === 0) return null;
  if (lineages.length === 1) return lineages[0];
  // Flatten every report across every chain, then sort newest-first so the
  // single freshest report becomes the visible card and the rest become
  // version history. `groupReportsByLineage` already sorts lineages by their
  // latest createdAt, so lineages[0].latest is the freshest — but flatten +
  // re-sort defensively so a stale createdAt on a sibling can't win the card.
  const all: SiteRagReport[] = [];
  for (const l of lineages) {
    all.push(l.latest, ...l.history);
  }
  all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const latest = all[0];
  // Anchor the collapsed lineage to the root of whichever chain owns the
  // freshest report — that lineageId is already stable across re-renders.
  const owningLineage =
    lineages.find(l => l.latest.id === latest.id || l.history.some(h => h.id === latest.id))
    ?? lineages[0];
  return {
    lineageId: owningLineage.lineageId,
    latest,
    history: all.slice(1),
  };
}

interface Props {
  site: SiteEntity;
  onViewReport: (reportId: string) => void;
  onGenerateReport: () => void;
  onGenerateTypedReport?: (reportTypeId: ReportTypeId) => void;
  /** Called after the site has been deleted via the header menu — host navigates
   *  back to the dashboard / clears active site. */
  onSiteDeleted?: () => void;
  /**
   * One-shot tab override for the initial render. The 3-step wizard sets this
   * to `'reports'` so a freshly-created site lands on the Reports tab with the
   * empty-state CTA visible. The host should remount this component (via a
   * `key={site.id}` on the JSX) so the initialiser re-runs per site.
   */
  initialTab?: Tab;
}

const statusColor: Record<string, string> = {
  green: 'bg-success',
  amber: 'bg-warning',
  red: 'bg-danger',
};

/** Spoken label for a report's RAG status — used as the aria-label on
 *  the small status dots so the colour isn't the only carrier (SL5/SL6,
 *  WCAG 1.4.1). */
const statusWord: Record<string, string> = {
  green: 'Pass',
  amber: 'Conditional',
  red: 'Concern',
};

const statusIcon: Record<string, React.ReactNode> = {
  green: <CheckCircle2 className="w-4 h-4 text-success" />,
  amber: <AlertTriangle className="w-4 h-4 text-warning" />,
  red: <XCircle className="w-4 h-4 text-danger" />,
};

/**
 * Provider-coverage status pill — surfaces the SED bootstrap pipeline's
 * progress (how much of the Site Evidence Document external providers like
 * FEMA / USGS / HEFR have populated) on the site title row. Subscribes to the
 * live site doc so the pill flips without a manual refresh. The 'complete'
 * state auto-hides after 3s so the UI doesn't get cluttered for long-tenured
 * sites.
 *
 * Renamed in AC4 (Doc_Classification_And_Evidence_Labels): the legacy label
 * was "Partial evidence (X% complete)" / "Evidence ready" / "Evidence
 * collection failed" — too easily confused with the DataReadinessPill
 * composite. This pill now leads with "Provider Coverage" and never uses the
 * word "evidence", so the two chips are unambiguously distinct.
 *
 * Exported (not the component default) so the dedicated test file can render
 * the four states in isolation without the rest of the landing page.
 */
export interface SedStatusPillProps {
  status?: SiteEntity['sedStatus'];
  /** 0..1 fraction; displayed in the 'partial' state (and as 100% on 'complete'). */
  completeness?: number;
  /** Called from the 'failed' state's Retry button. The host owns the
   *  bootstrap dispatch so this stays UI-only. */
  onRetry?: () => void;
}

/**
 * AC5 — hover/tap tooltip for the Provider Coverage pill. Explains exactly
 * what it measures and how it differs from the DataReadinessPill so a
 * user never has to click into anything to disambiguate the two chips.
 */
const PROVIDER_COVERAGE_TOOLTIP =
  'Provider Data — percentage of Site Evidence Document sections successfully populated by external ' +
  'providers (FEMA, USGS, HEFR, etc.). Independent of your uploaded documents — see the Evidence ' +
  'chip for the composite that also weighs your documents, document quality, and the site profile.';

export const SedStatusPill: React.FC<SedStatusPillProps> = ({ status, completeness, onRetry }) => {
  const [hideComplete, setHideComplete] = useState(false);

  // Auto-hide the 'complete' state 3s after first arriving at it. Resets if
  // the status flips back away (e.g. user retried after a partial run).
  useEffect(() => {
    if (status !== 'complete') {
      setHideComplete(false);
      return;
    }
    const t = setTimeout(() => setHideComplete(true), 3000);
    return () => clearTimeout(t);
  }, [status]);

  if (!status) return null;
  if (status === 'complete' && hideComplete) return null;

  const base = 'inline-flex items-center gap-1 px-2 py-0.5 rounded type-body font-bold uppercase tracking-widest border';

  // AC5 — the Provider Coverage pill uses a Cloud-icon family ("data coming
  // from outside") to be visually distinct from the DataReadinessPill's
  // BarChart3 (the composite/bar-chart). Distinct icon + distinct vocab +
  // distinct tooltip = no more "are these the same signal?" confusion.
  if (status === 'bootstrapping') {
    return (
      <span className={`${base} bg-state-hover border-border text-text-secondary`} data-testid="sed-status-pill" data-status="bootstrapping" title={PROVIDER_COVERAGE_TOOLTIP}>
        <CloudDownload className="w-2.5 h-2.5 animate-pulse" aria-hidden="true" />
        Collecting from providers…
      </span>
    );
  }

  if (status === 'partial') {
    const pct = typeof completeness === 'number' ? Math.round(completeness * 100) : null;
    return (
      <span
        className={`${base} bg-warning/10 border-warning/40 text-warning`}
        data-testid="sed-status-pill"
        data-status="partial"
        data-coverage-pct={pct !== null ? String(pct) : undefined}
        title={`${PROVIDER_COVERAGE_TOOLTIP}${pct !== null ? ` · ${pct}%` : ''}`}
      >
        <CloudDownload className="w-2.5 h-2.5" aria-hidden="true" />
        Provider Data · Partial
      </span>
    );
  }

  if (status === 'complete') {
    const pct = typeof completeness === 'number' ? Math.round(completeness * 100) : 100;
    return (
      <span
        className={`${base} bg-success/10 border-success/40 text-success`}
        data-testid="sed-status-pill"
        data-status="complete"
        data-coverage-pct={String(pct)}
        title={`${PROVIDER_COVERAGE_TOOLTIP} · ${pct}%`}
      >
        <Cloud className="w-2.5 h-2.5" aria-hidden="true" />
        Provider Data · Complete
      </span>
    );
  }

  // status === 'failed'
  return (
    <span className={`${base} bg-danger/10 border-danger/40 text-danger`} data-testid="sed-status-pill" data-status="failed" title={PROVIDER_COVERAGE_TOOLTIP}>
      <CloudOff className="w-2.5 h-2.5" aria-hidden="true" />
      Provider Data · Failed
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="ml-1 inline-flex items-center gap-0.5 underline hover:text-danger/80"
          data-testid="sed-status-pill-retry"
        >
          <RefreshCw className="w-2.5 h-2.5" />
          Retry
        </button>
      )}
    </span>
  );
};

function notifyEconomicResearchImprovement(notice: EconomicResearchImprovementNotice): void {
  toast.success(notice.title, {
    id: `economic-research-improvement:${notice.runId}`,
    description: notice.description,
  });
}

/**
 * Stable empty operations list. An inline `[]` here was a new array on every
 * render while decision analyses were loading, which invalidated every memo
 * keyed on it (incl. the Path-to-GO state build) on each render.
 */
const NO_DECISION_ANALYSIS_OPERATIONS: CurrentDecisionAnalysis[] = [];
/** Same reason: a fresh `[]` while the history loads was a changed dossier prop. */
const NO_DECISION_RECORDS: PathToGoDecisionRecord[] = [];

/** The three site destinations that render the Findings dossier. */
type DecisionWorkspaceSurface = 'analyses' | 'decision' | 'stakeholder';
type DossierProps = React.ComponentProps<typeof FindingsDossier>;
/** Parameter tuple / first parameter of an (optional) dossier handler prop. */
type DossierHandlerArgs<K extends keyof DossierProps> =
  Parameters<Extract<DossierProps[K], (...args: never[]) => unknown>>;
type DossierHandlerArg<K extends keyof DossierProps> = DossierHandlerArgs<K>[0];

function SiteLandingPageImpl({ site, onViewReport, onGenerateReport, onGenerateTypedReport, onSiteDeleted, initialTab }: Props) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Store-driven mounting can precede BrowserRouter observing a navigation.
  // Wait for this site's route on both surfaces before mirroring state; a
  // stale useSearchParams setter would otherwise replace /sites/:id with the
  // departing /editor path when Back to site mounts this page.
  const siteUrlSyncReady = parseSiteAppPath(location.pathname).siteId === site.id
    && (typeof window === 'undefined' || window.location.pathname === location.pathname);
  // Resolve rollout flags per render instead of freezing them at module import.
  // The promoted destinations ship as one composition:
  // Analyses · Decision · Stakeholder Brief.
  const decisionFindingsEnabled = isDecisionFindingsV1Enabled();
  const callReadyFindingsEnabled = isCallReadyFindingsV1Enabled();
  const simulationComputeEnabled = isEoEngineActive();
  const bomSimulationGenerationEnabled = isBomSimulationGenerationEnabled();
  const splitDecisionSurfacesEnabled = decisionFindingsEnabled
    && callReadyFindingsEnabled;
  const visibleTabs = useMemo(
    () => ALL_TABS.filter((tab) => (
      // The retired Path to GO hub is not a tab id any more (Path to GO
      // removal, stage 3); its old links redirect in resolveTabFromParams.
      (generationImpactEnabled || tab !== 'generation')
      && (simulationComputeEnabled || tab !== 'simulations')
      && (splitDecisionSurfacesEnabled || (tab !== 'decision' && tab !== 'stakeholder'))
    )),
    [simulationComputeEnabled, splitDecisionSurfacesEnabled],
  );
  /** The tab that hosts the live decision + the Decide surface: the promoted
   *  Decision tab, or Analyses / Reports when the split surfaces are rolled
   *  back (Decide then renders under it — see `reports-decide-fallback`). */
  const decisionHomeTab: Tab = splitDecisionSurfacesEnabled ? 'decision' : 'reports';
  const rerunReportId = searchParams.get('rerunReportId');
  const pathwayCompareId = searchParams.get('pathwayCompare');
  const fromPortfolio =
    (location.state as { fromPortfolio?: string } | null)?.fromPortfolio
    ?? searchParams.get('fromPortfolio')
    ?? null;
  // Per-slice selectors — Fix_React_Error_185_Prompt step 3.
  const siteRag = { reports: useStore((s) => s.siteRag.reports) };
  // Datacrate Admin gate — `beta` (partially-implemented) report types are
  // only offered to Datacrate Admins in the picker below; tenant users see
  // active types only. UI hint only (server/rules still authoritative).
  const isDatacrateAdmin = useIsDatacrateAdmin();
  // Persisted audit for THIS site (Firestore-backed, survives sessions), so
  // the Audit tab dot reflects the real last run instead of being hardcoded null.
  const siteAudit = useStore(s => s.auditBySite?.[site.id]);
  const updateSite = useStore(s => s.updateSite);
  const updateSiteTypedPreferencesDefault = useStore(s => s.updateSiteTypedPreferencesDefault);
  const updateSiteDocuments = useStore(s => s.updateSiteDocuments);
  const updateSiteFinancialAssumptions = useStore(s => s.updateSiteFinancialAssumptions);
  const updateSiteFinancialAssumptionsField = useStore(s => s.updateSiteFinancialAssumptionsField);
  const addTab = useStore(s => s.addTab);
  const setViewMode = useStore(s => s.setViewMode);
  const switchTab = useStore(s => s.setActiveTab);
  // SIM-MGMT — additive cloud-sim management actions, reused on the Engineering
  // Analysis tab cards (same actions SavedSimRow uses in FILE ▸ Load Model).
  const deleteCloudSim = useStore(s => s.deleteCloudSim);
  const renameCloudSim = useStore(s => s.renameCloudSim);
  const listSimBackups = useStore(s => s.listSimBackups);
  const restoreSimBackup = useStore(s => s.restoreSimBackup);
  // The active editor tab is what RUN/SOLVE targets — surfaced so the user
  // knows which saved model will solve when multiple tabs are open.
  const activeTabId = useStore(s => s.activeTabId);
  const navigate = useNavigate();
  // Full-page Parcel Test-Fit Studio (`/sites/:id/studio`). The studio lives
  // under the generation tab; on this route we force that tab and hand the
  // workbench a full-screen flag + the open/exit navigations. Path-derived so
  // it's shareable and survives browser back/forward.
  const studioRoute = useMemo(
    () => parseSiteAppPath(location.pathname).isStudio,
    [location.pathname],
  );
  // C3 — opening a report card must ALSO push the canonical report URL.
  // The parent `onViewReport` only sets the store's activeReportId; without
  // a matching URL segment, useUrlSiteHydration immediately re-promotes the
  // site view and clears it, so the report never opens. Navigating to
  // /sites/:id/reports/:reportId lets the hydration effect keep the report.
  const handleViewReport = useCallback(
    (reportId: string) => {
      onViewReport(reportId);
      try {
        navigate(reportReaderPath(site.id, reportId));
      } catch {
        /* navigate no-ops outside a Router (standalone preview surfaces) */
      }
    },
    [navigate, onViewReport, site.id],
  );
  // Reports-gallery "Export" affordance is defined further down, after
  // `siteReports` is available (it needs to resolve the report by id to apply
  // the draft-tier export gate). See `handleExportReport` below.
  // aurora-qa-3 (⑪) — Financial tab is split into 4 chip sub-tabs (Assumptions ·
  // Pathways · Cashflow & Lifecycle · Scenarios & Sensitivity). State lives in
  // the component body (the tab body itself is rendered inside an IIFE, where
  // hooks can't go).
  type FinancialSubTab = 'assumptions' | 'pathways' | 'cashflow' | 'scenarios';
  const [financialSubTab, setFinancialSubTab] = useState<FinancialSubTab>('assumptions');
  const siteComposite = useStore(s => s.portfolio?.composites?.[site.id] ?? null);
  // Redhead RCA (2026-08-11) — the composite-recompute backstop must not run
  // against a still-loading report list (see the RC-1 effect below). The
  // library-wide flag is combined with THIS site's body residency further
  // down, once `siteReports` exists (`reportsLoaded`).
  const isReportsLoaded = useStore(s => s.siteRag.isReportsLoaded);
  const isReportMetadataLoaded = useStore(s => s.siteRag.isReportMetadataLoaded);
  const hydratedReportSiteIds = useStore(s => s.siteRag.hydratedReportSiteIds);
  const ensureSiteReportBodies = useStore(s => s.ensureSiteReportBodies);
  const decisionBandConfig = useStore(s => s.portfolio?.bandConfig);
  const computeCompositeForSite = useStore(s => s.computeCompositeForSite);
  const workspaceCompanyId = useStore(s => s.companyId);
  // ASSIGNED-TEMPLATE BRANDING — the report card branding and the
  // audience-default select labels prefer the site's OWNING company brand
  // (site.companyId, 2026-06-10) and only then fall back to the ASSIGNED
  // template's brand (company name + logo) / the account-level theme.
  // The site has no per-site templateId, so resolve the same session
  // active/default template the dashboard site cards use, honouring a
  // Studio draft override when one is in flight. Falls through the
  // owning-company → template → theme → platform-default chain inside
  // resolveSiteCardBranding.
  // Defensive defaults — many existing tests stub useStore with a minimal
  // slice that omits the template fields. The real store always supplies an
  // array, so falling back to [] / null keeps resolution stable everywhere.
  const reportTemplates = useStore(s => s.reportTemplates) ?? [];
  const activeTemplateId = useStore(s => s.activeTemplateId) ?? null;
  const draftTemplate = useStore(s => s.activeDraftTemplate) ?? null;
  const companyArchetype = useStore(s => s.companyArchetype) ?? null;
  const [customerLogoUploading, setCustomerLogoUploading] = useState(false);
  const [customerLogoProgressPct, setCustomerLogoProgressPct] = useState<number | null>(null);
  const customerLogoInputRef = React.useRef<HTMLInputElement>(null);

  const reportAudienceDefault: ReportAudienceMode = site.reportAudienceDefault ?? 'internal';

  const handleCustomerLogoUpload = React.useCallback(async (file: File) => {
    const user = faAuth.currentUser;
    if (!user) {
      toast.error('Sign in again to upload a customer logo.');
      return;
    }
    setCustomerLogoUploading(true);
    setCustomerLogoProgressPct(0);
    try {
      const nextUrl = await uploadLogo(
        file,
        'user',
        user.uid,
        undefined,
        (p) => setCustomerLogoProgressPct(Math.round((p.progress || 0) * 100)),
      );
      await updateSite(site.id, { customerLogoUrl: nextUrl });
      toast.success('Customer logo saved for this site.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Logo upload failed.';
      toast.error(message);
    } finally {
      setCustomerLogoUploading(false);
      setCustomerLogoProgressPct(null);
    }
  }, [site.id, updateSite]);

  const [customerLogoRemoving, setCustomerLogoRemoving] = useState(false);
  // Task #65 — "customer logo returns after Remove". The old handler was a
  // fire-and-forget `void updateSite(site.id, { customerLogoUrl: null })`:
  // any failure was swallowed (console.warn only) and a stale post-write
  // refresh could resurrect the old URL. We now (a) clear via Firestore's
  // `deleteField()` so the field is truly removed (the codebase-blessed
  // pattern per updateSite's own comment), (b) `await` the write and surface
  // failures via toast, and (c) rely on the store's authoritative-merge fix
  // so a stale cached refresh can no longer revert the cleared value.
  const handleCustomerLogoRemove = React.useCallback(async () => {
    if (customerLogoRemoving) return;
    setCustomerLogoRemoving(true);
    try {
      const { deleteField } = await import('firebase/firestore');
      // `customerLogoUrl` is typed `string | null`; deleteField() is a
      // FieldValue write sentinel. The store sanitises it to a cleared value
      // in optimistic/refresh state so it never reaches the <img src>.
      await updateSite(site.id, {
        customerLogoUrl: deleteField() as unknown as null,
      });
      toast.success('Customer logo removed.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to remove the customer logo.';
      toast.error(message);
    } finally {
      setCustomerLogoRemoving(false);
    }
  }, [customerLogoRemoving, site.id, updateSite]);

  const handleAudienceDefaultChange = React.useCallback(async (next: ReportAudienceMode) => {
    if (next === reportAudienceDefault) return;
    await updateSite(site.id, { reportAudienceDefault: next });
    toast.success(next === 'customer' ? 'Reports default to customer audience.' : 'Reports default to internal audience.');
  }, [reportAudienceDefault, site.id, updateSite]);

  // ── Top-tab routing (SL2b) ──────────────────────────────────────
  //
  // `?tab=` is the only landing-page search param now. The former
  // `?view=` sub-tab param is gone; for backwards compatibility a
  // copy-pasted `…?tab=reports&view=library` link is migrated to
  // `…?tab=documents` and `…?tab=reports&view=evidence` to
  // `…?tab=evidence` (and a stray `view=generated` to `?tab=reports`).
  const initialTabFromUrl = useMemo<Tab | null>(() => {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return resolveTabFromParams(
      params.get('tab'),
      params.get('view'),
      visibleTabs,
      params.get('phase'),
    );
  }, [visibleTabs]);

  // Initial tab precedence: URL ?tab (incl. migrated ?view=) > the
  // `initialTab` prop (wizard hands off 'reports' / 'documents' for
  // fresh sites) > the per-site "last reports-area tab" memory >
  // 'home' default (SITE-HOME — opening a site lands on the Atrium home).
  const initialActiveTab: Tab = (() => {
    if (initialTabFromUrl) return initialTabFromUrl;
    if (initialTab && visibleTabs.includes(initialTab)) return initialTab;
    const remembered = readLastReportsAreaTab(site.id);
    if (remembered && visibleTabs.includes(remembered)) return remembered;
    return 'home';
  })();
  const [activeTab, setActiveTab] = useState<Tab>(initialActiveTab);
  // On the `/sites/:id/studio` route, force the generation tab (the studio's
  // host). Guarded by visibleTabs so a build without generation never lands on
  // a hidden tab.
  useEffect(() => {
    if (studioRoute && visibleTabs.includes('generation') && activeTab !== 'generation') {
      setActiveTab('generation');
    }
  }, [studioRoute, activeTab]);
  /**
   * `/sites/:id/studio` only renders anything when the v2 studio is enabled —
   * which it is not in a production build unless VITE_PARCEL_STUDIO_V2 is set.
   * Without this the URL was accepted, the generation tab was force-selected,
   * the overlay never rendered, and the address bar still read `/studio`: a
   * shared or bookmarked link that silently did nothing.
   *
   * Redirect to the tab the content actually lives on, so the URL never claims
   * a surface the build cannot show.
   */
  useEffect(() => {
    if (studioRoute && !isParcelStudioV2Enabled()) {
      navigate(siteOverviewPath(site.id, 'generation'), { replace: true });
    }
  }, [studioRoute, site.id]);
  const [generationWorkflowInitialStage, setGenerationWorkflowInitialStage] =
    useState<GenerationWorkflowStageId | null>(null);
  useEffect(() => {
    if (activeTab !== 'generation') setGenerationWorkflowInitialStage(null);
  }, [activeTab]);

  /**
   * DAM benchmarks for the Generation & Impact screening prefill.
   *
   * `resolveGenerationScreeningPrefill` has always accepted a `dams` tier and
   * uses it to prefill generation water intensity, data-centre water intensity
   * and (for solar-BESS) plant acres/gross MW from CITED benchmarks. The
   * workbench prop defaulted to `[]` and was never passed here, so that tier
   * has been inert since it was written: the fields silently fell through to
   * "Planning default" and the operator had no way to see that a cited
   * benchmark existed. Loading them restores the intended evidence ladder
   * (document fact > SED > DAM benchmark > planning default).
   *
   * Only the two report types the resolver actually reads are fetched, only
   * while the tab is open, and failures fall through to `[]` — a missing DAM
   * is exactly the pre-existing behaviour, never a blocked workbench.
   */
  const [generationDams, setGenerationDams] = useState<DatacrateModel[]>([]);
  useEffect(() => {
    if (activeTab !== 'generation') return;
    let cancelled = false;
    void (async () => {
      try {
        const mod = await import('../../services/dam/damStorage');
        const loaded = await Promise.all(
          (['water-resource', 'power-availability'] as const)
            .map(reportType => mod.loadCurrentDam(reportType).catch(() => null)),
        );
        if (!cancelled) setGenerationDams(loaded.filter((dam): dam is DatacrateModel => dam != null));
      } catch (error) {
        if (!cancelled) {
          console.debug('[GenerationImpact] DAM hydration failed:', (error as Error)?.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab]);
  // SITE-MAP-TAB-B — scroll active tab into view on mobile strip.
  const tabButtonRefs = React.useRef<Map<Tab, HTMLButtonElement>>(new Map());
  const [highlightedRerunLineageId, setHighlightedRerunLineageId] = useState<string | null>(null);
  const rerunHighlightTimeoutRef = React.useRef<number | null>(null);
  const lastHandledRerunKeyRef = React.useRef<string | null>(null);
  const [overviewDataConfidenceOpen, setOverviewDataConfidenceOpen] = useState(false);
  const requestedCompositeForSiteRef = React.useRef<Record<string, boolean>>({});

  // Persist the reports-area tab the user is on so revisiting this
  // site lands them back where they were instead of bouncing to
  // Home. Only writes for the reports-area tabs — switching
  // to Overview / Engineering Analysis / etc. shouldn't clobber the
  // memory.
  useEffect(() => {
    if (
      activeTab === 'reports'
      || activeTab === 'decision'
      || activeTab === 'stakeholder'
      || activeTab === 'documents'
      || activeTab === 'evidence'
    ) {
      writeLastReportsAreaTab(site.id, activeTab as ReportsAreaTab);
    }
  }, [activeTab, site.id]);

  // SITE-MAP-TAB-B — keep active tab visible in horizontal scroll strip.
  useEffect(() => {
    const el = tabButtonRefs.current.get(activeTab);
    if (!el) return;
    try {
      el.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    } catch {
      /* jsdom may not implement scrollIntoView options */
    }
  }, [activeTab]);

  // A report reader URL (`/sites/:id/reports/:id`) is NOT a landing-page tab
  // surface. Without this guard the mirror below `replaceState`s the landing
  // page's `?tab=` onto the report URL `handleViewReport` just pushed — the
  // effect still runs on the render between the navigate and this component
  // unmounting — producing shared/reloadable links like
  // `/sites/x/reports/y?tab=decision`. Passing null both suppresses the write
  // and strips a stale param an earlier render already stamped.
  const onReportRoute = useMemo(
    () => Boolean(parseSiteAppPath(location.pathname).reportId),
    [location.pathname],
  );

  // Mirror state to URL. The writer skips the default ('home') so
  // the URL stays clean for the common landing path; it always strips
  // the legacy `?view=` param so a migrated deep link doesn't keep it.
  // Deep links to any non-default tab (incl. ?tab=overview) still write +
  // resolve normally via resolveTabFromParams.
  useUrlSearchParamSync('tab', onReportRoute || activeTab === 'home' ? null : activeTab, siteUrlSyncReady);
  useUrlSearchParamSync('view', null, siteUrlSyncReady);
  // `?phase=` only ever meant a Path to GO phase. The retired tab's links are
  // redirected by it on arrival (resolveTabFromParams reads it above, before
  // this writer runs); after that it means nothing, so it is dropped.
  useUrlSearchParamSync('phase', null, siteUrlSyncReady);

  // Browser back / forward — re-read the URL when popstate fires.
  // The writers above use replaceState, so the back-stack only grows
  // when something else (a real navigation, a manual pushState)
  // pushes a new entry; popstate then snaps state back to whatever
  // the popped URL says.
  useEffect(() => {
    const onPop = () => {
      const p = new URLSearchParams(window.location.search);
      setActiveTab(
        resolveTabFromParams(p.get('tab'), p.get('view'), visibleTabs, p.get('phase')) ?? 'home',
      );
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [visibleTabs]);

  const didMountRef = React.useRef(false);
  const previousSiteIdRef = React.useRef(site.id);
  // R-109: when this component instance is reused for another site,
  // force the default tab so the new site always opens home-first.
  // Skip the first mount so explicit deep links (?tab=audit) still win.
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      previousSiteIdRef.current = site.id;
      return;
    }
    if (previousSiteIdRef.current !== site.id) {
      previousSiteIdRef.current = site.id;
      setActiveTab('home');
    }
  }, [site.id]);

  /** Tiny helper so the many "open the document library" call sites
   *  stay a single line. The library is now its own top-level tab. */
  const goToLibrary = () => setActiveTab('documents');
  const [showLibrary, setShowLibrary] = useState(false);

  /**
   * SET-B (B1/B2/B3) — cross-tab focus state. Lives in URL via
   * `?focus=doc:<id>` so deep-links + browser back/forward survive
   * reload. Format = `kind:id` so we can extend (`variable:<path>`)
   * without growing the param namespace.
   *
   * The state is mirrored to the URL by useUrlSearchParamSync; the
   * popstate handler below re-reads it on browser navigation so the
   * breadcrumb + the destination tab's focus receiver stay in step.
   * `kind`+`id` are separate state fields rather than a single
   * "doc:abc" string so the receivers don't have to re-split on every
   * render.
   */
  // EDX-3 + EDX-8 + CMU-2 — extended union. `req` carries a checklist
  // requirement id (e.g. 'geotech', 'phase1-esa') and drives the
  // Documents tab's SED-contribution filter pill (EDX-5). `upload-for`
  // carries the same id namespace but opens the Documents tab upload
  // widget pre-tagged for that requirement. `replace` carries a
  // docId — the Documents tab receiver opens a replace-mode dropzone
  // that lands the new upload with `replacesDocId=<id>` and soft-
  // archives the old doc. `conflicts` carries no id (panel auto-
  // scrolls to first unresolved row) and drives the
  // EvidenceConflictsPanel mounted by EDX-10. `variable:<sectionId>`
  // keeps its SED-section semantics (SEDVariableBrowser scroll).
  type FocusKind = 'doc' | 'variable' | 'req' | 'upload-for' | 'replace' | 'conflicts';
  const parseFocusParam = (raw: string | null): { kind: FocusKind; id: string } | null => {
    if (!raw) return null;
    // `conflicts` is the one kind that carries no id; allow the bare
    // token and a synthetic `conflicts:` form.
    if (raw === 'conflicts' || raw === 'conflicts:') {
      return { kind: 'conflicts', id: '' };
    }
    const idx = raw.indexOf(':');
    if (idx <= 0) return null;
    const kind = raw.slice(0, idx);
    const id = raw.slice(idx + 1);
    if (!id && kind !== 'conflicts') return null;
    if (
      kind === 'doc' ||
      kind === 'variable' ||
      kind === 'req' ||
      kind === 'upload-for' ||
      kind === 'replace' ||
      kind === 'conflicts'
    ) {
      return { kind, id };
    }
    return null;
  };
  const [focus, setFocus] = React.useState<{ kind: FocusKind; id: string } | null>(() => {
    if (typeof window === 'undefined') return null;
    return parseFocusParam(new URLSearchParams(window.location.search).get('focus'));
  });
  // EDX-8 — `conflicts` carries no id so we emit a bare `focus=conflicts`
  // token rather than the colon-suffix form. parseFocusParam accepts
  // both for back-compat in case a deep link is hand-crafted.
  useUrlSearchParamSync(
    'focus',
    focus
      ? focus.kind === 'conflicts'
        ? 'conflicts'
        : `${focus.kind}:${focus.id}`
      : null,
    siteUrlSyncReady,
  );
  // Re-read focus on browser navigation so the breadcrumb + scroll
  // receivers track back/forward correctly. Mirrors the tab popstate
  // handler above so the two URL fields stay coherent.
  useEffect(() => {
    const onPop = () => {
      setFocus(parseFocusParam(new URLSearchParams(window.location.search).get('focus')));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /**
   * GUIDED MODE is retired (Path to GO removal, stage 2): the hub it returned
   * to is gone, so nothing starts it and no Finish bar follows the user. A
   * stale `?guided=1` from an old bookmark, notification or dashboard link is
   * dropped from the URL and means nothing.
   */
  useUrlSearchParamSync('guided', null, siteUrlSyncReady);
  const openGenerationWorkflow = React.useCallback(
    (stage?: GenerationWorkflowStageId) => {
      setGenerationWorkflowInitialStage(stage ?? null);
      setActiveTab('generation');
    },
    [],
  );
  // Extra side-quest inputs the (site, sed, reports, intent) tuple can't see.
  const roadmapGraphForQuests = useStore(
    (s) => s.recommendationGraphBySite?.[site.id]?.graph ?? null,
  );
  const pathToGoExtras = useMemo(
    () => ({
      recommendationGraph: roadmapGraphForQuests,
      generationTabEnabled: generationImpactEnabled,
      simulationTabEnabled: simulationComputeEnabled,
    }),
    [roadmapGraphForQuests, simulationComputeEnabled],
  );

  /** SET-B1 — Documents → Site Evidence bridge. Sets the focus URL
   *  param FIRST so the destination tab's receiver sees the right
   *  scope on its first render, then flips the active tab. */
  const jumpToEvidenceForDoc = React.useCallback((docId: string) => {
    setFocus({ kind: 'doc', id: docId });
    setActiveTab('evidence');
  }, []);
  /** DF-3 — DocLineageInspector → Site Evidence / UFC bridge. The
   *  cross-doc conflict row's "Resolve in UFC →" button calls this
   *  with the conflict's `(sectionId, fieldKey)`. We set the focus
   *  URL param using the UFC `<sectionId>.<fieldKey>` shape so the
   *  EvidenceConflictsPanel scrolls + ring-highlights the matching
   *  group. The variable browser also reads the same shape for its
   *  reciprocal "Resolve →" chip — single source of truth. */
  const jumpToVariable = React.useCallback((sectionId: string, fieldKey: string) => {
    setFocus({ kind: 'variable', id: `${sectionId}.${fieldKey}` });
    setActiveTab('evidence');
  }, []);
  /** SET-B2 — Site Evidence → Documents bridge. Mirror of the above. */
  const jumpToDocumentsForDoc = React.useCallback((docId: string) => {
    setFocus({ kind: 'doc', id: docId });
    setActiveTab('documents');
  }, []);
  /** EDX-3 — Coverage Matrix cell → Documents tab pre-filtered to the
   *  requirement's SED-contribution pill. `requirementId` is a checklist
   *  item id (e.g. 'geotech', 'phase1-esa'). */
  const jumpToDocumentsForReq = React.useCallback((requirementId: string) => {
    setFocus({ kind: 'req', id: requirementId });
    setActiveTab('documents');
  }, []);
  /** EDX-3 — Coverage Matrix Missing cell → Documents tab upload widget
   *  pre-tagged for the requirement. */
  const jumpToDocumentsUploadFor = React.useCallback((requirementId: string) => {
    setFocus({ kind: 'upload-for', id: requirementId });
    setActiveTab('documents');
  }, []);
  /**
   * CMU-2 — Coverage Matrix populated-row `[Replace]` → Documents tab
   * replace-mode dropzone. The focus param encodes the docId being
   * replaced; the requirementId is appended after `::` so the
   * receiver can ALSO pre-apply the SED-contribution filter pill
   * AND tag the new upload's `intendedRequirementIds` (carries the
   * URE-2 intent forward through the version chain).
   *
   * Example focus token: `replace:doc-abc123::utility-letter`.
   */
  const jumpToDocumentsReplaceDoc = React.useCallback(
    (docId: string, requirementId: string) => {
      setFocus({ kind: 'replace', id: `${docId}::${requirementId}` });
      setActiveTab('documents');
    },
    [],
  );
  /**
   * CMU-3 — Coverage Matrix populated-row `[+ Add supporting]` →
   * Documents tab upload widget pre-tagged for the requirement. Same
   * underlying bridge as `jumpToDocumentsUploadFor`; the distinct
   * handler exists so future hosts can surface a different label /
   * help-text without forking the upload-for receiver.
   */
  const jumpToDocumentsAddSupporting = React.useCallback(
    (requirementId: string) => {
      setFocus({ kind: 'upload-for', id: requirementId });
      setActiveTab('documents');
    },
    [],
  );
  /** SET-B3 — breadcrumb actions. Clear strips the focus param;
   *  back-to-other flips the tab while keeping the focus context. */
  const clearFocus = React.useCallback(() => setFocus(null), []);
  const crossTabFocus = React.useMemo<CrossTabFocus | null>(
    () => {
      if (!focus) return null;
      return { kind: focus.kind, id: focus.id };
    },
    [focus],
  );
  /** SET-B3 — flip between Documents ↔ Site Evidence while keeping
   *  the focus context (the URL `?focus=` param). Used by the
   *  breadcrumb's "Back to {otherTab}" button only — actions that
   *  intentionally drop focus (clicking a sibling tab) go through
   *  the normal `setActiveTab` path which doesn't touch focus. */
  const flipFocusTab = React.useCallback(() => {
    setActiveTab(prev => prev === 'evidence' ? 'documents' : 'evidence');
  }, []);

  const [showReportTypePicker, setShowReportTypePicker] = useState(false);
  const [powerPreferencesOpen, setPowerPreferencesOpen] = useState(false);
  // When the user clicks an empty-state report-type card, we preselect that
  // type for the wizard so it jumps straight to preferences. Cleared on close
  // so the next "+ New Report" click lands on the type-select step.
  const [pickerInitialType, setPickerInitialType] = useState<ReportTypeId | undefined>(undefined);
  // U12 — true when the wizard was opened via an "Add report" / new-report
  // affordance (always start fresh on the Type step, never resume a stuck or
  // prior generation). The inflight "View details" path leaves this false.
  const [wizardForceNew, setWizardForceNew] = useState(false);
  const activeGenerations = useStore(s => s.siteRag.activeGenerations ?? STABLE_EMPTY_RECORD);
  const generationOutcomes = useStore(s => s.siteRag.generationOutcomes ?? STABLE_EMPTY_RECORD);
  const cancelReportGenerationJob = useStore(s => s.cancelReportGenerationJob);
  const siteInflightRuns = useMemo(
    () => Object.values(activeGenerations).filter(r => r.siteId === site.id),
    [activeGenerations, site.id],
  );
  // FIX #94 — terminal outcomes for THIS site, indexed for fast lookup.
  // generationOutcomes only ever holds TERMINAL jobs (succeeded/failed
  // recorded by the store's applyTerminalJob), so a draft whose run resolved to
  // an entry here is DONE — synthesizing a "Continuing generation… 0%" run for
  // it below would be a phantom that never clears. We index two ways:
  //   - by runId (precise): the draft's runId === the outcome's run key.
  //   - by reportType (fallback): only used for drafts that carry NO runId
  //     (legacy / pre-runId drafts), so we don't accidentally suppress a
  //     legitimate NEW generation of a type that previously completed.
  const terminalOutcomes = useMemo(() => {
    const byRunId = new Set<string>();
    const typesNoRunId = new Set<string>();
    for (const outcome of Object.values(generationOutcomes)) {
      if (outcome.siteId !== site.id) continue;
      if (outcome.id) byRunId.add(outcome.id);
      if (outcome.reportTypeId) typesNoRunId.add(outcome.reportTypeId);
    }
    return { byRunId, typesNoRunId };
  }, [generationOutcomes, site.id]);
  // Merge sessionStorage generating drafts until Firestore hydrates activeGenerations.
  const displayInflightRuns = useMemo(() => {
    const runs = [...siteInflightRuns];
    const typesWithRuns = new Set(runs.map(r => r.reportTypeId).filter(Boolean));
    // William QA (2026-06-17) — a "generating" sessionStorage draft is only a
    // brief BRIDGE shown until the live run (Firestore job / store
    // activeGenerations) hydrates. If no live run ever appears and the draft
    // lingers past this budget, the server job died (or finished without leaving
    // a terminal record this client can sweep on), and we were left rendering a
    // permanent "Continuing generation… 0%" ghost card. Sweep it. (The 30-min
    // hard sweep in findReportDialogDraftsForSite was too slow to catch this.)
    const PHANTOM_STALE_MS = 10 * 60 * 1000;
    for (const draft of findReportDialogDraftsForSite(site.id)) {
      if (draft.step !== 'generating') continue;
      if (typesWithRuns.has(draft.reportType)) continue;
      if (Date.now() - draft.savedAt > PHANTOM_STALE_MS) {
        clearReportDialogDraft(site.id, draft.reportType);
        continue;
      }
      // FIX #94 — the draft's job already reached a terminal state but the
      // draft was never swept (e.g. the terminal Firestore change fired while
      // this draft lingered in sessionStorage). Without this guard we'd
      // synthesize a phantom run stuck at 0% forever. A genuinely in-flight gen
      // (no matching terminal outcome, no live run) still falls through and
      // renders. Match by runId when the draft has one; only fall back to the
      // by-type signal for runId-less legacy drafts so a NEW generation of a
      // previously-completed type is never suppressed.
      const isTerminal = draft.runId
        ? terminalOutcomes.byRunId.has(draft.runId)
        : terminalOutcomes.typesNoRunId.has(draft.reportType);
      if (isTerminal) {
        clearReportDialogDraft(site.id, draft.reportType);
        continue;
      }
      typesWithRuns.add(draft.reportType);
      runs.push({
        id: draft.runId ?? `draft-${draft.reportType}`,
        siteId: site.id,
        reportTypeId: draft.reportType,
        startedAt: new Date(draft.savedAt).toISOString(),
        progress: 0,
        progressStage: 'Continuing generation…',
        // Persisted drafts use a string type; unknown legacy IDs keep the
        // standard ETA rather than being passed as a typed report ID.
        estimatedDurationMs: estimatedReportGenerationDurationMs(
          Object.values(REPORT_TYPE_REGISTRY).find(type => type.id === draft.reportType)?.id ?? 'feasibility',
        ),
      });
    }
    return runs;
  }, [site.id, siteInflightRuns, terminalOutcomes]);
  const inflightRunByType = useMemo(() => {
    const map: Record<string, (typeof displayInflightRuns)[number]> = {};
    for (const run of displayInflightRuns) {
      if (run.reportTypeId) map[run.reportTypeId] = run;
    }
    return map;
  }, [displayInflightRuns]);
  // Workflow Prompt 5 — expansion now keys on lineageId (each lineage is its own row).
  const [expandedLineageId, setExpandedLineageId] = useState<string | null>(null);
  // SL5 — which lineage row's [⋮] overflow menu is open (one at a time).
  const [reportMenuLineageId, setReportMenuLineageId] = useState<string | null>(null);
  // Workflow Prompt 7 — DeleteReportDialog state.
  const [reportToDelete, setReportToDelete] = useState<SiteRagReport | null>(null);
  // BoM→Simulation wizard (Generate simulation) open state.
  const [generateWizardOpen, setGenerateWizardOpen] = useState(false);
  // Site-delete header menu + dialog state.
  const [showSiteDelete, setShowSiteDelete] = useState(false);
  useEffect(() => {
    const handler = () => setShowSiteDelete(true);
    window.addEventListener('datacrate:delete-site', handler);
    return () => window.removeEventListener('datacrate:delete-site', handler);
  }, []);
  // SDL-3 — Delete SED dialog state lives inside the SiteEvidenceTab
  // subcomponent (near the toolbar mount it gates) so the host doesn't
  // need to thread setters through the SiteEvidenceTab props.
  // Location / boundary editor — opened from the ⋮ menu, the pencil next
  // to the address line, or the Site Map tab action rail. `null`
  // when closed; otherwise carries which mode to open in.
  //
  // KMU — `'boundary-import'` is an internal sentinel: it widens the
  // external SiteLocationBoundaryMode contract from 'location' | 'boundary'
  // to a third option that means "open in boundary mode AND pre-snap the
  // inner tab to Import". The mount site below translates the sentinel
  // back into the editor's two-prop shape (initialMode='boundary',
  // initialBoundaryView='import'), so SiteLocationBoundaryEditor's
  // external API stays unchanged.
  const [locationBoundaryEditorMode, setLocationBoundaryEditorMode] =
    useState<SiteLocationBoundaryMode | 'boundary-import' | null>(() => site.studyArea && site.studyArea.included.length === 0 ? 'study' : null);
  const [regionalLayersDialogOpen, setRegionalLayersDialogOpen] = useState(false);
  const [layerEditorOpen, setLayerEditorOpen] = useState(false);
  const openLayerEditorIfReady = useCallback(() => {
    if (!isSiteLayerSetWriteReady(site)) {
      toast.error(
        site._siteLayersHydrationError
          ? 'Site layers failed to load completely. Refresh the site before editing layers.'
          : 'Site layers are still loading. Wait for layer hydration to finish before editing layers.',
      );
      return;
    }
    setLayerEditorOpen(true);
  }, [site]);
  // Unified geo import (KMZ / GeoJSON / shapefile / DWG). `initialFiles`
  // carries a drag-drop payload from the map frame into the dialog.
  const [addLayersDialogOpen, setAddLayersDialogOpen] = useState(false);
  const [addLayersInitialFiles, setAddLayersInitialFiles] = useState<File[] | null>(null);
  const [guideProgress, setGuideProgress] = useState<ReportGuideProgress>(() =>
    loadReportGuideProgress(faAuth.currentUser?.uid ?? null),
  );
  const [guideUid, setGuideUid] = useState<string | null>(faAuth.currentUser?.uid ?? null);
  const [intentFieldFocus, setIntentFieldFocus] = useState<string | null>(null);
  // Which Intent view shows: the screening brief or Design & report
  // assumptions (where the Stage 2 decisions live). Owned here so "Do this
  // next" and the evidence worklist can deep-link into the Design view.
  const [intentInputView, setIntentInputView] = useState<'screening' | 'detailed'>('screening');
  React.useEffect(() => {
    const listen = faAuth.onAuthStateChanged;
    if (typeof listen !== 'function') return;
    const unsub = listen.call(faAuth, user => {
      const uid = user?.uid ?? null;
      setGuideUid(uid);
      setGuideProgress(loadReportGuideProgress(uid));
    });
    return () => unsub();
  }, []);

  // ── Site edit-lock (#82) — read-only gating. ──────────────────────────
  // A locked site is whole-site read-only for everyone EXCEPT the holder
  // (uid === lockedByUid), a platform-admin (isAdmin claim), or a
  // company-admin (companyRole === 'admin'). This mirrors the store's
  // `assertSiteWritable` guard so disabled affordances and write rejections
  // line up. Zustand rule: subscribe to the `companyRole` scalar root only.
  const lockCompanyRole = useStore(s => s.companyRole);
  const lockIsCompanyAdmin = lockCompanyRole === 'admin';
  const [lockIsPlatformAdmin, setLockIsPlatformAdmin] = useState(false);
  React.useEffect(() => {
    let alive = true;
    void requireAdminClaim()
      .then(ok => { if (alive) setLockIsPlatformAdmin(ok); })
      .catch(() => { /* fall through to non-admin (least privilege) */ });
    return () => { alive = false; };
  }, []);
  const canEditSite = canEditLockedSite(site, guideUid, {
    isAdmin: lockIsPlatformAdmin,
    isCompanyAdmin: lockIsCompanyAdmin,
  });
  // True only when the lock is actively blocking THIS viewer.
  const lockReadOnly = site.locked === true && !canEditSite;
  // Identify the holder for the banner copy.
  const lockHolderLabel = site.lockedByName || site.lockedByEmail || 'another user';

  // #89 — close the #82 lock gaps on the landing page. The name pencil
  // was already `disabled={lockReadOnly}`; this helper extends the same
  // gate to EVERY edit affordance that opens an editor / wizard / upload
  // (config saves, add/remove actions, upload buttons, map-tab editors).
  // Wrapping an opener with `guardLockedAction` makes it a no-op + toast
  // for a locked non-holder, mirroring the store's `assertSiteWritable`
  // so the UI never silently appears to mutate a locked site. The
  // store/service guards remain the authoritative backstop; this is the
  // matching UX layer. Holders / admins (`lockReadOnly === false`) are
  // unaffected.
  const guardLockedAction = useCallback(
    <A extends unknown[]>(action: (...args: A) => void) =>
      (...args: A): void => {
        if (lockReadOnly) {
          toast.error(`This site is locked for editing by ${lockHolderLabel}. Request edit access to make changes.`);
          return;
        }
        action(...args);
      },
    [lockReadOnly, lockHolderLabel],
  );

  // FILE-menu Lock/Unlock dispatcher (`datacrate:toggle-site-lock`, fired by
  // App.tsx, mirroring Delete Site). The listener owns the lock/unlock
  // decision: toggle when the viewer may write, otherwise no-op (the header
  // controls surface request-access / force-unlock instead).
  const lockSite = useStore(s => s.lockSite);
  const unlockSite = useStore(s => s.unlockSite);
  React.useEffect(() => {
    const handler = () => {
      if (site.locked === true) {
        // Only the holder / admins may unlock from the menu; others must use
        // Request edit access (header control), so the menu action no-ops.
        if (canEditSite) void unlockSite(site.id);
      } else {
        void lockSite(site.id);
      }
    };
    window.addEventListener('datacrate:toggle-site-lock', handler);
    return () => window.removeEventListener('datacrate:toggle-site-lock', handler);
  }, [site.id, site.locked, canEditSite, lockSite, unlockSite]);

  React.useEffect(() => {
    const followUp = consumeGuidePendingActionForSite(site.id);
    if (!followUp) return;
    if (followUp.openBoundary) {
      setLocationBoundaryEditorMode(followUp.openBoundary);
    }
    if (followUp.openLayerEditor) {
      openLayerEditorIfReady();
    }
    if (followUp.openReportWizard) {
      setWizardForceNew(true);
      setShowReportTypePicker(true);
    }
  }, [site.id, openLayerEditorIfReady]);
  // KMU — single Export KMZ handler shared by every surface that mounts
  // KmzActionsMenu (Site Evidence toolbar, SiteLandingAtAGlance). Keeping
  // one call site keeps the KMU-AUDIT "one exporter call site in this
  // host" invariant; the in-report Site Boundary card has its own,
  // pre-existing exporter handler in SiteRagReportView that operates on
  // the report's snapshotted site, which is the correct behaviour there.
  const handleExportKMZ = useCallback(async () => {
    try {
      const [{ exportSiteAsKmz }, { saveAs }] = await Promise.all([
        import('../../services/kmzExporter'),
        import('file-saver'),
      ]);
      const out = await exportSiteAsKmz(site);
      saveAs(out.blob, out.filename);
      toast.success(`Exported ${out.filename}. Import it in Google Earth Web to view in 3D.`);
    } catch (e) {
      toast.error(`KMZ export failed: ${(e as Error).message}`);
    }
  }, [site]);
  // KMU — single Update KMZ handler shared by every surface. Opens the
  // boundary editor pre-snapped to the Import dropzone.
  const handleUpdateKMZ = useCallback(() => {
    // #89 — updating the KMZ rewrites the site boundary (a write); gate
    // the shared opener for a locked non-holder so every surface that
    // wires `onUpdateKMZ` (at-a-glance card, Site Evidence toolbar) is
    // covered. Export / Open-in-Earth stay available (read-only).
    if (lockReadOnly) {
      toast.error(`This site is locked for editing by ${lockHolderLabel}. Request edit access to make changes.`);
      return;
    }
    setLocationBoundaryEditorMode('boundary-import');
  }, [lockReadOnly, lockHolderLabel]);
  const handleOpenGoogleEarth = useCallback(() => {
    const center = resolveSiteEarthViewCenter(site);
    if (!center) {
      toast.error('Draw or import a boundary first.');
      return;
    }
    openGoogleEarthWeb(center);
  }, [site]);
  // Non-destructive stale-evidence prompt — set after a location change so
  // the host can offer a re-collect. Holds the new address for the banner
  // copy; cleared when the user dismisses or fires the re-collect.
  const [staleEvidenceAddress, setStaleEvidenceAddress] = useState<string | null>(null);
  const [reCollecting, setReCollecting] = useState(false);
  const [cloudSims, setCloudSims] = useState<any[]>([]);
  const [cloudSimsLoaded, setCloudSimsLoaded] = useState(false);
  // Unified simulations list: cloud sims + any in-memory-only tabs. Hoisted to
  // the component body — it was previously declared with `useMemo` *inside* the
  // `activeTab === 'simulations' && (() => { … })()` IIFE, so the hook only ran
  // when the Engineering Analysis tab was active. Toggling to/from that tab
  // changed the hook count between renders → React error #310 ("Rendered more
  // hooks than during the previous render"). Hooks must run unconditionally.
  const allSims = useMemo(() => {
    const items: {
      id: string;
      name: string;
      cloudId?: string;
      tabId?: string;
      version?: number;
      createdAt?: string;
      isInMemory: boolean;
      sourceBoP?: string;
      repairMode?: boolean;
      eoCapability?: string;
    }[] = [];
    const seen = new Set<string>();
    // Cloud sims first
    for (const cs of cloudSims) {
      seen.add(cs.id);
      const tab = useStore.getState().tabs.find(t => t.cloudId === cs.id);
      items.push({
        id: cs.id,
        name: cs.name || 'Analysis',
        cloudId: cs.id,
        tabId: tab?.id,
        version: cs.version,
        createdAt: cs.createdAt?.toDate?.()?.toISOString() || cs.createdAt,
        isInMemory: !!tab,
        sourceBoP: tab?.sourceBoP ?? cs.sourceBoP,
        repairMode: cs.repairMode === true,
        eoCapability: typeof cs.eoCapability === 'string' ? cs.eoCapability : undefined,
      });
    }
    // In-memory tabs not yet in cloud
    for (const simId of site.simulationIds ?? []) {
      const tab = useStore.getState().tabs.find(t => t.id === simId);
      if (tab?.cloudId && seen.has(tab.cloudId)) continue;
      if (tab) items.push({
        id: simId,
        name: tab.name,
        tabId: tab.id,
        cloudId: tab.cloudId,
        version: tab.version,
        isInMemory: true,
        sourceBoP: tab.sourceBoP,
      });
    }
    return items;
  }, [cloudSims, site.simulationIds]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState(site.name);

  // Live subscription to the site doc so the SED status pill flips without a
  // manual refresh. The bootstrap pipeline writes its progress to the same
  // doc fields (sedStatus / sedCompleteness) — onSnapshot picks them up the
  // moment they land. Falls back to the prop's values when the snapshot is
  // still warming up.
  const [liveSed, setLiveSed] = useState<Pick<
    SiteEntity,
    | 'sedStatus'
    | 'sedCompleteness'
    | 'sedId'
    | 'sedVersion'
    | 'sedStoragePath'
    | 'sedComputedAt'
    | 'sedFailureReason'
    | 'sedSectionCheckedAt'
    | 'siteConfigUpdatedAt'
    | 'generationAssessmentSelection'
    | 'projectIntent'
    | 'promotedGenerationScenarioSnapshot'
    | 'generationImpactLayoutHead'
    | 'economicContributionSnapshot'
    | 'economicProfileSnapshot'
    | 'economicResearchSnapshotPointer'
    | 'draftOnly'
  > | null>(null);
  useEffect(() => {
    let active = true;
    let unsub: (() => void) | null = null;
    (async () => {
      try {
        const { auth, db } = await import('../../firebase');
        const user = auth.currentUser;
        if (!user || !active) return;
        const { doc, onSnapshot } = await import('firebase/firestore');
        unsub = onSnapshot(
          doc(db, `users/${site._ownerUid ?? user.uid}/sites`, site.id),
          snap => {
            if (!snap.exists()) return;
            // Its own listener, outside the store's normalizeSiteEntity path —
            // decode the promoted scenario here or workflowSite renders a
            // Firestore-encoded body.
            const data = normalizeSiteEntity(snap.data() as SiteEntity);
            const next: NonNullable<typeof liveSed> = {
              sedStatus: data.sedStatus,
              sedCompleteness: data.sedCompleteness,
              sedId: data.sedId,
              sedVersion: data.sedVersion,
              sedStoragePath: data.sedStoragePath,
              sedComputedAt: data.sedComputedAt,
              sedFailureReason: data.sedFailureReason,
              sedSectionCheckedAt: data.sedSectionCheckedAt,
              siteConfigUpdatedAt: data.siteConfigUpdatedAt,
              generationAssessmentSelection: data.generationAssessmentSelection,
              projectIntent: data.projectIntent,
              promotedGenerationScenarioSnapshot: data.promotedGenerationScenarioSnapshot,
              generationImpactLayoutHead: data.generationImpactLayoutHead,
              economicContributionSnapshot: data.economicContributionSnapshot,
              economicProfileSnapshot: data.economicProfileSnapshot,
              economicResearchSnapshotPointer: data.economicResearchSnapshotPointer,
              draftOnly: data.draftOnly,
            };
            // IDENTITY — this listener fires on EVERY write to the site doc
            // (bootstrap progress stamps, fields this page never reads). Keep
            // the previous object when the picked fields are unchanged, so
            // `workflowSite` and everything memoised on it — the Path-to-GO
            // build included — hold their references.
            setLiveSed(prev => (prev && structuralEqual(prev, next) ? prev : next));
          },
          err => console.warn('[SiteLandingPage] site doc subscription error:', err),
        );
      } catch (e) {
        console.warn('[SiteLandingPage] failed to subscribe to site doc:', (e as Error)?.message);
      }
    })();
    return () => {
      active = false;
      if (unsub) unsub();
    };
  }, [site.id, site._ownerUid]);

  const sedStatus = liveSed?.sedStatus ?? site.sedStatus;
  const sedCompleteness = liveSed?.sedCompleteness ?? site.sedCompleteness;
  // The SED pointer fields (sedId / sedVersion / sedStoragePath) must ALSO track
  // the live site doc, not just the `site` prop. After a (first) rebuild the
  // bootstrap writes these to the site doc, but the prop stays stale until the
  // parent re-fetches — so the SED loader below (keyed on the prop) never
  // re-fired and the "Build your Site Evidence Document" empty state stuck until
  // a hard reload. Preferring the live values makes the loader re-run the moment
  // the new sedId lands.
  const liveSedId = liveSed?.sedId ?? site.sedId;
  const liveSedVersion = liveSed?.sedVersion ?? site.sedVersion;
  const liveSedStoragePath = liveSed?.sedStoragePath ?? site.sedStoragePath;
  const workflowSite = useMemo<SiteEntity>(
    () => liveSed ? { ...site, ...liveSed } : site,
    [liveSed, site],
  );
  const economicContributionEnabled = isEconomicContributionV1Enabled();
  const workforceProfile = useEconomicWorkforceProfile({
    site: workflowSite,
    // A canonical-location check starts only when Stakeholder Brief is open. It is not
    // coupled to report-body hydration or the project's MW/jobs screen.
    enabled: activeTab === 'stakeholder' && callReadyFindingsEnabled && economicContributionEnabled,
  });
  const findingsSite = useMemo<SiteEntity>(() => {
    // A workforce profile is county-specific. Do not flash a saved profile
    // from the site's prior coordinates while the canonical server check is
    // pending, and suppress both the dedicated and legacy embedded copies if
    // a known county mismatch exists.
    if (shouldWithholdEconomicWorkforceProfile(workforceProfile)) {
      return {
        ...workflowSite,
        economicProfileSnapshot: undefined,
        ...(workflowSite.economicContributionSnapshot
          ? {
              economicContributionSnapshot: {
                ...workflowSite.economicContributionSnapshot,
                economicProfile: null,
              },
            }
          : {}),
      };
    }
    return workforceProfile.profile
      ? { ...workflowSite, economicProfileSnapshot: workforceProfile.profile }
      : workflowSite;
  }, [workforceProfile, workflowSite]);
  const economicResearch = useEconomicEvidenceResearch({
    site: workflowSite,
    // Research remains independent of report-body hydration, but a site page
    // outside Stakeholder Brief must not initiate or continue a paid background run.
    enabled: activeTab === 'stakeholder' && callReadyFindingsEnabled && economicContributionEnabled,
  });
  useEconomicResearchImprovementNotification(
    economicResearch.run,
    notifyEconomicResearchImprovement,
  );

  const handleRetryBootstrap = async () => {
    try {
      // Single shared entry point — same action the failed-SED preflight
      // banner in ReportPreferencesStep dispatches, so retry semantics
      // (sedStatus stamp, sedFailureReason clear, fire-and-forget
      // bootstrap) stay consistent across both surfaces.
      await useStore.getState().retrySEDBootstrap(site.id);
      toast.success('Retrying evidence collection…');
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Failed to retry: unknown error');
    }
  };

  /**
   * Re-collect the SED after a location change. Reuses the same
   * `refreshSedSections` store action the "Refresh evidence" popover and
   * the rerun panels use — passes every section id (the action drops the
   * provider-less ones internally), so this re-fetches climate / grid /
   * seismic / flood / … at the site's new coordinates. Non-destructive:
   * the existing SED is merged, not wiped.
   */
  const handleReCollectEvidence = async () => {
    if (reCollecting) return;
    setReCollecting(true);
    try {
      await useStore.getState().refreshSedSections(site.id, [...SED_SECTION_IDS]);
      toast.success('Re-collecting site evidence for the new location…');
      setStaleEvidenceAddress(null);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Failed to re-collect evidence: unknown error');
    } finally {
      setReCollecting(false);
    }
  };

  /**
   * Re-collect ONE provider-backed SED section — the ledger's "Re-run
   * collection" verb.
   *
   * That button used to `toast.info("… Use Rebuild SED to retry the provider")`
   * and navigate to the tab the user was already on. On Redhead that is the #1
   * GO-blocker, so the single task standing between a site and a verdict named
   * an action it did not perform and sent the user elsewhere to find it.
   *
   * Nothing new is invented here: `refreshSedSections` → `topUpSiteSed({
   * onlyProviders })` hits the scoped branch that passes `forceRefresh`,
   * `rateLimitMs: 0` and `cacheTtlMs: 0`, so the TTL, the Firestore cache and
   * the 60s in-memory rate-limit are all bypassed and the provider genuinely
   * re-runs. `PsePrefetchCta` already called exactly this for publicSentiment.
   *
   * Reports the OUTCOME rather than the attempt. `preflightCheck` promises the
   * user "if it stays empty after a refresh, the provider is failing and needs
   * attention rather than another rebuild" — so when the run completes but
   * upgrades nothing, say that, instead of a success toast the evidence
   * contradicts. Re-running a failing provider a second time is the loop that
   * copy exists to break.
   */
  const handleRecollectSection = async (sectionId: SedSectionId, stepLabel: string) => {
    if (reCollecting) return;
    setReCollecting(true);
    // The ledger's step label already carries its state ("… — not collected"),
    // which reads wrong inside a progress line. Use the canonical section name.
    const label = SED_SECTION_LABELS[sectionId] ?? stepLabel;
    const dismiss = toast.loading(`Re-running collection for ${label}…`);
    try {
      const result = await useStore.getState().refreshSedSections(site.id, [sectionId]);
      toast.dismiss(dismiss);
      if (!result.ok) {
        toast.error(result.reason ?? 'The refresh failed.');
        return;
      }
      if (result.upgraded.includes(sectionId)) {
        // Collected is not the same as KEPT — but the save verdict now comes
        // from the WRITER (`outcome` threaded through refreshSedSections),
        // not a version-delta guess that misread idempotent rewrites and
        // nothing-new runs as failures.
        if (!result.persisted) {
          toast.error(
            result.reason
              ?? `${label} was collected but could NOT be saved — it will be gone on reload. This is a permissions problem on this site, not a provider failure; re-running will not help.`,
            { duration: 10000 },
          );
          return;
        }
        toast.success(`${label} collected.`);
        return;
      }
      if (result.unchanged) {
        toast.info(`${label} is already current — nothing new to save.`);
        return;
      }
      // Ran cleanly, changed nothing — the provider is the problem.
      const waivers = site.siteConfigOverrides?.providerCoverageDispatchWaivers ?? [];
      const sectionProviders: Record<string, string[]> = {
        parcel: ['regrid-parcels'],
        environmental: ['usfws-critical-habitat', 'nwi-wetlands', 'padus'],
        flood: ['fema-nfhl', 'fema-source-context'],
        waterStress: ['wri-aqueduct'],
      };
      const isWaived = (sectionProviders[sectionId] ?? []).some((p) => waivers.some((w) => w.providerId === p));

      toast.warning(
        isWaived
          ? `${label} is currently waived on this site. Re-running will not fetch any data while the waiver is in force.`
          : `${label} still returned nothing. Its provider is failing rather than simply not having run — another refresh will not help.`,
        { duration: 8000 },
      );
    } catch (e) {
      toast.dismiss(dismiss);
      toast.error((e as Error)?.message ?? 'Failed to re-run collection: unknown error');
    } finally {
      setReCollecting(false);
    }
  };

  // Workflow Prompt 7 — open the confirmation dialog instead of window.confirm.
  const handleDeleteReport = (e: React.MouseEvent | React.KeyboardEvent, report: SiteRagReport) => {
    e.stopPropagation();
    setReportToDelete(report);
  };

  // Get all reports for this site — match by siteId OR by site name (for pre-migration reports)
  const siteReports = useMemo(() => {
    return siteRag.reports
      .filter(r => reportBelongsToSite(site, r))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [siteRag.reports, site.id, site.reportIds, site.name]);

  // Site-scoped body hydration: the library lists every site's rows up front
  // but reads bodies only for the open site. THIS site counts as loaded once
  // its current heads are resident (or their read settled), so Home, Findings
  // and the composite backstop never compute from metadata-only heads after an
  // in-app navigation to a site whose bodies have not arrived yet.
  const siteBodiesPending = useMemo(
    () => isReportMetadataLoaded === true && siteReportBodiesPending(
      siteReports as unknown as Record<string, unknown>[],
      hydratedReportSiteIds,
      site.id,
    ),
    [isReportMetadataLoaded, siteReports, hydratedReportSiteIds, site.id],
  );
  const reportsLoaded = isReportsLoaded && !siteBodiesPending;
  useEffect(() => {
    if (!isReportMetadataLoaded || typeof ensureSiteReportBodies !== 'function') return;
    void ensureSiteReportBodies(site.id).catch(() => {
      /* fail closed: the rows stay metadata-only and the reader's Retry remains */
    });
  }, [site.id, isReportMetadataLoaded, ensureSiteReportBodies]);

  /*
   * Authoritative semantic-analysis hydration.
   *
   * This is deliberately GET-only: opening Home, Findings, or Path to GO never
   * starts a generation job. We resolve compact current pointers first, then
   * load and re-check each exact immutable revision. Aborting on site/auth
   * changes prevents a late response from one site entering another site's
   * decision state.
   */
  const decisionAnalysisScope = JSON.stringify([guideUid, workflowSite._ownerUid ?? guideUid, site.id]);
  const [decisionAnalysisLoad, setDecisionAnalysisLoad] = useState<{
    scope: string;
    siteId: string;
    status: 'idle' | 'loading' | 'ready' | 'failed';
    analysisGateEnabled: boolean;
    currentInputs?: Partial<Record<DecisionAnalysisDomain, DecisionAnalysisInputEnvelopeV1>>;
    entries: BrowserDecisionAnalysisSnapshot[];
    operations: CurrentDecisionAnalysis[];
  }>(() => ({
    scope: decisionAnalysisScope,
    siteId: site.id,
    status: 'idle',
    analysisGateEnabled: false,
    entries: [],
    operations: [],
  }));
  // Retry-visibility (chip task 2026-07-28): a Retry 202 must show on the
  // matrix immediately. `analysisOpsNonce` refetches the operational state;
  // `pendingRefreshByType` bridges the gap until the server reflects it.
  const [analysisOpsNonce, setAnalysisOpsNonce] = useState(0);
  const [pendingRefreshByType, setPendingRefreshByType] =
    useState<Partial<Record<ReportTypeId, number>>>({});
  // The pointer read currently in flight (identity-tagged so a superseded
  // load cannot clear its successor's marker) and when the last read started.
  // The progress poll below reads both to skip overlapping/duplicate ticks.
  const decisionAnalysisLoadInFlightRef = useRef<{ startedAt: number } | null>(null);
  const decisionAnalysisLastLoadAtRef = useRef<number | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    const ownerUid = typeof workflowSite._ownerUid === 'string'
      && workflowSite._ownerUid.trim()
      ? workflowSite._ownerUid
      : undefined;

    if (!guideUid) {
      setDecisionAnalysisLoad({
        scope: decisionAnalysisScope,
        siteId: site.id,
        status: 'idle',
        analysisGateEnabled: false,
        entries: [],
        operations: [],
      });
      return () => controller.abort();
    }

    // Stale-while-revalidate — reset to 'loading' ONLY on a site switch.
    // The 12s progress poll re-runs this effect via analysisOpsNonce, and
    // wiping `operations` first meant the runtime map lost its 'generating'
    // entry for the 1-3s the refetch took; the merged runtime then fell back
    // to the report-outcome map, which still held the PREVIOUS run's failure.
    // Live symptom (William 2026-08-03): a running analysis alternated every
    // poll between "Phase 5: Review iteration 1 — score 80/100" and the old
    // "Failed — hard gates failed" row. Keeping the last ready snapshot while
    // revalidating removes the gap; a genuine site change still resets.
    setDecisionAnalysisLoad(prev => (
      prev.scope === decisionAnalysisScope && prev.status === 'ready'
        ? prev
        : {
            scope: decisionAnalysisScope,
            siteId: site.id,
            status: 'loading',
            analysisGateEnabled: false,
            entries: [],
            operations: [],
          }
    ));
    const inFlight = { startedAt: Date.now() };
    decisionAnalysisLoadInFlightRef.current = inFlight;
    decisionAnalysisLastLoadAtRef.current = inFlight.startedAt;
    void (async () => {
      try {
        const current = await loadCurrentDecisionAnalyses(site.id, {
          ownerUid,
          signal: controller.signal,
        });
        const revisionLoads = current.analyses.flatMap(pointer => {
          const references = pointer.reference ? [pointer.reference] : [];
          // The held/degraded attempt readiness judges currency on — the same
          // basis the invalidation reaper uses, so "Refresh required" here
          // matches what the reaper enqueues (and the server Path to GO).
          const judgedAttempt = latestAttemptJudgedOnInput({
            refreshState: pointer.refreshState,
            refreshInputHash: pointer.refreshInputHash,
            latestAttemptReference: pointer.latestAttemptReference,
            referenceAnalysisId: pointer.reference?.analysisId,
          }, { siteId: site.id, domain: pointer.domain });
          if (
            pointer.latestAttemptReference
            && ['held', 'degraded', 'failed'].includes(pointer.refreshState ?? '')
            && pointer.latestAttemptReference.analysisId
              !== pointer.reference?.analysisId
          ) {
            references.push(pointer.latestAttemptReference);
          }
          return references.map(async revision => {
            // Content-addressed: the same (snapshotPath, contentHash) can only
            // name the same bytes, so the 12s poll re-downloads a revision only
            // when the pointer actually moved.
            const exact = await loadDecisionAnalysisRevisionCached(
              site.id,
              revision,
              {
                viewerUid: guideUid,
                ownerUid,
                signal: controller.signal,
              },
            );
            if (
              exact.reference.analysisId !== revision.analysisId
              || exact.reference.siteId !== revision.siteId
              || exact.reference.domain !== revision.domain
              || exact.reference.inputHash !== revision.inputHash
              || exact.reference.contentHash !== revision.contentHash
              || exact.reference.snapshotPath !== revision.snapshotPath
              || exact.reference.createdAt !== revision.createdAt
            ) {
              throw new Error(
                `Current analysis pointer ${revision.analysisId} changed while it was loading.`,
              );
            }
            const projectedRevision = pointer.reference?.analysisId === revision.analysisId
              ? revisionWithOperationalRefresh(revision, pointer)
              : revision;
            return {
              revision: projectedRevision,
              snapshot: exact.analysis,
              // The pointer's report id belongs to its LATEST commit, never to
              // an older passing reference a held/stale attempt did not replace.
              ...(pointer.reportId
                && pointerReportIdOwnerAnalysisId(pointer) === revision.analysisId
                ? { reportId: pointer.reportId }
                : {}),
              ...(pointer.reportRevisionId
                ? { reportRevisionId: pointer.reportRevisionId }
                : {}),
              ...(pointer.latestAttemptReference?.analysisId === revision.analysisId
                && pointer.reference?.analysisId !== revision.analysisId
                ? { isLatestAttempt: true }
                : {}),
              ...(judgedAttempt?.analysisId === revision.analysisId
                ? { judgedLatestAttempt: true }
                : {}),
            } satisfies BrowserDecisionAnalysisSnapshot;
          });
        });
        const settled = await Promise.allSettled(revisionLoads);
        const entries = settled.flatMap(result => (
          result.status === 'fulfilled' ? [result.value] : []
        ));
        for (const result of settled) {
          if (result.status !== 'rejected' || controller.signal.aborted) continue;
          console.warn(
            '[SiteLandingPage] an exact decision-analysis revision was unavailable:',
            result.reason instanceof Error ? result.reason.message : result.reason,
          );
        }
        if (controller.signal.aborted) return;
        setDecisionAnalysisLoad(prev => {
          // IDENTITY — the 12s poll usually re-delivers the same pointers and
          // (cached) revisions while only operational progress moves. Keep the
          // previous sub-objects when they are structurally unchanged so
          // `loadedDecisionAnalyses` → analysisSnapshots → the Path-to-GO
          // extras stay memoised; keep the whole state when nothing moved.
          const samePage = prev.scope === decisionAnalysisScope && prev.status === 'ready';
          const nextEntries = samePage && structuralEqual(prev.entries, entries)
            ? prev.entries : entries;
          const nextOperations = samePage && structuralEqual(prev.operations, current.analyses)
            ? prev.operations : current.analyses;
          const nextInputs = samePage
            && structuralEqual(prev.currentInputs, current.currentAnalysisInputsByDomain)
            ? prev.currentInputs : current.currentAnalysisInputsByDomain;
          if (
            samePage
            && prev.analysisGateEnabled === current.analysisGateEnabled
            && nextEntries === prev.entries
            && nextOperations === prev.operations
            && nextInputs === prev.currentInputs
          ) return prev;
          return {
            scope: decisionAnalysisScope,
            siteId: site.id,
            status: 'ready',
            analysisGateEnabled: current.analysisGateEnabled,
            currentInputs: nextInputs,
            entries: nextEntries,
            operations: nextOperations,
          };
        });
      } catch (error) {
        if (controller.signal.aborted) return;
        console.warn(
          '[SiteLandingPage] current decision analyses unavailable; currentness withheld:',
          error instanceof Error ? error.message : error,
        );
        // The last readable report remains available, but an unavailable
        // current authority cannot keep a positive currentness assertion.
        setDecisionAnalysisLoad({
          scope: decisionAnalysisScope, siteId: site.id, status: 'failed', analysisGateEnabled: true,
          entries: [], operations: [],
        });
      } finally {
        if (decisionAnalysisLoadInFlightRef.current === inFlight) {
          decisionAnalysisLoadInFlightRef.current = null;
        }
      }
    })();
    return () => controller.abort();
  }, [guideUid, site.id, workflowSite._ownerUid, analysisOpsNonce, decisionAnalysisScope]);

  const loadedDecisionAnalyses = (
    decisionAnalysisLoad.scope === decisionAnalysisScope
    && decisionAnalysisLoad.status === 'ready'
  )
    ? decisionAnalysisLoad.entries
    : null;
  const analysisSnapshots = useMemo<readonly PathToGoAnalysisSnapshot[]>(
    () => loadedDecisionAnalyses ?? [],
    [loadedDecisionAnalyses],
  );
  const currentAnalysisInputsByDomain = useMemo<Partial<
    Record<DecisionAnalysisDomain, DecisionAnalysisInputEnvelopeV1>
  >>(
    () => currentAnalysisInputsFromSnapshots(analysisSnapshots, decisionAnalysisLoad.currentInputs),
    [analysisSnapshots, decisionAnalysisLoad.currentInputs],
  );
  const analysisGateEnabled = loadedDecisionAnalyses
    ? decisionAnalysisLoad.analysisGateEnabled : true;
  const compositeAnalysisContext = useMemo(() => loadedDecisionAnalyses
    ? { analysisGateEnabled, analysisSnapshots, currentAnalysisInputsByDomain }
    : UNAVAILABLE_COMPOSITE_ANALYSIS_CONTEXT,
  [loadedDecisionAnalyses, analysisGateEnabled, analysisSnapshots, currentAnalysisInputsByDomain]);
  const decisionAnalysisOperations = (
    decisionAnalysisLoad.scope === decisionAnalysisScope
    && decisionAnalysisLoad.status === 'ready'
  )
    ? decisionAnalysisLoad.operations
    : NO_DECISION_ANALYSIS_OPERATIONS;
  const findingsAnalyses = useMemo(
    () => loadedDecisionAnalyses
      ? projectDecisionAnalysesIntoReports(
          workflowSite,
          siteReports,
          loadedDecisionAnalyses,
        )
      : siteReports,
    [loadedDecisionAnalyses, siteReports, workflowSite],
  );
  // The current decision analyses for THIS site have not settled yet. Until
  // they do, the plan runs with the gate on and no analyses, so every saved
  // report would read "Refresh required" (2026-09-18) — currentness is
  // unknown, not stale. Findings and the Path to GO cockpit both wait on it.
  const decisionAnalysesPending = Boolean(guideUid)
    && (
      decisionAnalysisLoad.scope !== decisionAnalysisScope
      || decisionAnalysisLoad.status === 'idle'
      || decisionAnalysisLoad.status === 'loading'
    );
  const findingsAnalysesState: 'loading' | 'settled' = !reportsLoaded || decisionAnalysesPending
      ? 'loading'
      : 'settled';
  const findingsAnalysisTypeCount = useMemo(
    () => countDistinctReportTypes(findingsAnalyses),
    [findingsAnalyses],
  );
  const [pendingFindingsAnalysisId, setPendingFindingsAnalysisId] = useState<string | null>(null);
  const handleOpenFindingsAnalysis = useCallback((analysisRecordId: string) => {
    const hasReportReader = siteReports.some(row => row.id === analysisRecordId);
    if (hasReportReader) {
      handleViewReport(analysisRecordId);
      return true;
    }
    const semanticId = analysisRecordId.startsWith('analysis-')
      ? analysisRecordId.slice('analysis-'.length)
      : analysisRecordId;
    const projected = findingsAnalyses.find(row => (
      row.id === analysisRecordId
      || row.decisionAnalysis?.analysisId === semanticId
      || row.analysisRevision?.analysisId === semanticId
    ));
    if (!projected) return false;
    setPendingFindingsAnalysisId(analysisRecordId);
    setActiveTab('reports');
    return true;
  }, [findingsAnalyses, handleViewReport, siteReports]);

  const reportRuntimeByType = useMemo<
    Partial<Record<ReportTypeId, ReportRuntimeLifecycle>>
  >(() => deriveReportRuntimeByType({
    siteId: site.id,
    reports: siteReports,
    liveRuns: displayInflightRuns,
    outcomes: Object.values(generationOutcomes),
  }), [displayInflightRuns, generationOutcomes, site.id, siteReports]);
  const analysisRefreshRuntimeByType = useMemo<
    Partial<Record<ReportTypeId, ReportRuntimeLifecycle>>
  >(() => {
    const runtime: Partial<Record<ReportTypeId, ReportRuntimeLifecycle>> = {};
    for (const operation of decisionAnalysisOperations) {
      // A RETIRED domain has no writer: the dispatch gate refuses the only job
      // that could move its pointer, so whatever state the pointer is frozen in
      // can never change. Rendering it as live work invites a customer into a
      // path that cannot complete — live 2026-09-09, the Pineywoods
      // water-resource pointer showed "Queued" for a run that had already
      // succeeded, with no way to act on it. Existing retired reports stay
      // readable; only the runtime overlay is suppressed.
      if (REPORT_TYPE_REGISTRY[operation.domain]?.status === 'retired') continue;
      if (operation.refreshState === 'queued') {
        runtime[operation.domain] = 'queued';
      } else if (operation.refreshState === 'refreshing') {
        runtime[operation.domain] = 'generating';
      } else if (operation.refreshState === 'failed') {
        runtime[operation.domain] = 'failed';
      }
    }
    // Optimistic overlay LAST: a just-accepted retry (202) must outrank the
    // STALE terminal state still sitting in the pre-retry snapshot (live:
    // the row snapped back to 'Failed'/'Refresh required' right after the
    // 'refresh queued' toast). The server wins the moment it acknowledges
    // the new run (queued/refreshing); a 60s expiry stops a vanished job
    // from masking reality forever.
    for (const [type, acceptedAt] of Object.entries(pendingRefreshByType) as Array<[ReportTypeId, number]>) {
      const serverState = decisionAnalysisOperations
        .find(operation => operation.domain === type)?.refreshState;
      if (serverState === 'queued' || serverState === 'refreshing') continue;
      if (Date.now() - acceptedAt < 60_000) runtime[type] = 'queued';
    }
    return runtime;
  }, [decisionAnalysisOperations, pendingRefreshByType]);
  useEffect(() => {
    // Drop optimistic entries only when the server ACKNOWLEDGES the new run
    // (queued/refreshing) or the entry expires — a stale pre-retry snapshot
    // (failed/ready) must NOT clear it, that snapshot is what the optimism
    // exists to outrank.
    setPendingRefreshByType(prev => {
      const next = { ...prev };
      let changed = false;
      for (const [type, acceptedAt] of Object.entries(prev) as Array<[ReportTypeId, number]>) {
        const serverState = decisionAnalysisOperations
          .find(operation => operation.domain === type)?.refreshState;
        if (serverState === 'queued' || serverState === 'refreshing' || Date.now() - acceptedAt >= 60_000) {
          delete next[type];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [decisionAnalysisOperations]);
  // While any analysis refresh is active, poll the operational state so the
  // stage/progress on the matrix stays LIVE (it froze at 'Initializing…'
  // otherwise — the only refetches were the one-shot post-retry nonce bumps).
  // Gated (prod 2026-10: ~586 pointer reads / 30 min for one site, p95 11 s):
  // only while something is genuinely running (a retired domain's frozen
  // pointer never counts), paused in a hidden tab with one catch-up read on
  // return, and never overlapping a read that is still in flight.
  const analysisOpsPollActive = shouldPollAnalysisOperations({
    operations: decisionAnalysisOperations,
    pendingRefreshByType,
    isRetiredDomain: domain => REPORT_TYPE_REGISTRY[domain]?.status === 'retired',
  });
  useGatedPoll({
    active: analysisOpsPollActive,
    intervalMs: ANALYSIS_OPS_POLL_INTERVAL_MS,
    staleInFlightMs: ANALYSIS_OPS_STALE_IN_FLIGHT_MS,
    getInFlightSince: () => decisionAnalysisLoadInFlightRef.current?.startedAt ?? null,
    getLastFetchAt: () => decisionAnalysisLastLoadAtRef.current,
    onFetch: () => setAnalysisOpsNonce(n => n + 1),
  });
  const analysisRefreshProgressByType = useMemo<
    Partial<Record<ReportTypeId, { progress?: number; stage?: string; heartbeatAt?: string | null; runId?: string; startedAt?: string | null; estimatedDurationMs?: number | null }>>
  >(() => {
    const out: Partial<
      Record<ReportTypeId, { progress?: number; stage?: string; heartbeatAt?: string | null; runId?: string; startedAt?: string | null; estimatedDurationMs?: number | null }>
    > = {};
    // Seed from every live generation run. Decision-analysis pointers can lag
    // job creation (and some generated types have no operation row yet), but
    // users should still see timing instead of the generic "Analysing" label.
    for (const [type, run] of Object.entries(inflightRunByType) as Array<
      [ReportTypeId, (typeof displayInflightRuns)[number]]
    >) {
      if (REPORT_TYPE_REGISTRY[type]?.status === 'retired') continue;
      out[type] = {
        ...(typeof run.progress === 'number' ? { progress: run.progress } : {}),
        ...(run.progressStage ? { stage: run.progressStage } : {}),
        ...(run.id ? { runId: run.id } : {}),
        ...(run.startedAt ? { startedAt: run.startedAt } : {}),
        estimatedDurationMs: run.estimatedDurationMs
          ?? estimatedReportGenerationDurationMs(type),
      };
    }
    // Preserve an approximate countdown during the short optimistic window
    // before either the job listener or the analysis-operation pointer lands.
    for (const [type, acceptedAt] of Object.entries(pendingRefreshByType) as Array<[ReportTypeId, number]>) {
      if (REPORT_TYPE_REGISTRY[type]?.status === 'retired' || out[type]) continue;
      out[type] = {
        stage: 'Queued…',
        startedAt: new Date(acceptedAt).toISOString(),
        estimatedDurationMs: estimatedReportGenerationDurationMs(type),
      };
    }
    for (const operation of decisionAnalysisOperations) {
      // Same reason as analysisRefreshRuntimeByType: a retired domain cannot
      // have live progress, so it must not offer a progress view or a Cancel.
      if (REPORT_TYPE_REGISTRY[operation.domain]?.status === 'retired') continue;
      if (operation.refreshState === 'queued' || operation.refreshState === 'refreshing') {
        // Join the generation-job run for this type: its id lets the row's
        // stall indicator subscribe to the server heartbeat and powers Cancel —
        // an hour-long job with a frozen phase label was indistinguishable
        // from a hang (live 2026-08-11), with no cancel affordance anywhere
        // on this surface.
        const run = inflightRunByType[operation.domain];
        // `refreshStartedAt` can belong to the previous attempt because queued
        // pointer writes merge in place. The request timestamp is the stable
        // anchor across queued -> refreshing; live jobs use their createdAt-
        // backed run timestamp.
        const startedAt = resolveGenerationCountdownStartedAt({
          runAcceptedAt: run?.startedAt,
          refreshRequestedAt: operation.refreshRequestedAt,
          refreshStartedAt: operation.refreshStartedAt,
        });
        out[operation.domain] = {
          ...out[operation.domain],
          ...(typeof operation.refreshProgress === 'number' ? { progress: operation.refreshProgress } : {}),
          ...(operation.refreshStage ? { stage: operation.refreshStage } : {}),
          ...(run?.id ? { runId: run.id } : {}),
          ...(startedAt ? { startedAt } : {}),
          estimatedDurationMs: run?.estimatedDurationMs
            ?? estimatedReportGenerationDurationMs(operation.domain),
        };
      }
    }
    return out;
  }, [decisionAnalysisOperations, displayInflightRuns, inflightRunByType, pendingRefreshByType]);
  const analysisRefreshErrorByType = useMemo<
    Partial<Record<ReportTypeId, string>>
  >(() => {
    const errors: Partial<Record<ReportTypeId, string>> = {};
    for (const operation of decisionAnalysisOperations) {
      // An EXHAUSTED pointer is `stale`, not `failed`, but it is the one state
      // the user most needs explained: nothing will re-run this domain on its
      // own again. Carry its reason so the dossier has something to translate.
      if (
        operation.refreshState === 'failed'
        || operation.autoRebindExhaustedAt
        // Optional evidence only (autoRebindGate): stale, nothing re-runs on
        // its own — carry the reason so the dossier can say so.
        || (operation.refreshState === 'stale' && operation.autoRebindSkippedAt)
      ) {
        // refreshError is the worker's crash/abort error; a run that COMMITTED
        // but failed readiness (e.g. quality hard gates) clears it and speaks
        // through refreshReason instead — fall back so the matrix explains
        // that family rather than showing a bare generic line.
        errors[operation.domain] =
          operation.refreshError
          ?? operation.refreshReason
          ?? 'The analysis refresh failed. Retry is safe.';
      }
    }
    return errors;
  }, [decisionAnalysisOperations]);
  /*
   * AUTO-REBIND CAP state, per domain. Past AUTO_REBIND_MAX_CONSECUTIVE
   * consecutive input-stale commits nothing re-runs the domain on its own, and
   * the pointer is the only thing that says so. Carried separately from the
   * error map because an exhausted pointer is `stale`, not `failed`.
   */
  const analysisAutoRebindByType = useMemo<
    Partial<Record<ReportTypeId, { exhausted: boolean; streak?: number; optionalEvidenceSections?: string[] }>>
  >(() => {
    const state: Partial<Record<ReportTypeId, { exhausted: boolean; streak?: number; optionalEvidenceSections?: string[] }>> = {};
    for (const operation of decisionAnalysisOperations) {
      if (operation.autoRebindExhaustedAt) {
        state[operation.domain] = {
          exhausted: true,
          ...(typeof operation.autoRebindStreak === 'number'
            ? { streak: operation.autoRebindStreak }
            : {}),
        };
      } else if (operation.refreshState === 'stale' && operation.autoRebindSkippedAt) {
        // AUTOMATIC-REBIND GATE: only optional currency evidence moved, so no
        // automatic rerun was armed — newer evidence available, manual rerun.
        state[operation.domain] = {
          exhausted: false,
          optionalEvidenceSections: operation.autoRebindSkippedSections?.length
            ? [...operation.autoRebindSkippedSections]
            : ['publicSentiment'],
        };
      }
    }
    return state;
  }, [decisionAnalysisOperations]);
  /** Boolean projection of the cap state for the Path-to-GO plan (see reportPlan). */
  const analysisAutoRefreshExhaustedByType = useShallowStableRecord(useMemo<
    Partial<Record<ReportTypeId, boolean>>
  >(() => {
    const out: Partial<Record<ReportTypeId, boolean>> = {};
    for (const [type, state] of Object.entries(analysisAutoRebindByType)) {
      if (state?.exhausted) out[type as ReportTypeId] = true;
    }
    return out;
  }, [analysisAutoRebindByType]));
  // Compared BY VALUE: both inputs rebuild on every progress tick of a live
  // run, but the merged lifecycle map almost always has the same contents.
  // A stable reference here keeps `pathToGoCockpitExtras` — and the whole
  // Path-to-GO build keyed on it — from recomputing while a job merely
  // advances its percentage.
  const decisionRuntimeByType = useShallowStableRecord(useMemo<
    Partial<Record<ReportTypeId, ReportRuntimeLifecycle>>
  >(
    () => mergeDecisionRuntimeByType(reportRuntimeByType, analysisRefreshRuntimeByType),
    [analysisRefreshRuntimeByType, reportRuntimeByType],
  ));

  // aurora-qa-3 (amended 2026-08-22) — Reports-gallery "Export": legacy
  // no-revision reports open the print PDF directly (/print/{reportId});
  // immutable V2 revisions route to the report view, whose header menu serves
  // the validated artifacts (the mutable print route categorically rejects
  // them). Barred reports open the report view with the state's own hint.
  /**
   * Open the mutable print view. Split out of `handleExportReport` so the
   * export-time audit warning can hold the window.open until the operator
   * confirms — it still runs from THEIR click, so the popup blocker sees a
   * user gesture.
   */
  const openPrintView = useCallback((reportId: string) => {
    const url = `/print/${encodeURIComponent(reportId)}`;
    const opened = window.open(url, '_blank', 'noopener');
    if (!opened) {
      toast.error(
        'Popup blocked — allow popups for this site, then click Export again.',
        { id: 'pdf-export' },
      );
      return;
    }
    toast.success('Opening print view — use "Save as PDF" in the print dialog.', {
      id: 'pdf-export',
    });
  }, []);
  /**
   * Report awaiting the export-time audit warning (id, so a store refresh
   * cannot leave the dialog holding a stale report object).
   */
  const [pendingAuditExportId, setPendingAuditExportId] = useState<string | null>(null);

  const handleExportReport = useCallback(
    (reportId: string) => {
      const report = siteReports.find((r) => r.id === reportId);
      void (async () => {
        const { isExportableTier } = await import('../../lib/export/draftExportGate');
        if (report && !isExportableTier(report)) {
          // Barred — say WHY (draft, degraded, invalid, incomplete each carry
          // their own hint) instead of misdiagnosing everything as a draft,
          // and open the report view where the state's recovery action lives.
          const { resolveDraftTierUx } = await import('../../lib/draftTierUx');
          toast.error(
            resolveDraftTierUx(report).exportHint
              ?? 'This report cannot be exported yet — open it to see why.',
            { id: 'pdf-export' },
          );
          handleViewReport(reportId);
          return;
        }
        // Immutable V2 revisions cannot use the mutable /print route — the
        // server categorically rejects them (assertMutablePrintEligible). Route
        // to the report view, where the validated artifact downloads live.
        if (report?.reportRevision) {
          toast.success('Opening the report — download the validated PDF from the header menu.', { id: 'pdf-export' });
          handleViewReport(reportId);
          return;
        }
        // EXPORT-TIME AUDIT WARNING (2026-08-25) — the header menu's exports
        // route through this same confirm; this button did not, and it opens
        // the print view in a new tab where Save-as-PDF auto-opens, so an
        // audit-flagged report could be shared without ever seeing the
        // caveat. Advisory, not a bar: "Export anyway" proceeds.
        if (report && reportHasOpenAuditFlag(report)) {
          setPendingAuditExportId(reportId);
          return;
        }
        // Investment-grade / legacy — open the interactive print view in a new
        // tab. No HMAC token ⇒ PrintReportView loads via the user's Firebase
        // session and auto-opens the Print dialog ("Save as PDF").
        openPrintView(reportId);
      })();
    },
    [siteReports, handleViewReport, openPrintView],
  );
  /** The report the warning is holding, re-read from the live list. */
  const pendingAuditExportReport = pendingAuditExportId
    ? siteReports.find((r) => r.id === pendingAuditExportId)
    : undefined;

  const latestReport = siteReports[0];
  const sitePreload = useMemo(
    () => buildSitePreloadData(site, latestReport?.datasets?.climate),
    [site, latestReport?.datasets?.climate],
  );
  const reportsByType = useMemo(
    () => latestReportByType(site, siteReports),
    [site, siteReports],
  );

  // Report-card branding — the company (primary) logo always shows on a
  // card; the customer (secondary) logo shows alongside it only when the
  // site's report audience is 'customer'.
  //
  // OWNING-COMPANY BRAND (2026-06-10) — the card's primary mark is the
  // brand of the site's OWNING company (`site.companyId` →
  // companies/{cid}/settings/branding + companies/{cid}.name), never the
  // viewer's session template/theme brand (live bug: Clayco-owned sites'
  // report cards wearing the Azimuth session-template mark). Resolution
  // runs through `resolveSiteCardBranding`: owning brand first, then the
  // v1.3.2 ownership guard (session chain for own/personal sites,
  // suppression for cross-company sites whose brand read was denied).
  const reportTheme = useEffectiveReportTheme();
  // Viewer org (store) — only consulted by the FALLBACK guard branch.
  const viewerCompanyId = workspaceCompanyId;
  const viewerCompanyName = useStore((s) => s.companyName);
  // The owning company's brand; null while loading / personal site /
  // rules-denied read → resolveSiteCardBranding falls back gracefully.
  const owningCompanyBranding = useCompanyBranding(site.companyId);
  // Brand logo (feat/org-logo) — effective uploaded logo (user → org) for the
  // report-card cover, fed into resolveSiteCardBranding's `effectiveLogoUrl`.
  const brandUserLogoUrl = useStore((s) => s.userLogoUrl);
  const brandOrgLogoUrl = useStore((s) => s.orgLogoUrl);
  // Assigned template (same precedence as the dashboard site cards):
  // Studio draft override → session active/default → archetype seed.
  // No per-site templateId exists, so there is no report-linked id to feed.
  const assignedTemplate = useMemo(
    () => resolveActiveReportTemplate({
      reportTemplateId: null,
      templates: reportTemplates,
      activeTemplateId,
      templateOverride: draftTemplate,
      archetype: site.archetypeOverride ?? (companyArchetype as CompanyArchetype | undefined) ?? null,
    }),
    [reportTemplates, activeTemplateId, draftTemplate, site.archetypeOverride, companyArchetype],
  );
  const reportCardBranding = useMemo(
    () => resolveSiteCardBranding({
      site,
      theme: reportTheme,
      template: assignedTemplate,
      effectiveLogoUrl: resolveUploadedLogo({ userLogoUrl: brandUserLogoUrl, orgLogoUrl: brandOrgLogoUrl }),
      viewerCompanyId: viewerCompanyId ?? null,
      viewerCompanyName: viewerCompanyName ?? null,
      owningCompanyBranding,
    }),
    [
      site.id,
      site.name,
      site.companyId,
      site.reportAudienceDefault,
      site.customerLogoUrl,
      reportTheme.logoUrl,
      reportTheme.companyName,
      assignedTemplate,
      brandUserLogoUrl,
      brandOrgLogoUrl,
      viewerCompanyId,
      viewerCompanyName,
      owningCompanyBranding,
    ],
  );
  // Display company name for the audience-default labels. The OWNING
  // company's name (site.companyId → companies/{cid}.name) is authoritative
  // — the select copy names the org whose mark the customer cover wears
  // (e.g. 'Customer (Clayco + customer logo)' on a Clayco-owned site), NOT
  // the viewer's session brand. Falls back to the previous session chain
  // (assigned template brand → effective theme → 'Datacrate') while
  // loading / for personal sites / when the cross-tenant read is denied.
  const brandCompanyName =
    (owningCompanyBranding?.companyName?.trim() || undefined)
    ?? (assignedTemplate?.theme?.companyName?.trim() || undefined)
    ?? (reportTheme.companyName?.trim() || undefined)
    ?? DEFAULT_COMPANY_NAME;
  const latestExecutiveSummaryReport = latestExecutiveSummaryForDecision(
    siteReports.filter(report => !report.siteId || report.siteId === site.id),
  );
  const executiveSummaryNarrativeStatus =
    typeof latestExecutiveSummaryReport?.overallStatus === 'string'
      ? latestExecutiveSummaryReport.overallStatus
      : null;

  useEffect(() => {
    if (typeof computeCompositeForSite !== 'function') return;
    // Redhead RCA (2026-08-11): NEVER recompute before the report list has
    // loaded. A recompute against the still-loading (empty/partial) store list
    // produces the empty-report-set composite — only `evidenceQuality` measured
    // (10% coverage) and zero sourceReportIds ("No source reports yet") — and
    // displays it immediately (state is set before the persist). The staleness
    // check below would then re-fire once reports arrive, but the degraded
    // frame is real, user-visible, and was persisted mid-regen. Waiting for
    // `isReportsLoaded` costs nothing: the effect re-runs when it flips.
    if (!reportsLoaded) return;
    // Company-shared reports are only visible after auth resolves the viewer's
    // company scope. A shared site can mount under the loading overlay before
    // that happens; computing then would poison the persisted composite with
    // an own-only/empty report set. `workspaceCompanyId` is a dependency so the
    // effect retries as soon as scope lands.
    if (workflowSite.companyId && !workspaceCompanyId) return;
    // RC-1: recompute on stale-OR-absent — a report write epoch-stamps the cached composite, so
    // guarding only on absence would render the pre-report composite ("no source reports"). The ref is
    // an in-flight latch cleared on completion (finally), so a later stale-stamp re-triggers while
    // concurrent renders don't double-compute.
    // M-tier fingerprint (2026-07-02): the CURRENT inputs (this site's report list + SED pointer)
    // ride along so a fingerprint-carrying composite is stale exactly when its inputs changed —
    // no 24h wall-clock recompute on unchanged inputs, immediate recompute on a changed report set.
    if (!isSiteCompositeStale(siteComposite, undefined, { site: workflowSite, reports: siteReports, analysisContext: compositeAnalysisContext })) return;
    if (requestedCompositeForSiteRef.current[workflowSite.id]) return;
    requestedCompositeForSiteRef.current[workflowSite.id] = true;
    void computeCompositeForSite(workflowSite.id, compositeAnalysisContext)
      // Swallow first (best-effort recompute) so a failed compute can't reject unhandled, THEN clear
      // the in-flight latch on completion (success or failure) so a later stale-stamp re-triggers.
      .catch(() => {})
      .finally(() => {
        requestedCompositeForSiteRef.current[workflowSite.id] = false;
      });
  }, [
    workflowSite,
    siteReports,
    siteComposite,
    computeCompositeForSite,
    reportsLoaded,
    workspaceCompanyId,
    compositeAnalysisContext,
  ]);

  const openOverviewVerdictDrilldown = useCallback(() => {
    setActiveTab('audit');
    setOverviewDataConfidenceOpen(true);
  }, []);

  // Count unique report types
  const reportTypeCount = useMemo(
    () => countDistinctReportTypes(siteReports),
    [siteReports],
  );
  const reportVersionCount = siteReports.length;

  const overviewPrefsCustomizedCount = useMemo(() => {
    const prefs = site.typedPreferencesDefaults ?? {};
    const types = [
      'feasibility', 'environmental', 'test-fit', 'power-availability',
      'interconnection', 'supply-chain-risk', 'public-sentiment',
    ] as const;
    return types.filter(rt => {
      const slot = prefs[rt];
      if (!slot || typeof slot !== 'object') return false;
      return Object.values(slot).some(v => v !== undefined && v !== null && v !== '');
    }).length;
  }, [site.typedPreferencesDefaults]);

  const openOverviewWizard = useCallback(() => {
    // #89 — generating a report writes to the site; gate the opener for a
    // locked non-holder so every caller (Overview CTA, etc.) is covered.
    if (lockReadOnly) {
      toast.error(`This site is locked for editing by ${lockHolderLabel}. Request edit access to make changes.`);
      return;
    }
    setPickerInitialType(undefined);
    setWizardForceNew(true);
    setShowReportTypePicker(true);
  }, [lockReadOnly, lockHolderLabel]);

  // Page-level SED load shared across tabs (elevation resolution, etc.).
  // v1.3.4 (#24) — ONE definitive card per report TYPE. We still group by
  // `parentReportId` lineage within a type (so version history threads
  // correctly), but then COLLAPSE every chain of a type into a single
  // definitive lineage: latest report across all chains is the card, all
  // others (sibling "Alt" chains included) fold into version history. This
  // kills the parallel "Alt #N" cards while preserving the full history.
  const lineagesByType = useMemo(() => {
    const byType: Record<string, SiteRagReport[]> = {};
    for (const r of siteReports) {
      const typeId = r.reportType ?? 'feasibility';
      if (!byType[typeId]) byType[typeId] = [];
      byType[typeId].push(r);
    }
    const result: Record<string, ReportLineage[]> = {};
    for (const [typeId, reports] of Object.entries(byType)) {
      const collapsed = collapseTypeLineagesToDefinitive(groupReportsByLineage(reports));
      result[typeId] = collapsed ? [collapsed] : [];
    }
    return result;
  }, [siteReports]);
  const rerunTargetLineageId = useMemo(() => {
    if (!rerunReportId) return null;
    for (const lineages of Object.values(lineagesByType)) {
      for (const lineage of lineages) {
        if (lineage.lineageId === rerunReportId) return lineage.lineageId;
        if (lineage.latest.id === rerunReportId) return lineage.lineageId;
        if (lineage.history.some((h) => h.id === rerunReportId)) return lineage.lineageId;
      }
    }
    return null;
  }, [lineagesByType, rerunReportId]);

  useEffect(() => {
    if (activeTab !== 'reports' || !rerunReportId || !rerunTargetLineageId) return;
    const nextHandledKey = `${rerunReportId}:${rerunTargetLineageId}`;
    if (lastHandledRerunKeyRef.current === nextHandledKey) return;
    const target = document.querySelector(`[data-testid="lineage-card-${rerunTargetLineageId}"]`) as HTMLElement | null;
    if (!target) return;
    lastHandledRerunKeyRef.current = nextHandledKey;
    try {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch {
      // best-effort scroll only
    }
    setHighlightedRerunLineageId(rerunTargetLineageId);
    if (rerunHighlightTimeoutRef.current != null) {
      window.clearTimeout(rerunHighlightTimeoutRef.current);
    }
    rerunHighlightTimeoutRef.current = window.setTimeout(() => {
      setHighlightedRerunLineageId((cur) => (cur === rerunTargetLineageId ? null : cur));
    }, 2000);
  }, [activeTab, rerunReportId, rerunTargetLineageId]);

  useEffect(() => () => {
    if (rerunHighlightTimeoutRef.current != null) {
      window.clearTimeout(rerunHighlightTimeoutRef.current);
      rerunHighlightTimeoutRef.current = null;
    }
  }, []);

  const orphanInflightRuns = useMemo(
    () => displayInflightRuns.filter(
      r => r.reportTypeId && !(lineagesByType[r.reportTypeId]?.length),
    ),
    [displayInflightRuns, lineagesByType],
  );

  // True only on the VERY FIRST report generation for a site — a generation is
  // in flight AND no report has completed yet. Used to suppress the
  // "No reports yet" empty-state heading/icon and the "Choose a report type to
  // begin." prompt while the first progress card is on screen (they read as
  // contradictory next to a 35% progress bar). Once any report completes
  // (siteReports.length > 0) or nothing is generating, the empty state shows
  // exactly as before.
  const firstRunGenerating = siteReports.length === 0 && orphanInflightRuns.length > 0;

  const openInflightWizard = useCallback((typeId: ReportTypeId) => {
    setPickerInitialType(typeId);
    setWizardForceNew(false); // resume the in-flight run for this type
    setShowReportTypePicker(true);
  }, []);

  const renderOrphanInflightCards = () => orphanInflightRuns.map(run => {
    const typeId = run.reportTypeId as ReportTypeId;
    const typeDef = REPORT_TYPE_REGISTRY[typeId];
    const typeShortLabel = typeDef?.shortLabel ?? typeId;
    const IconMap: Record<string, React.ElementType> = { FileText, Leaf, Building2, Zap, Droplets, Cable };
    const TypeIcon = IconMap[typeDef?.icon ?? 'FileText'] ?? BarChart3;
    const placeholderId = `inflight-${typeId}-${run.id}`;
    return (
      <div
        key={placeholderId}
        className="relative rounded-lg shadow-sm overflow-hidden border border-primary/40 bg-primary/5"
        data-testid={`lineage-inflight-${placeholderId}`}
        role="status"
        aria-live="polite"
      >
        <div className="p-3 space-y-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <TypeIcon className="w-4 h-4 text-text-muted shrink-0" />
              <span className={`${REPORT_CARD_TITLE_CLASS} pl-0 truncate`}>{typeShortLabel}</span>
              {!run.waitingForSiteEvidence && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />}
            </div>
            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                variant="outline"
                size="sm"
                data-testid={`lineage-inflight-view-${placeholderId}`}
                onClick={() => openInflightWizard(typeId)}
              >
                View details
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="type-meta uppercase tracking-widest text-text-secondary hover:text-danger"
                data-testid={`lineage-inflight-cancel-${placeholderId}`}
                onClick={() => { void cancelReportGenerationJob(run.id); }}
                title="Cancel this report generation"
              >
                Cancel
              </Button>
            </div>
          </div>
          <InflightRunProgressStrip
            runId={run.id}
            progressStage={run.progressStage || 'Initializing…'}
            progress={run.progress}
            startedAt={run.startedAt}
            estimatedDurationMs={run.estimatedDurationMs}
            showTips
            compact
            testIdSuffix={`-${placeholderId}`}
          />
        </div>
      </div>
    );
  });
  // (existingReportTypes was consumed by the old ReportTypePicker; the new
  // NewReportWizard derives this internally from the store.)

  // Site-level documents — site.documents (uploaded pre-report) merged with
  // every report's documents (uploaded after a report exists). Documents are
  // keyed by site, so the Documents tab is independent of report state.
  const siteDocuments = useMemo(() => {
    const seen = new Set<string>();
    const docs: import('../../types/siteRag').SiteDocument[] = [];
    for (const d of site.documents ?? []) {
      if (!seen.has(d.id)) { seen.add(d.id); docs.push(withResolvedSiteDocumentFileName(d, d.id || `doc-${docs.length}`)); }
    }
    for (const d of latestReport?.documents || []) {
      if (!seen.has(d.id)) { seen.add(d.id); docs.push(withResolvedSiteDocumentFileName(d, d.id || `doc-${docs.length}`)); }
    }
    for (const r of siteReports) {
      for (const d of r.documents || []) {
        if (!seen.has(d.id)) { seen.add(d.id); docs.push(withResolvedSiteDocumentFileName(d, d.id || `doc-${docs.length}`)); }
      }
    }
    return docs;
  }, [site.documents, siteReports, latestReport]);

  // Page-level SED load (elevation resolution on Documents / Overview).
  const [docsTabSed, setDocsTabSed] = React.useState<SiteEvidenceDocument | null>(() => {
    if (site.sed) return site.sed;
    if (!site.sedId) return null;
    // SED-VERSION-LABEL — version-key the synchronous cache read so the
    // initial render doesn't flash the stale pre-bump doc before the async
    // effect corrects it.
    return getCachedSedById(site.sedId, site.sedStoragePath, site.sedVersion) ?? null;
  });
  const [docsTabSedLoading, setDocsTabSedLoading] = React.useState(() => {
    if (!site.sedId || site.sed) return false;
    return getCachedSedById(site.sedId, site.sedStoragePath, site.sedVersion) == null;
  });
  const [docsTabSedError, setDocsTabSedError] = React.useState<string | null>(null);
  const [docsTabSedReloadKey, setDocsTabSedReloadKey] = React.useState(0);
  const currentEvidenceReadRef = React.useRef<CurrentEvidenceReadIdentity | null>(null);
  currentEvidenceReadRef.current = liveSedId ? {
    siteId: site.id,
    ownerUid: siteOwnerUid(site, faAuth.currentUser?.uid ?? ''),
    sedId: liveSedId,
    storagePath: liveSedStoragePath,
    version: liveSedVersion,
  } : null;
  const handleCurrentEvidenceLoaded = React.useCallback((read: CurrentEvidenceReadIdentity) => {
    const current = currentEvidenceReadRef.current;
    if (!current
      || current.siteId !== read.siteId
      || current.ownerUid !== read.ownerUid
      || current.sedId !== read.sedId
      || current.storagePath !== read.storagePath
      || current.version !== read.version) return;
    // The child has read this exact body through the normal loader, which
    // primes the owner/path/version cache. Re-read that cache through the
    // page's normal bounded read; do not adopt an arbitrary child payload.
    setDocsTabSedReloadKey(key => key + 1);
  }, []);
  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const sedId = liveSedId;
    setDocsTabSedError(null);
    if (!sedId) {
      setDocsTabSed(null);
      setDocsTabSedLoading(false);
      return;
    }
    const candidate = getCachedSedById(sedId, liveSedStoragePath, liveSedVersion);
    const cached = candidate?.id === sedId
      && (liveSedVersion == null || candidate.version === liveSedVersion)
      ? candidate : null;
    setDocsTabSed(cached ?? null);
    setDocsTabSedLoading(!cached);
    if (cached) return;
    // The deadline includes module loading, Firebase token acquisition, fetch
    // and body parsing. None may leave Home/Brief reading indefinitely.
    const timer = setTimeout(() => controller.abort(new Error(EVIDENCE_READ_TIMEOUT_MESSAGE)), EVIDENCE_READ_TIMEOUT_MS);
    (async () => {
      try {
        const { loadSedById } = await awaitAbortableRead(import('../../services/sedLoader'), controller.signal);
        const doc = await awaitAbortableRead(
          loadSedById(sedId, liveSedStoragePath, liveSedVersion, { signal: controller.signal }),
          controller.signal,
        );
        if (!doc) throw new Error('Saved evidence could not be loaded. Reload evidence to try again.');
        if (doc.id !== sedId || (liveSedVersion != null && doc.version !== liveSedVersion)) {
          throw new Error('The saved evidence read did not match the current document version. Reload evidence to try again.');
        }
        if (!cancelled && !controller.signal.aborted) setDocsTabSed(doc);
      } catch (e) {
        if (!cancelled) setDocsTabSedError(e instanceof Error ? e.message : 'Saved evidence could not be loaded.');
      } finally {
        clearTimeout(timer);
        if (!cancelled) setDocsTabSedLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [site.id, site._ownerUid, liveSedId, liveSedStoragePath, liveSedVersion, docsTabSedReloadKey]);
  // A failed current read is not an evidence-free site. Keep the read-side
  // decision/recommendation gates held until the actual saved body is read.
  const docsTabSedReadBlocked = docsTabSedLoading || Boolean(docsTabSedError);

  // Read-side composite authority. A persisted composite is a cache, not the
  // current verdict: report generation deliberately stale-stamps it and the
  // async persist refresh can land a render later. Once the complete report
  // list and current SED are available, derive the exact oracle result locally
  // whenever that cache is absent/stale. This prevents Path to GO and Findings
  // from presenting the old "Insufficient data" frame while the identical
  // background computation is still being persisted.
  const currentSiteComposite = useMemo(() => {
    if (!reportsLoaded || docsTabSedReadBlocked) return null;
    try {
      if (!isSiteCompositeStale(siteComposite, undefined, {
        site: workflowSite,
        reports: siteReports,
        analysisContext: compositeAnalysisContext,
      })) {
        return siteComposite;
      }
      return computeSiteComposite({
        site: workflowSite,
        customerId: workspaceCompanyId ?? workflowSite.companyId ?? 'unknown',
        reports: siteReports,
        analysisContext: compositeAnalysisContext,
        evidence: docsTabSed,
        bandConfig: decisionBandConfig ?? null,
      });
    } catch (err) {
      console.error('[SiteLandingPage] Failed to compute site composite:', err);
      return null;
    }
  }, [
    reportsLoaded,
    docsTabSedReadBlocked,
    siteComposite,
    workflowSite,
    siteReports,
    workspaceCompanyId,
    docsTabSed,
    decisionBandConfig,
    compositeAnalysisContext,
  ]);

  const derivedVerdict = useMemo(
    () => deriveSiteVerdict(
      currentSiteComposite,
      executiveSummaryNarrativeStatus,
      decisionBandConfig ?? undefined,
    ),
    [currentSiteComposite, executiveSummaryNarrativeStatus, decisionBandConfig],
  );

  // RP1 — the authoritative Go/No-Go rollup, built from the same LIVE SED
  // document used by Path to GO. Strict report satisfaction (especially the
  // on-site Test Fit authority tuple) therefore cannot disagree merely because
  // one surface evaluated against a null evidence document.
  const executiveDecisionRollup = useMemo(
    () => {
      try {
        return buildExecutiveDecisionRollup({
          site: workflowSite,
          sed: docsTabSed ?? null,
          reports: siteReports,
          intent: workflowSite.projectIntent,
          bandConfig: decisionBandConfig ?? null,
          composite: currentSiteComposite,
          analysisContext: { analysisGateEnabled, analysisSnapshots, currentAnalysisInputsByDomain },
        });
      } catch (err) {
        console.error('[SiteLandingPage] Failed to build executive decision rollup:', err);
        return {
          band: null,
          label: VERDICT_WITHHELD_LABEL,
          rationale: [
            'Verdict withheld — decision evidence could not be evaluated from an incomplete analysis record.',
          ],
          bindingConstraints: [
            'Insufficient evidence — decision analysis record is incomplete or unavailable.',
          ],
          perSourceVerdicts: [],
          advisoryVerdicts: [],
          firmCapacityVerdict: null,
          adequacy: {
            adequate: false,
            reason: 'Decision analysis record is incomplete or unavailable',
            coverageRatio: 0,
          },
          mandatorySatisfied: false,
        };
      }
    },
    [
      workflowSite,
      docsTabSed,
      siteReports,
      decisionBandConfig,
      currentSiteComposite,
      analysisGateEnabled,
      analysisSnapshots,
      currentAnalysisInputsByDomain,
    ],
  );

  // J8 — site-level inputs for the tier-aware readiness-gap computation.
  // The readiness panel + per-type chips re-resolve setup/intent rows
  // against ACTUAL state (projectIntent / typedPreferences / SED) rather
  // than the stale persisted `site.coverage` map (which has no tier-0
  // setup/intent branch, so it wrote "Site Project Intent" + the report's
  // own setup as a false "Missing"). See `lib/coverage/readinessGaps.ts`.
  const readinessGapInput = React.useMemo<ReadinessGapInputBase>(
    () => ({
      persistedCoverage: site.coverage,
      sed: docsTabSed ?? null,
      projectIntent: site.projectIntent ?? null,
      typedPreferences: site.typedPreferencesDefaults,
      documents: site.documents ?? [],
      authoredClaims: site.siteContext?.claims,
    }),
    [site.coverage, docsTabSed, site.projectIntent, site.typedPreferencesDefaults, site.documents, site.siteContext?.claims],
  );
  // Route a gap's resolution onto the existing affordances — no new flow:
  //   - fill-intent → Project Intent tab
  //   - open-setup  → New Report wizard for the type (hosts the setup step)
  //   - upload-doc  → Documents (library / upload) tab
  //   - rerun       → Site evidence (provider coverage + evidence refresh;
  //                   reports themselves are re-run only from the report page)
  const handleResolveGap = useCallback((action: ReadinessGapAction) => {
    switch (action.kind) {
      case 'fill-intent':
        setActiveTab('intent');
        return;
      case 'open-setup':
        guardLockedAction(() => {
          setPickerInitialType(action.reportType);
          setWizardForceNew(true);
          setShowReportTypePicker(true);
        })();
        return;
      case 'upload-doc':
        setActiveTab('documents');
        return;
      case 'rerun':
        setActiveTab('evidence');
        return;
    }
  }, [guardLockedAction]);

  // ONE ranked DCT document match for the whole page. It feeds Path to GO
  // (below), so steps for requirements the user already uploaded a matching
  // document for read "Verify evidence" instead of asking for the upload
  // again, and the Site Evidence tab's CoverageMatrix + Author prefill, so all
  // of them read one run. The run waits for the composite: it resolves once
  // reports and the SED have loaded, and it fixes the decision stage — the same
  // `resolveSiteDecisionStage(composite)` `buildPathToGoState` derives from it —
  // so the matcher never scores a scope that is about to change.
  const plannedCoverageTypes = useMemo(
    () => resolvePathToGoPlannedTypes(workflowSite, workflowSite.projectIntent).plannedTypes,
    [workflowSite],
  );
  const siteCoverageMatch = useSiteCoverageMatch({
    siteId: site.id,
    documents: siteDocuments,
    reports: siteReports,
    plannedReportTypes: plannedCoverageTypes,
    disconnectedLinks: site.userDisconnectedLinks,
    decisionStage: currentSiteComposite
      ? resolveSiteDecisionStage(currentSiteComposite).currentStage
      : undefined,
    // A hermetic run (the generation e2e) holds the page-level match off; see
    // `isPageCoverageMatchEnabled`.
    enabled: currentSiteComposite != null && isPageCoverageMatchEnabled(),
  });
  // Path to GO names matched uploads from the site's own documents, so it gets
  // the rows for those only (`coverageMatchForDocuments`); the Site Evidence
  // tab keeps the full match over `siteDocuments`.
  const pathToGoCoverageMatch = useMemo(
    () => coverageMatchForDocuments(siteCoverageMatch.matchByType, workflowSite.documents ?? []),
    [siteCoverageMatch.matchByType, workflowSite.documents],
  );

  const pathToGoCockpitExtras = useMemo(
    () => ({
      ...pathToGoExtras,
      canEdit: !lockReadOnly,
      evidenceLoading: !reportsLoaded || docsTabSedReadBlocked || decisionAnalysesPending,
      reportRuntimeByType: decisionRuntimeByType,
      analysisRuntimeByType: decisionRuntimeByType,
      analysisAutoRefreshExhaustedByType: analysisAutoRefreshExhaustedByType,
      decisionRollup: executiveDecisionRollup,
      analysisSnapshots,
      currentAnalysisInputsByDomain,
      analysisGateEnabled,
      coverageMatchByType: pathToGoCoverageMatch,
    }),
    [
      analysisGateEnabled,
      analysisSnapshots,
      currentAnalysisInputsByDomain,
      decisionAnalysesPending,
      docsTabSedReadBlocked,
      executiveDecisionRollup,
      lockReadOnly,
      pathToGoExtras,
      decisionRuntimeByType,
      reportsLoaded,
      analysisAutoRefreshExhaustedByType,
      pathToGoCoverageMatch,
    ],
  );

  const pathToGoSharedState = useMemo(
    () => buildPathToGoState(
      workflowSite,
      docsTabSed ?? null,
      siteReports,
      workflowSite.projectIntent,
      decisionBandConfig,
      currentSiteComposite,
      executiveDecisionRollup.bindingConstraints,
      pathToGoCockpitExtras,
    ),
    [
      workflowSite,
      docsTabSed,
      siteReports,
      decisionBandConfig,
      currentSiteComposite,
      executiveDecisionRollup.bindingConstraints,
      pathToGoCockpitExtras,
    ],
  );

  // Reading a stage does not change the canonical stage, report requirements
  // or Intent. Scope the review override to the site and owner.
  const decisionReviewScope = JSON.stringify([workflowSite.id, workflowSite._ownerUid, guideUid]);
  const [decisionReviewOverride, setDecisionReviewOverride] = useState<{ scope: string; stage: 1 | 2 } | null>(null);
  const decisionReviewStage = decisionReviewOverride?.scope === decisionReviewScope
    ? decisionReviewOverride.stage : pathToGoSharedState.decisionStage?.currentStage ?? 1;
  const screeningDecisionEvidence = useMemo(() => {
    if (activeTab !== 'decision' || decisionReviewStage !== 1) return null;
    try {
      return buildScreeningDecisionEvidenceSnapshot({
        site: workflowSite,
        sed: docsTabSed ?? null,
        reports: siteReports,
        analysisGateEnabled,
      });
    } catch (err) {
      console.error('[SiteLandingPage] Failed to build screening decision evidence snapshot:', err);
      return null;
    }
  }, [activeTab, decisionReviewStage, workflowSite, docsTabSed, siteReports, analysisGateEnabled]);

  // Display provenance only: the Decide form still gates on the untouched
  // page-level state. Use the same saved-body/readiness rules as its signals.
  const validationScoreReportIds = useMemo(() => selectDecisionStageReports(
    findingsAnalyses, 2, pathToGoSharedState.reportPlan, workflowSite.id,
  ).map(report => report.id), [findingsAnalyses, pathToGoSharedState.reportPlan, workflowSite.id]);

  // Stage 2 decisions — the checklist the Design view leads with once early
  // screening completes. Strategy and review writes respect the site lock.
  const stage2Decisions = pathToGoSharedState.stage2Decisions;
  const handleSetStage2Strategy = useCallback(async (value: BtmStrategy) => {
    if (lockReadOnly) return;
    try {
      await updateSiteTypedPreferencesDefault(site.id, 'power-availability', {
        ...(site.typedPreferencesDefaults?.['power-availability'] ?? {}),
        btmStrategy: value,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the power strategy.');
      throw error;
    }
  }, [lockReadOnly, site.id, site.typedPreferencesDefaults, updateSiteTypedPreferencesDefault]);
  const handleSaveStage2Review = useCallback(async (review: Stage2DecisionReview) => {
    if (lockReadOnly) return;
    try {
      await updateSite(site.id, { stage2DecisionReview: review });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the decision review.');
    }
  }, [lockReadOnly, site.id, updateSite]);

  const [pathToGoDecisionRecords, setPathToGoDecisionRecords] =
    useState<PathToGoDecisionRecord[]>([]);
  /**
   * Whether `pathToGoDecisionRecords` is a claim about the audit trail. Only
   * `loaded` is: until auth resolves and the read succeeds, an empty list means
   * "unknown", not "nothing recorded". The Decision tab's Decide section holds
   * its record form until `loaded` (a new record supersedes the current one on
   * the server).
   */
  const [decisionHistoryStatus, setDecisionHistoryStatus] =
    useState<DecisionHistoryStatus>('loading');
  const [decisionHistoryReloadNonce, setDecisionHistoryReloadNonce] = useState(0);
  const decisionHistoryStage = decisionFindingsEnabled ? decisionReviewStage : 2;
  const decisionHistoryScope = JSON.stringify([guideUid, workflowSite.id, workflowSite._ownerUid, decisionHistoryStage]);
  // A write may complete after navigation; never insert its response into a
  // different site/owner/stage history view.
  const decisionHistoryScopeRef = useRef(decisionHistoryScope);
  decisionHistoryScopeRef.current = decisionHistoryScope;
  const [decisionHistoryReadScope, setDecisionHistoryReadScope] = useState<string | null>(null);
  const scopedDecisionHistoryStatus = decisionHistoryReadScope === decisionHistoryScope ? decisionHistoryStatus : 'loading';
  const scopedDecisionRecords = scopedDecisionHistoryStatus === 'loaded' ? pathToGoDecisionRecords : NO_DECISION_RECORDS;

  useEffect(() => {
    let cancelled = false;
    setDecisionHistoryReadScope(decisionHistoryScope);
    setDecisionHistoryStatus('loading');
    if (!guideUid) {
      // Auth has not resolved: the history is unknown, not empty.
      setPathToGoDecisionRecords([]);
      return () => { cancelled = true; };
    }
    void listPathToGoDecisions(workflowSite, 20, decisionHistoryStage)
      .then(records => {
        if (!cancelled) {
          setPathToGoDecisionRecords(records);
          setDecisionHistoryStatus('loaded');
        }
      })
      .catch(error => {
        if (!cancelled) {
          console.warn(
            '[PathToGO] decision history unavailable:',
            (error as Error)?.message ?? error,
          );
          setDecisionHistoryStatus('failed');
        }
      });
    return () => { cancelled = true; };
  }, [guideUid, workflowSite.id, workflowSite._ownerUid, decisionHistoryStage, decisionHistoryScope, decisionHistoryReloadNonce]);

  // Brief reads the immutable Stage 1 chain independently of the selected
  // Decision view. Scope auth, owner and site before rendering a prior result.
  const briefScreeningEnabled = activeTab === 'stakeholder' && splitDecisionSurfacesEnabled && callReadyFindingsEnabled;
  const briefScreeningScope = JSON.stringify([workflowSite.id, workflowSite._ownerUid ?? null]);
  const briefScreeningReadScope = JSON.stringify([guideUid, workflowSite.id, workflowSite._ownerUid ?? null]);
  const [briefScreeningHistory, setBriefScreeningHistory] = useState<{
    scope: string; status: DecisionHistoryStatus; record: PathToGoDecisionRecord | null;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    if (!briefScreeningEnabled || !guideUid) {
      setBriefScreeningHistory(null);
      return () => { cancelled = true; };
    }
    setBriefScreeningHistory({ scope: briefScreeningReadScope, status: 'loading', record: null });
    void listPathToGoDecisions(workflowSite, 20, 1).then(records => {
      if (cancelled) return;
      if (records.some(record => record.siteId !== workflowSite.id || record.decisionStage !== 1)) {
        throw new Error('Screening history belongs to another site or stage.');
      }
      setBriefScreeningHistory({ scope: briefScreeningReadScope, status: 'loaded', record: records[0] ?? null });
    }).catch(() => {
      if (!cancelled) setBriefScreeningHistory({ scope: briefScreeningReadScope, status: 'failed', record: null });
    });
    return () => { cancelled = true; };
  }, [briefScreeningEnabled, guideUid, workflowSite.id, workflowSite._ownerUid, briefScreeningReadScope]);

  const briefScreeningReview = useMemo<BriefScreeningReviewData>(() => {
    const sourceLoading = !guideUid || !reportsLoaded || docsTabSedReadBlocked || decisionAnalysesPending;
    const evidence = briefScreeningEnabled && !sourceLoading
      ? buildScreeningDecisionEvidenceSnapshot({ site: workflowSite, sed: docsTabSed ?? null, reports: siteReports, analysisGateEnabled }) : null;
    const reportId = evidence?.screening?.report?.id;
    const report = reportId ? siteReports.find(candidate => candidate.id === reportId) ?? null : null;
    const currentBasis = evidence ? { evidence, report } : null;
    const exactReport = resolveCurrentScreeningRecommendationReport(workflowSite, currentBasis);
    const history = briefScreeningHistory?.scope === briefScreeningReadScope ? briefScreeningHistory : null;
    return { scope: briefScreeningScope, historyStatus: history?.status ?? 'loading', record: history?.record ?? null,
      sourceStatus: sourceLoading ? 'loading' : 'settled',
      currentBasis: exactReport ? { evidence, report: exactReport } : null };
  }, [guideUid, reportsLoaded, docsTabSedReadBlocked, decisionAnalysesPending, briefScreeningEnabled,
    workflowSite, docsTabSed, siteReports, analysisGateEnabled, briefScreeningHistory, briefScreeningReadScope, briefScreeningScope]);

  const buildCurrentPathToGoDecisionEnvelope = useCallback(() => {
    return {
      state: pathToGoSharedState,
      evidence: buildPathToGoDecisionEvidenceSnapshot({
        site: workflowSite,
        intent: workflowSite.projectIntent,
        reports: siteReports,
        state: pathToGoSharedState,
      }),
    };
  }, [
    workflowSite,
    siteReports,
    pathToGoSharedState,
  ]);

  const handleRecordPathToGoDecision = useCallback(async (draft: {
    decision: 'GO' | 'CONDITIONAL' | 'NO_GO' | 'DEFER';
    rationale: string;
    decisionOwner: string;
  }) => {
    if (lockReadOnly) {
      throw new Error(`This site is locked by ${lockHolderLabel}. Request edit access first.`);
    }
    const { state, evidence } = buildCurrentPathToGoDecisionEnvelope();
    if (
      (draft.decision === 'GO' || draft.decision === 'CONDITIONAL')
      && !pathToGoPositiveDecisionAllowed(state)
    ) {
      throw new Error(
        'GO and CONDITIONAL require current decision intent, required reports, and verification.',
      );
    }
    const record = await submitPathToGoDecision({
      site: workflowSite,
      ...draft,
      evidence,
    });
    if (decisionHistoryScopeRef.current === JSON.stringify([guideUid, workflowSite.id, workflowSite._ownerUid, 2])) {
      setPathToGoDecisionRecords(current => [record, ...current.filter(candidate => candidate.id !== record.id)]);
    }
    toast.success(`Decision v${record.version} recorded and awaiting approval.`);
  }, [
    lockHolderLabel,
    lockReadOnly,
    buildCurrentPathToGoDecisionEnvelope,
    workflowSite,
    guideUid,
  ]);

  const handleRecordScreeningDecision = useCallback(async (draft: ScreeningDecisionDraft) => {
    if (lockReadOnly) throw new Error(`This site is locked by ${lockHolderLabel}. Request edit access first.`);
    if (!reportsLoaded || docsTabSedReadBlocked || decisionAnalysesPending) throw new Error('Wait for the current evidence and screening reports to finish loading.');
    const evidence = buildScreeningDecisionEvidenceSnapshot({
      site: workflowSite, sed: docsTabSed ?? null, reports: siteReports, analysisGateEnabled,
    });
    if ((draft.decision === 'PROCEED' || draft.decision === 'PROCEED_WITH_CONCERNS') && !screeningPositiveDecisionAllowed(evidence)) {
      throw new Error(evidence.screening?.reason ?? 'A current eligible saved screening is required before proceeding.');
    }
    const record = await submitPathToGoDecision({ site: workflowSite, ...draft, decisionStage: 1, evidence });
    if (decisionHistoryScopeRef.current === JSON.stringify([guideUid, workflowSite.id, workflowSite._ownerUid, 1])) {
      setPathToGoDecisionRecords(current => [record, ...current.filter(candidate => candidate.id !== record.id)]);
    }
    toast.success(`Screening decision v${record.version} recorded and awaiting approval.`);
  }, [lockReadOnly, lockHolderLabel, reportsLoaded, docsTabSedReadBlocked, decisionAnalysesPending,
    workflowSite, docsTabSed, siteReports, analysisGateEnabled, guideUid]);

  const handleReviewPathToGoDecision = useCallback(async (
    recordId: string,
    outcome: 'approved' | 'rejected',
    rejectionReason?: string,
  ) => {
    if (lockReadOnly) {
      throw new Error(`This site is locked by ${lockHolderLabel}. Request edit access first.`);
    }
    const current = pathToGoDecisionRecords.find(record => record.id === recordId);
    if (!current) throw new Error('Decision record not found. Reload the site and try again.');
    const { state, evidence } = buildCurrentPathToGoDecisionEnvelope();
    if (
      outcome === 'approved'
      && (current.decision === 'GO' || current.decision === 'CONDITIONAL')
      && !pathToGoPositiveDecisionAllowed(state)
    ) {
      throw new Error(
        'GO and CONDITIONAL cannot be approved while required evidence is missing or stale.',
      );
    }
    const reviewEvidence = current.decisionStage === 1
      ? buildScreeningDecisionEvidenceSnapshot({ site: workflowSite, sed: docsTabSed ?? null, reports: siteReports, analysisGateEnabled })
      : evidence;
    if (outcome === 'approved' && current.decisionStage === 1
      && (current.decision === 'PROCEED' || current.decision === 'PROCEED_WITH_CONCERNS')
      && !screeningPositiveDecisionAllowed(reviewEvidence)) {
      throw new Error(reviewEvidence.screening?.reason ?? 'The current screening source is not eligible for approval.');
    }
    const reviewed = await reviewPathToGoDecision({
      site: workflowSite,
      recordId,
      outcome,
      rejectionReason,
      currentEvidence: reviewEvidence,
    });
    if (decisionHistoryScopeRef.current === JSON.stringify([guideUid, workflowSite.id, workflowSite._ownerUid, current.decisionStage ?? 2])) {
      setPathToGoDecisionRecords(records => records.map(record => record.id === reviewed.id ? reviewed : record));
    }
    toast.success(
      `Decision v${reviewed.version} ${outcome === 'approved' ? 'approved' : 'rejected'}.`,
    );
  }, [
    lockHolderLabel,
    lockReadOnly,
    pathToGoDecisionRecords,
    buildCurrentPathToGoDecisionEnvelope,
    workflowSite,
    docsTabSed, siteReports, analysisGateEnabled, guideUid,
  ]);

  // ── Decision record / review wiring for the Decide surface (the Decision
  // tab, or Analyses when that tab is rolled back). The Path to GO Decide
  // phase that also used it was deleted in Path to GO removal stage 3. ──
  /**
   * ROLE GATE — who may approve / reject a submitted decision. Review controls
   * receive the handler only when this is true; the server re-checks it
   * (`assertDecisionApprover`). Unchanged predicate, now named once.
   */
  const canReviewDecisions =
    lockIsPlatformAdmin || lockCompanyRole === 'approver' || lockCompanyRole === 'admin';
  /** Default decision owner for a new record — the signed-in user. */
  const decisionOwnerDefault =
    faAuth.currentUser?.displayName
    || faAuth.currentUser?.email
    || '';
  /** Read-only viewers are told who to ask; nothing is written. */
  const requestSiteEditAccess = useCallback(() => {
    toast.info(`Request edit access from ${lockHolderLabel} to change this site.`);
  }, [lockHolderLabel]);
  /**
   * RP2 — route an in-place SED amendment through the ONE persist path.
   * Lock-gated inline (guardLockedAction discards the return, but the dialog
   * needs the SaveAmendmentResult). The amendment bumps sedVersion (→
   * docsTabSed reload) AND stale-stamps the composite (the RP3 keystone), so
   * the meter + verdict move.
   */
  const handleResolveInPlace = useCallback(async (
    amendment: SEDAmendment,
  ): Promise<SaveAmendmentResult> => {
    if (lockReadOnly) {
      toast.error(
        `This site is locked for editing by ${lockHolderLabel}. Request edit access to make changes.`,
      );
      return { ok: false, error: 'Site is locked for editing' };
    }
    return useStore.getState().saveSEDAmendment(amendment);
  }, [lockReadOnly, lockHolderLabel]);

  /**
   * Typed Path-to-GO routing. Each action lands on the owning panel instead of
   * translating every dependency into a generic tab jump. Used by Analyses,
   * the Home next action and the report wizard; it no longer enters guided
   * mode (retired with the Path to GO tab, stage 2).
   */
  const handlePathToGoAction = useCallback((action: PathAction) => {
    switch (action.kind) {
      case 'intent':
        // The grid / hybrid / BTM-only strategy is a Stage 2 decision: legacy
        // `btmStrategy` actions land on its checklist card, which writes the
        // same report-setup field (and disables itself on a locked site).
        if (action.field === 'btmStrategy') {
          setIntentInputView('detailed');
          setIntentFieldFocus(stage2FocusField('power-supply'));
          setActiveTab('intent');
          return;
        }
        if (action.view === 'design' || action.field?.startsWith('stage2:')) setIntentInputView('detailed');
        setIntentFieldFocus(action.field ?? null);
        setActiveTab('intent');
        return;
      case 'site-boundary':
        setActiveTab('map');
        if (!lockReadOnly) setLocationBoundaryEditorMode('boundary');
        return;
      case 'evidence':
        if (action.targetId) setFocus({ kind: 'variable', id: action.targetId });
        setActiveTab('evidence');
        return;
      case 'report-setup':
        if (lockReadOnly) {
          toast.error(`This site is locked for editing by ${lockHolderLabel}. Request edit access to generate a report.`);
          return;
        }
        setPickerInitialType(action.reportType);
        setWizardForceNew(true);
        setShowReportTypePicker(true);
        return;
      case 'report-progress':
        setActiveTab('reports');
        return;
      case 'analysis-open':
        setPendingFindingsAnalysisId(action.recordId);
        setActiveTab('reports');
        return;
      case 'report-qa':
        // Report QA is owned by Audit, not by the printable reader. Preserve
        // the plan row's report identity so "Review analysis · BTM
        // Feasibility" selects and scrolls to that report's exact quality /
        // reconciliation queue instead of opening the reader or an unfiltered
        // report list.
        if (action.reportId) {
          setActiveTab('audit');
          navigate(reportAuditPath(site.id, action.reportId));
          return;
        }
        setActiveTab('audit');
        navigate(siteOverviewPath(site.id, 'audit'));
        return;
      case 'generation-impact':
        // Every focus except 'scenario' names an action that lives on the
        // handoff step (Save scenario / Promote / Publish layout are all in
        // GenerationImpactTestFitWorkflow). Mapping only 'layout' there sent a
        // user who clicked a CTA labelled "Save scenario" to Step 1 and left
        // them to find Step 6 themselves.
        openGenerationWorkflow(
          action.focus === 'scenario' ? 'requirements' : 'handoff',
        );
        return;
      case 'sed-rebuild':
        openGenerationWorkflow('handoff');
        return;
      case 'bom':
        setActiveTab('supplychain');
        return;
      case 'financial-inputs':
        setActiveTab('financial');
        return;
      case 'audit':
        setFocus({ kind: 'conflicts', id: '' });
        setActiveTab('audit');
        setOverviewDataConfidenceOpen(true);
        return;
      case 'request-access':
        toast.info(
          action.destination === 'generation-impact'
            ? 'Ask a workspace administrator to enable Generation & Impact or grant edit access.'
            : `This site is read-only. Request edit access from ${lockHolderLabel}.`,
        );
        return;
      case 'none':
        return;
    }
  }, [
    lockHolderLabel,
    lockReadOnly,
    navigate,
    openGenerationWorkflow,
    site.id,
  ]);

  /**
   * EVIDENCE LEDGER wiring.
   *
   * `evidenceSteps` is the SAME selector the (now deleted) Path to GO Evidence
   * stage read, so the tab and the hub could not list different work for one
   * site. Before that the hub filtered `rankedSteps` inline while the tab
   * derived its own list from
   * `documentChecklists` — which is how Redhead came to show "4 open ·
   * 1 GO-blocking" and "80% READY · 0 Missing" simultaneously.
   */
  const evidenceSteps = useMemo(
    // Waived requirements drop OUT of the worklist. Without this the user
    // records "I checked, the source has no record" and the gap keeps
    // demanding the very thing they just attested was unobtainable. The waiver
    // stays visible in the Waived gaps register — removed from the work, not
    // from the record.
    () => selectEvidenceSteps(
      pathToGoSharedState.rankedSteps,
      new Set(Object.keys(site.requirementWaivers ?? {})),
    ),
    [pathToGoSharedState.rankedSteps, site.requirementWaivers],
  );

  /**
   * Resolve one ledger task with its primary verb. Every branch routes through
   * an EXISTING handler — no new navigation paths were invented for the ledger.
   */
  const handleResolveEvidenceStep = useCallback(
    (step: NextStep, verb: EvidenceVerbKind) => {
      const targetId = step.resolver?.targetId;
      switch (verb) {
        case 'intent':
          handlePathToGoAction({ kind: 'intent', label: step.label });
          return;
        case 'verify':
          // Documents exist; the ask is to confirm what extraction pulled.
          // Land on the requirement's worksheet, not the upload widget.
          setActiveTab('evidence');
          if (targetId) setFocus({ kind: 'variable', id: targetId });
          return;
        case 'upload':
          if (targetId) jumpToDocumentsUploadFor(targetId);
          else goToLibrary();
          return;
        case 'recollect': {
          // A provider section that did not collect. Uploading cannot fix it —
          // only re-running the provider can, so DO that here rather than
          // telling the user to go and find the lever themselves.
          setActiveTab('evidence');
          const sectionId = targetId as SedSectionId | undefined;
          if (sectionId && (SED_SECTION_IDS as readonly string[]).includes(sectionId)) {
            void handleRecollectSection(sectionId, step.label);
            return;
          }
          // No resolvable section id — fall back to the full-SED retry, which is
          // the same action the failed-SED preflight banner dispatches. Still a
          // real re-run, just unscoped.
          void handleRetryBootstrap();
          return;
        }
        case 'open':
        default:
          if (targetId) setFocus({ kind: 'variable', id: targetId });
          setActiveTab('evidence');
          return;
      }
    },
    // `handleRecollectSection` / `handleRetryBootstrap` are plain (unmemoised)
    // functions, so listing them re-creates this callback each render. That is
    // the correct trade: `handleRecollectSection` closes over `reCollecting`,
    // and a stale capture would let a second run start while one is in flight.
    [handlePathToGoAction, jumpToDocumentsUploadFor, goToLibrary,
      handleRecollectSection, handleRetryBootstrap],
  );

  /**
   * "Draft email request" target on the Site Evidence ledger. The contractor
   * outreach dialog opens right here (Path to GO removal, stage 1) — it used to
   * route to Path to GO, where the user had to find the same row again. Cleared
   * whenever the Evidence tab is left, so it can never reopen on return.
   */
  const [evidenceOutreachTarget, setEvidenceOutreachTarget] = useState<{
    id: string;
    label: string;
  } | null>(null);
  React.useEffect(() => {
    if (activeTab !== 'evidence') setEvidenceOutreachTarget(null);
  }, [activeTab]);

  /** Secondary affordance per verb. Also reuses existing routes only. */
  const handleSecondaryEvidenceAction = useCallback(
    (step: NextStep, verb: EvidenceVerbKind) => {
      const targetId = step.resolver?.targetId;
      if (verb === 'upload') {
        // "Draft email request" — open the contractor outreach dialog (moved
        // here from Path to GO's Verify phase), keyed on the step's outreach
        // template.
        if (step.outreachId) {
          setEvidenceOutreachTarget({
            id: step.outreachId,
            label: outreachDocTypeLabel(step.label),
          });
          return;
        }
        // `upload` only carries this secondary when the step has an outreach
        // template (evidenceStepVerb); without one there is nothing to draft,
        // so the document upload is the honest ask.
        if (targetId) jumpToDocumentsUploadFor(targetId);
        else goToLibrary();
        return;
      }
      if (verb === 'verify') {
        if (targetId) jumpToDocumentsForReq(targetId);
        return;
      }
      if (verb === 'author') {
        // "Upload another document" — the attached one yielded nothing, so the
        // ask is a different document, not the same one again.
        const reqId = step.id.startsWith('coverage:')
          ? step.id.slice('coverage:'.length)
          : targetId;
        if (reqId) jumpToDocumentsUploadFor(reqId);
        else goToLibrary();
        return;
      }
      // No `recollect` branch: that verb no longer offers a secondary. See
      // evidenceStepVerb() — the old "Provider status" button had nothing it
      // could open, so it was removed rather than left as a dead click.
    },
    [jumpToDocumentsForReq],
  );

  /**
   * The tab a blocker remediation opens (open-intent → Intent, open-tab → that
   * tab, open-evidence / anything else → Site evidence), WITHOUT navigating —
   * null when it names an optional workspace this build does not offer. The
   * navigation below and every label that names its destination read this one
   * resolver, so a link can never say one tab and open another.
   */
  const decisionStepTab = useCallback((step: NextStep): Tab | null => {
    const action = step.resolver?.action;
    const targetId = step.resolver?.targetId;
    if (action === 'open-intent') return targetId === 'btmStrategy' ? 'reports' : 'intent';
    if (action === 'open-tab' && targetId) {
      return visibleTabs.includes(targetId as Tab) ? targetId as Tab : null;
    }
    return 'evidence';
  }, [visibleTabs]);

  /**
   * Decision tab — route a blocker remediation to the tab that owns it (see
   * decisionStepTab; open-evidence lands at the exact variable). Carried over
   * from the Path to GO wizard's resolve routing; guided mode, which that
   * routing used to enter, is gone. Returns the tab it opened (null when it
   * opened nothing).
   */
  const handleDecisionResolveStep = useCallback((step: NextStep): Tab | null => {
    const tab = decisionStepTab(step);
    if (tab === null) {
      toast.info('That optional workspace is not available in this release.');
      return null;
    }
    const action = step.resolver?.action;
    const targetId = step.resolver?.targetId;
    if (action === 'open-intent') {
      if (targetId === 'btmStrategy') {
        // Same Stage 2 decision card as the "Do this next" route.
        setIntentInputView('detailed');
        setIntentFieldFocus(stage2FocusField('power-supply'));
        setActiveTab('intent');
        return 'intent';
      }
      if (targetId?.startsWith('stage2:')) setIntentInputView('detailed');
      setIntentFieldFocus(targetId ?? null);
    } else if (action === 'open-evidence' && targetId) {
      setFocus({ kind: 'variable', id: targetId });
    }
    setActiveTab(tab);
    return tab;
  }, [decisionStepTab, lockReadOnly, lockHolderLabel]);

  /**
   * Decision tab — open the first piece of work of a prerequisite phase that
   * still holds GO / CONDITIONAL. Picks the step with the SAME predicate
   * `buildPathToGoState` counts the phase with (define: intent steps + the
   * boundary step; verify: preflight steps touching a required analysis) and
   * routes it exactly as a blocker remediation is routed. Navigation only —
   * nothing here feeds a gate. Returns the tab it opened.
   */
  const decisionPrerequisiteStep = useCallback(
    (phaseId: 'define' | 'verify'): NextStep | undefined => {
      const steps = pathToGoSharedState.rankedSteps;
      if (phaseId === 'define') {
        return steps.find(
          step => step.kind === 'intent' || step.id === 'site-basis:boundary',
        );
      }
      const required = new Set(pathToGoSharedState.requiredReports);
      return steps.find(step => (
        (step.affectedAnalysisTypes ?? []).some(type => required.has(type))
      ));
    },
    [pathToGoSharedState.rankedSteps, pathToGoSharedState.requiredReports],
  );
  /** Where handleDecisionOpenPrerequisite(phase) lands, without navigating. */
  const decisionPrerequisiteTab = (phaseId: DecidePrerequisitePhaseId): Tab | null => {
    if (phaseId === 'build') return 'reports';
    const first = decisionPrerequisiteStep(phaseId);
    if (first?.resolver) return decisionStepTab(first);
    return phaseId === 'define' ? 'intent' : 'evidence';
  };
  const handleDecisionOpenPrerequisite = useCallback(
    (phaseId: DecidePrerequisitePhaseId): Tab | null => {
      if (phaseId === 'build') {
        setActiveTab('reports');
        return 'reports';
      }
      const first = decisionPrerequisiteStep(phaseId);
      if (first?.resolver) {
        return handleDecisionResolveStep(first);
      }
      const fallback: Tab = phaseId === 'define' ? 'intent' : 'evidence';
      setActiveTab(fallback);
      return fallback;
    },
    [decisionPrerequisiteStep, handleDecisionResolveStep],
  );

  /**
   * Path to GO removal, stage 2 — the ONE in-page router for every former
   * "open Path to GO [at a phase]" entry point (Home journey band + verdict
   * pill, Stakeholder Brief, the Decision dossier, Scoring Engine, the report
   * wizard). A work phase (define / build / verify) lands on the tab that owns
   * its first open item — the same routing as the Decide surface's
   * prerequisite links. Decide, or no phase, lands on the Decide surface of
   * the Decision tab (Analyses when that tab is rolled back) and scrolls to
   * it. Navigation only: nothing here feeds a gate.
   */
  const [decideScrollRequest, setDecideScrollRequest] = useState(0);
  const openDecisionWork = useCallback(
    (phase?: PathToGoPhaseId): Tab | null => {
      if (phase === 'define' || phase === 'build' || phase === 'verify') {
        return handleDecisionOpenPrerequisite(phase);
      }
      setDecideScrollRequest(request => request + 1);
      setActiveTab(decisionHomeTab);
      return decisionHomeTab;
    },
    [handleDecisionOpenPrerequisite, decisionHomeTab],
  );

  /**
   * Analyses "Resolve inputs" for an analysis with no record and no plan
   * action: open the first ranked step that blocks THAT analysis, routed like
   * a Decision-tab blocker; otherwise the first verification item that still
   * holds a required analysis. (It used to open the Path to GO hub.)
   */
  const openAnalysisInputs = useCallback(
    (analysisType: ReportTypeId) => {
      const step = pathToGoSharedState.rankedSteps.find(candidate => (
        (candidate.affectedAnalysisTypes ?? []).includes(analysisType)
      ));
      if (step?.resolver) {
        handleDecisionResolveStep(step);
        return;
      }
      openDecisionWork('verify');
    },
    [pathToGoSharedState.rankedSteps, handleDecisionResolveStep, openDecisionWork],
  );

  /**
   * Home's next-action CTA, the report wizard's plan actions and the Analyses /
   * Decision dossier's plan actions dispatch the SAME typed plan action the
   * Path to GO next-action card dispatched (handlePathToGoAction). The two
   * kinds that handler does not route are routed here instead of doing
   * nothing:
   *  - a cleared "Review decision" (`none`) opens the Decide surface;
   *  - `engineering-source` ("Resolve inputs": the selected simulation
   *    authority holds the analysis) opens Engineering Analysis, where that
   *    simulation is re-solved or replaced — or, in a build without it, the
   *    blocked analysis's own inputs (`analysisType`), else the work of the
   *    action's phase.
   */
  const runDecisionPlanAction = useCallback(
    (action: PathAction, phase?: PathToGoPhaseId, analysisType?: ReportTypeId) => {
      if (action.kind === 'none') {
        openDecisionWork('decide');
        return;
      }
      if (action.kind === 'engineering-source') {
        if (visibleTabs.includes('simulations')) {
          setActiveTab('simulations');
          return;
        }
        if (analysisType) {
          openAnalysisInputs(analysisType);
          return;
        }
        openDecisionWork(phase ?? 'build');
        return;
      }
      handlePathToGoAction(action);
    },
    [handlePathToGoAction, openDecisionWork, openAnalysisInputs, visibleTabs],
  );

  /**
   * Evidence arrival from the Decision tab. A blocker or verification route
   * that lands on Site evidence offers "Back to Decision" (the old ledger
   * strip returned to Path to GO). Cleared as soon as the user leaves Site
   * evidence, so a later visit is not mislabelled as an arrival.
   */
  const [evidenceFromDecision, setEvidenceFromDecision] = useState(false);
  useEffect(() => {
    if (activeTab !== 'evidence') setEvidenceFromDecision(false);
  }, [activeTab]);
  /** Call with the tab a Decision-tab route opened; arms "Back to Decision". */
  const noteDecisionRoute = (openedTab: Tab | null) => {
    if (openedTab === 'evidence') setEvidenceFromDecision(true);
  };

  // Intent actions carry the exact owning field. The canonical Project Intent
  // editor is a stepper whose inactive panels remain mounted but hidden, so
  // select the owning step first and then transfer focus to its first control.
  // This makes Path-to-GO deep links useful to keyboard and screen-reader users
  // instead of merely landing on the generic Intent tab.
  React.useEffect(() => {
    if (activeTab !== 'intent' || !intentFieldFocus) return;

    let focusFrame = 0;
    const selectFrame = window.requestAnimationFrame(() => {
      const root = document.getElementById('overview-intent-editor');
      const field = root
        ? Array.from(root.querySelectorAll<HTMLElement>('[data-field-id]'))
            .find(candidate => candidate.dataset.fieldId === intentEditorFieldId(intentFieldFocus))
        : null;
      if (!field) {
        setIntentFieldFocus(null);
        return;
      }

      const panel = field.closest<HTMLElement>('[role="tabpanel"]');
      if (panel?.hidden) {
        const tabId = panel.getAttribute('aria-labelledby');
        const tab = tabId ? document.getElementById(tabId) : null;
        if (tab instanceof HTMLButtonElement) tab.click();
      } else if (!panel && field.dataset.stepId && root) {
        const stepTab = root.querySelector<HTMLButtonElement>(
          `[role="tab"][id$="-step-tab-${field.dataset.stepId}"]`,
        );
        if (stepTab && stepTab.getAttribute('aria-selected') !== 'true') {
          stepTab.click();
        }
      }

      focusFrame = window.requestAnimationFrame(() => {
        const currentRoot = document.getElementById('overview-intent-editor');
        const currentField = currentRoot
          ? Array.from(currentRoot.querySelectorAll<HTMLElement>('[data-field-id]'))
              .find(candidate => candidate.dataset.fieldId === intentEditorFieldId(intentFieldFocus))
          : null;
        const control = currentField?.querySelector<HTMLElement>(
          'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        control?.focus({ preventScroll: true });
        if (currentField && typeof currentField.scrollIntoView === 'function') {
          currentField.scrollIntoView({ block: 'center', behavior: 'auto' });
        }
        setIntentFieldFocus(null);
      });
    });

    return () => {
      window.cancelAnimationFrame(selectFrame);
      if (focusFrame) window.cancelAnimationFrame(focusFrame);
    };
  }, [activeTab, intentFieldFocus]);

  /**
   * SET-B3 — human-readable label for the current focus, surfaced in
   * the cross-tab breadcrumb. Defaults to the raw id when the doc
   * can't be resolved (deleted, filtered, or arriving from a deep-
   * link that pre-dates the doc rename). The label intentionally
   * stays terse — the breadcrumb is a single line at 10px.
   */
  const focusLabel = useMemo<string>(() => {
    if (!focus) return '';
    if (focus.kind === 'doc') {
      const doc = siteDocuments.find(d => d.id === focus.id);
      return doc ? doc.fileName : focus.id;
    }
    if (focus.kind === 'req' || focus.kind === 'upload-for') {
      // EDX-3 / EDX-8 — checklist requirement id. Resolve to the
      // requirement label via the union checklist; falls through to
      // the raw id for unknown ids.
      try {
        const allItems = getDocumentChecklistAcross(
          Object.values(REPORT_TYPE_REGISTRY)
            .filter(t => t.status === 'active' || t.status === 'beta')
            .map(t => t.id) as ReportTypeId[],
        );
        const item = allItems.find(i => i.id === focus.id);
        if (item) {
          return focus.kind === 'upload-for'
            ? `Upload for ${item.label}`
            : item.label;
        }
      } catch { /* fall through */ }
      return focus.id;
    }
    if (focus.kind === 'conflicts') {
      // EDX-10 — bare conflicts focus lands on the panel; no id.
      return 'Unresolved conflicts';
    }
    if (focus.kind === 'replace') {
      // CMU-2 — `replace:<docId>::<requirementId>`; resolve docId →
      // filename so the breadcrumb reads "Replace utility-letter.pdf"
      // instead of an opaque id. Falls through to the raw id when the
      // doc can't be resolved (e.g. deleted before the deep link
      // resolved).
      const [docId] = focus.id.split('::');
      const doc = siteDocuments.find(d => d.id === docId);
      return doc ? `Replace ${doc.fileName}` : `Replace ${docId ?? focus.id}`;
    }
    if (focus.kind === 'variable') {
      // A bare SED-section token is a camelCase internal key. Rendering it raw
      // produced "Focused on publicSentiment" — an identifier shown to the user
      // as if it were a name. Resolve through the canonical display map; dotted
      // `section.field` paths and unmapped ids keep the previous raw text.
      const label = SED_SECTION_LABELS[focus.id as SedSectionId];
      if (label) return label;
    }
    return focus.id;
  }, [focus, siteDocuments]);

  // Load cloud simulations for this site. Hoisted to a callback so saved-sim
  // card mutations (delete/rename) can re-fetch the list after they settle.
  const fetchCloudSims = useCallback(async () => {
    try {
      const { auth, db } = await import('../../firebase');
      const user = auth.currentUser;
      if (!user) return;
      const { collection, getDocs, query, where } = await import('firebase/firestore');
      const q = query(collection(db, `users/${user.uid}/simulations`), where('siteId', '==', site.id));
      const snap = await getDocs(q);
      setCloudSims(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch { /* no cloud sims */ }
    setCloudSimsLoaded(true);
  }, [site.id]);

  React.useEffect(() => {
    void fetchCloudSims();
  }, [fetchCloudSims]);

  // Build a simulation `Site` object from the SiteEntity + latest report data.
  // Hoisted out of handleCreateSimulation so the BoM-regeneration path can
  // open its generated canvas WITHIN the same site context (climate, preload).
  const buildSimSite = useCallback((): import('../../simulation/types').Site => {
    // HOTFIX(lat-crash): `site.location` / `site.siteCentroid` are typed
    // required `{lat,lng}` but are NOT runtime-validated — a partial / orphan
    // stub doc carries neither. The simulation `Site.location` /
    // `viewport.center` are also typed required, so seed a finite null-island
    // fallback (mirrors the map components' terminal `{lat:0,lng:0}` guard)
    // rather than leaking `undefined` into the canvas, which crashed downstream
    // geo projection on `.lat`.
    const studySite = applySiteStudyArea(site);
    const simLocation = isFiniteLatLng(site.location) ? site.location : { lat: 0, lng: 0 };
    const simCenter = isFiniteLatLng(site.siteCentroid) ? site.siteCentroid : simLocation;
    const simSite: import('../../simulation/types').Site = {
      id: site.id,
      name: site.name,
      location: simLocation,
      zoom: 14,
      address: site.address,
      siteBoundary: studySite.siteBoundary,
      siteArea: studySite.siteArea,
      ragReportId: latestReport?.id,
      viewport: site.siteCentroid ? { center: simCenter, zoom: 15 } : { center: simCenter, zoom: 14 },
    };

    // Extract climate data from latest report if available
    if (sitePreload.ambientTempC != null) simSite.ambientTemp = sitePreload.ambientTempC;
    if (sitePreload.wetBulbTempC != null) simSite.wetBulbTemp = sitePreload.wetBulbTempC;
    if (sitePreload.peakTempC != null) simSite.peakTemp = sitePreload.peakTempC;
    if (sitePreload.humidityPct != null) simSite.humidity = sitePreload.humidityPct;
    if (sitePreload.waterStress === 'Low' || sitePreload.waterStress === 'Moderate' || sitePreload.waterStress === 'High') {
      simSite.waterStress = sitePreload.waterStress;
    }
    if (sitePreload.elevationM != null) simSite.elevation = sitePreload.elevationM;
    if (sitePreload.slopePct != null) simSite.slope = sitePreload.slopePct;
    simSite.sitePreload = sitePreload;
    if (latestReport?.timeSeries) {
      simSite.timeSeries = latestReport.timeSeries.map(ts => ({
        month: ts.month,
        avgTemp: ts.avgTemp,
        peakTemp: ts.peakTemp,
        wetBulbTemp: ts.wetBulbTemp ?? 0,
        coolingEfficiency: ts.wetBulbTemp ? Math.max(0, 1 - (ts.wetBulbTemp / 35)) : 0.5,
      }));
    }
    // EL2: skip the elevation hand-off when the saved triple is the
    // legacy zero-triple fingerprint (lookup-failed clobber) — let the
    // sim's own default fill in instead of seeding a false 0 m.
    if (site.siteElevationRange) {
      const r = site.siteElevationRange;
      const legacyZero = r.min === 0 && r.max === 0 && r.avg === 0;
      if (!legacyZero && Number.isFinite(r.avg)) {
        simSite.elevation = r.avg;
      }
    }
    return simSite;
  }, [site, latestReport, sitePreload]);

  // Owner request A (5 Oct 2026) — every Site → editor entry point links the
  // Canvas it opens to THIS Site (Site Configuration → Active Site) and records
  // the launch, so the empty Canvas can offer this Site's power strategy as a
  // starting point. A simulation already linked to another Site keeps that
  // link (only the user's own "Link site" action changes it); read-only
  // Canvases are never mutated. Call it after the tab to open is active.
  const linkOpenedCanvasToSite = useCallback(
    (simSite?: import('../../simulation/types').Site) => {
      const result = openActiveCanvasFromSite(simSite ?? buildSimSite());
      const notice = siteLaunchLinkNotice(result.decision, site.name);
      if (notice) toast.info(notice.message);
    },
    [buildSimSite, site.name],
  );

  // Generated models open in the EO editor for readiness review before any solve is allowed.

  const handleCreateSimulation = async () => {
    await ensureCanvasAssumptionBridge();
    const simSite = buildSimSite();

    // Create a new simulation tab with site data pre-filled
    addTab(`${site.name} — Engineering Analysis`, [], [], undefined, simSite);
    // The new Canvas is born linked (simSite); record that this Site opened it.
    linkOpenedCanvasToSite(simSite);

    // Link simulation to site
    const newTabId = useStore.getState().activeTabId;
    await updateSite(site.id, {
      simulationIds: [...site.simulationIds ?? [], newTabId],
    });

    // Auto-save to cloud so it persists on refresh
    try {
      const { auth, db } = await import('../../firebase');
      const user = auth.currentUser;
      if (user) {
        const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
        const state = useStore.getState();
        const tab = state.tabs.find(t => t.id === newTabId);
        if (tab) {
          const docRef = await addDoc(collection(db, `users/${user.uid}/simulations`), {
            userId: user.uid,
            name: tab.name,
            siteId: site.id,
            nodes: JSON.stringify(tab.nodes),
            edges: JSON.stringify(tab.edges),
            simState: JSON.stringify(tab.simState),
            site: JSON.stringify(tab.site),
            siteRag: '{}',
            version: 1,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
          useStore.getState().updateTab(newTabId, { cloudId: docRef.id, version: 1, autoSave: true });
        }
      }
    } catch (e) { console.warn('Auto-save simulation failed:', e); }

    // Switch to simulation view. C7 — also navigate to the neutral
    // /editor route; otherwise the App URL-sync effects see the
    // site-scoped URL still in the address bar and snap viewMode back
    // to siterag, so the editor never actually opens.
    setViewMode('simulation');
    navigate('/editor');
    toast.success(`Simulation created for ${site.name} with site data pre-loaded`);
  };

  // BoM→Simulation wizard entry. Generating a sim now goes through the
  // SHARED generation engine + save recipe (generateSimFromSite /
  // generateIndicativeSim → saveGeneratedSimToSite), producing a POPULATED,
  // reviewable, editable canvas instead of a blank one. This replaces both the
  // blank "New Simulation" canvas and the retired server-generated execution
  // path as the primary generate entry point.
  const openGenerateWizard = useCallback(() => {
    // #89 — generating a simulation attaches a sim to the site; gate the
    // opener for a locked non-holder so both "Generate simulation"
    // buttons (Engineering Analysis empty/populated states) are covered.
    if (lockReadOnly) {
      toast.error(`This site is locked for editing by ${lockHolderLabel}. Request edit access to make changes.`);
      return;
    }
    setGenerateWizardOpen(true);
  }, [lockReadOnly, lockHolderLabel]);

  // After the wizard generates + saves (the tab is already created + cloud-
  // linked by saveGeneratedSimToSite), open it on the canvas. C7 — navigate to
  // the neutral /editor route so the App URL-sync effects keep viewMode.
  const handleWizardGenerated = useCallback(
    (_tabId: string) => {
      setGenerateWizardOpen(false);
      // "Add to existing" merges into the ACTIVE Canvas, which may be unlinked.
      linkOpenedCanvasToSite();
      setViewMode('simulation');
      navigate('/editor');
    },
    [linkOpenedCanvasToSite, setViewMode, navigate],
  );

  const handleSaveName = async () => {
    if (!editName.trim()) return;
    await updateSite(site.id, { name: editName.trim() });
    setIsEditingName(false);
    toast.success('Site renamed');
  };

  const generationImpactRequired = useMemo(
    () => requiresGenerationImpactTestFit(
      generationImpactTestFitScopeFromSite(workflowSite),
    ),
    [workflowSite],
  );

  // Top-level tab order — the FUNNEL: the strip literally reads as the
  // journey to a GO decision (design pass A, William-approved 2026-07-21).
  // Home leads, then Define (set up) → Analyze (project modelling and
  // validation) → Decide (verdict + execution, Roadmap last = post-decision).
  // The Path to GO hub tab is retired (Path to GO removal, stage 2); its old
  // `?tab=pathtogo` links redirect in resolveTabFromParams.
  const baseTabs: { id: Tab; label: string; count?: number; icon: LucideIcon; group?: string }[] = [
    { id: 'home', label: 'Home', icon: Home },
    // ── Define — set up the project ──
    { id: 'intent', label: 'Intent', icon: Pencil, group: 'Define' },
    { id: 'map', label: 'Site Map', icon: MapPin, group: 'Define' },
    { id: 'documents', label: 'Documents', count: siteDocuments.length, icon: FolderOpen, group: 'Define' },
    { id: 'evidence', label: 'Site evidence', icon: BookOpen, group: 'Define' },
    // ── Analyze deep-dives (the side-quests) ──
    // SIM-COUNT-DELETE — count the REAL current sim set (cloud sims +
    // in-memory tabs), not the static `site.simulationIds` array.
    // `deleteCloudSim` removes the Firestore doc but never prunes
    // `site.simulationIds`, so reading that length left a stale badge
    // after a delete. `allSims` is the same union the tab body renders,
    // so the badge now tracks `fetchCloudSims()` refreshes exactly.
    { id: 'simulations', label: 'Engineering Analysis', count: allSims.length, icon: Activity, group: 'Analyze' },
    {
      id: 'generation',
      label: 'Generation & Impact',
      icon: Factory,
      // Owner copy decision (2026-09-06): the group header names the analysis
      // itself, not the Test Fit report that consumes it — Generation & Impact
      // is required for the decision on every non-grid-only site (P1.10).
      group: generationImpactRequired ? 'Build · Generation & Impact required' : 'Analyze',
    },
    { id: 'financial', label: 'Financial Model', icon: BarChart3, group: 'Analyze' },
    { id: 'supplychain', label: 'Equipment (BOM)', icon: Package, group: 'Analyze' },
    // ── Decide — verdict, integrity, post-decision execution ──
    {
      id: 'reports',
      label: decisionFindingsEnabled ? 'Analyses' : 'Reports',
      // The flagged Findings surface has an honest loading contract: zero is a
      // claim about the settled record, not a placeholder. Omit the count until
      // report metadata + current bodies have reached their terminal state.
      // Legacy Reports keeps its existing badge behaviour byte-for-byte.
      count: decisionFindingsEnabled
        ? (findingsAnalysesState === 'settled' ? findingsAnalysisTypeCount : undefined)
        : reportTypeCount,
      icon: FileText,
      group: 'Decide',
    },
    ...(splitDecisionSurfacesEnabled ? [
      { id: 'decision' as const, label: 'Decision', icon: CheckCircle2, group: 'Decide' },
      // Named for the moment it is actually used: someone on a call with an
      // AHJ or a utility who needs the scoping answers in seconds. "Stakeholder
      // Brief" did not tell a customer that is what lives here (live 2026-09-13,
      // Beauregard Parish call).
      { id: 'stakeholder' as const, label: 'Stakeholder Brief \u00b7 Call answers', icon: Building2, group: 'Decide' },
    ] : []),
    { id: 'audit', label: 'Audit', icon: ShieldCheck, group: 'Decide' },
    { id: 'deepdive', label: 'Scoring Engine', icon: Microscope, group: 'Decide' },
    { id: 'roadmap', label: 'Roadmap', icon: GitBranch, group: 'Decide' },
  ];
  const tabs = baseTabs.filter((tab) => visibleTabs.includes(tab.id));

  // Journey mini-map — per-tab status dots, every one a cheap PURE predicate
  // (cockpit contract: status is derived, never asserted). 'neutral' → no dot.
  const tabStatuses = useMemo(() => {
    // KNOWN GAP: this only sees FINISHED generation work (a promoted snapshot
    // or a published layout head). A scenario that is built and saved but not
    // promoted shows no dot, which contradicts tabStatus's own contract ("a
    // STARTED-but-unfinished generation chain shows 'open'"). Saved scenarios
    // live in a subcollection loaded inside the workbench, so there is no cheap
    // pure predicate for them here — closing this needs a lightweight
    // `generationImpactScenarioHead` on the site entity, written on save, in
    // the same shape as `generationImpactLayoutHead`.
    const hasGenerationWorkflow = Boolean(
      workflowSite.promotedGenerationScenarioSnapshot
      || workflowSite.projectIntent?.promotedGenerationScenarioId
      || workflowSite.generationImpactLayoutHead,
    );
    const inputs: TabStatusInputs = {
      intentCoverageStatus: workflowSite.coverage?.['site-project-intent']?.status ?? null,
      hasBoundary: siteHasBoundary(workflowSite),
      documentCount: siteDocuments.length,
      sedPresent: workflowSite.sedStatus === 'complete',
      sedStale: isStaleSed(workflowSite),
      analysisCount: findingsAnalysisTypeCount,
      decisionReady: Boolean(
        currentSiteComposite
        && pathToGoSharedState.verdict?.dataAdequate
        && pathToGoSharedState.verdict.band,
      ),
      compositePresent: currentSiteComposite != null,
      // Derived from the PERSISTED audit (it survives sessions via Firestore),
      // and only 'clean' when that audit is still current — see resolveAuditClean.
      auditClean: resolveAuditClean(
        siteAudit?.lastResult
          ? {
              lastRunAt: siteAudit.lastRunAt ?? siteAudit.lastResult.lastRunAt,
              reportIds: siteAudit.lastResult.reportIds,
              severityCounts: siteAudit.lastResult.severityCounts,
            }
          : null,
        siteReports,
      ),
      hasSimulation: allSims.length > 0,
      financialAssumptionsSet: site.financialAssumptions != null,
      supplyChainReady: isSupplyChainPlanUserReady(site.supplyChainPlan),
      generationReady: hasGenerationWorkflow
        ? evaluatePersistedTestFitWorkflow(workflowSite, docsTabSed ?? null).ready
        : null,
      roadmap: roadmapProgress(roadmapGraphForQuests?.nodes ?? []),
    };
    const out: Partial<Record<Tab, 'cleared' | 'open'>> = {};
    for (const tab of visibleTabs) {
      const s = tabStatus(tab, inputs);
      if (s !== 'neutral') out[tab] = s;
    }
    return out;
  }, [workflowSite, siteDocuments.length, findingsAnalysisTypeCount, currentSiteComposite, pathToGoSharedState.verdict?.dataAdequate, pathToGoSharedState.verdict?.band, allSims.length, docsTabSed, roadmapGraphForQuests]);

  // HOME JOURNEY BAND — the same derived state + shared resolver the hub
  // renders (zero new state). Computed only while Home is the active tab.
  const homeJourney = useMemo(() => {
    return {
      loading: docsTabSedReadBlocked || !reportsLoaded || decisionAnalysesPending,
      state: pathToGoSharedState,
      nextAction: resolveNextAction(
        pathToGoSharedState,
        currentSiteComposite,
        decisionBandConfig,
      ),
    };
  }, [docsTabSedReadBlocked, reportsLoaded, decisionAnalysesPending, pathToGoSharedState, currentSiteComposite, decisionBandConfig]);

  const SITE_TAB_SHORT: Partial<Record<Tab, string>> = {
    map: 'Map',
    evidence: 'Evidence',
    supplychain: 'Equipment',
    documents: 'Docs',
    simulations: 'Engineering',
    generation: 'Generation',
    financial: 'Financial',
    reports: decisionFindingsEnabled ? 'Analyses' : 'Reports',
    decision: 'Decision',
    stakeholder: 'Answers',
  };

  // Dynamic browser title: "<SiteName> · <ActiveTabLabel> — Datacrate"
  // while a site is open. Restores the descriptive default on unmount so
  // leaving the site (back to dashboard) doesn't leave a stale site title.
  const activeTabLabel = tabs.find(t => t.id === activeTab)?.label ?? 'Home';
  useEffect(() => {
    const prev = document.title;
    document.title = `${site.name} · ${activeTabLabel} — Datacrate`;
    return () => {
      document.title = prev;
    };
  }, [site.name, activeTabLabel]);

  /** The strip label of an offered tab (null for one this build hides). */
  const siteTabLabel = (tab: Tab | null): string | null =>
    (tab ? tabs.find(candidate => candidate.id === tab)?.label : undefined) ?? null;
  /**
   * The destination a prerequisite phase's route names: the tab its FIRST open
   * item actually opens (decisionPrerequisiteTab), so "Open Site evidence"
   * never opens Intent or the Site Map. Null when that item lives in a
   * workspace this build hides — the route then explains that instead of
   * navigating, and its link falls back to the item's own resolver label.
   */
  const prerequisiteDestinationLabel = (phaseId: DecidePrerequisitePhaseId): string | null =>
    siteTabLabel(decisionPrerequisiteTab(phaseId));
  const prerequisiteRouteLabel = (phaseId: 'define' | 'verify'): string => {
    const label = prerequisiteDestinationLabel(phaseId);
    if (label) return `Open ${label}`;
    return decisionPrerequisiteStep(phaseId)?.resolver?.label ?? 'Open';
  };

  /** Decide → where the work of each prerequisite phase lives (navigation only). */
  const decidePrerequisiteRoutes: Partial<
    Record<DecidePrerequisitePhaseId, DecidePrerequisiteRoute>
  > = {
    define: {
      label: prerequisiteRouteLabel('define'),
      onOpen: () => noteDecisionRoute(handleDecisionOpenPrerequisite('define')),
    },
    // In the rolled-back layout Decide renders ON the Analyses / Reports tab,
    // under the analyses it would route to — no self-link there.
    ...(activeTab !== 'reports'
      ? {
          build: {
            label: `Open ${decisionFindingsEnabled ? 'Analyses' : 'Reports'}`,
            onOpen: () => handleDecisionOpenPrerequisite('build'),
          },
        }
      : {}),
    verify: {
      label: prerequisiteRouteLabel('verify'),
      onOpen: () => noteDecisionRoute(handleDecisionOpenPrerequisite('verify')),
    },
  };
  /**
   * Stakeholder Brief "Continue in …" — the tab each phase route opens
   * (openDecisionWork), named from the same resolver. Omitted for a phase
   * whose first item lives in a hidden workspace: the Brief then says only
   * "Continue".
   */
  const phaseDestinationLabelsNow: Partial<Record<'build' | 'verify' | 'decide', string>> = {};
  for (const [phase, label] of [
    ['build', siteTabLabel('reports')],
    ['verify', prerequisiteDestinationLabel('verify')],
    ['decide', siteTabLabel(decisionHomeTab)],
  ] as const) {
    if (label) phaseDestinationLabelsNow[phase] = label;
  }
  // Value-stable: a fresh record per render is a changed prop for the
  // memoised dossier, although the labels only move with the home tab.
  const phaseDestinationLabels = useShallowStableRecord(phaseDestinationLabelsNow);

  /**
   * P2.3 — the 0.65 measured-weight floor (2026-09-05) retracted published
   * verdicts for every site measuring between 60% and 65% of decision weight.
   * A retraction is announced, not shipped quietly: one dismissible tip per
   * user, wherever the Decide surface (and so the verdict) is read — the
   * promoted Decision tab, or Analyses / Reports when it is rolled back. Same
   * tip id everywhere (it moved here from the retired Path to GO hub), so a
   * user who dismissed it once does not see it again.
   */
  const renderDecisionFloorTip = (reviewStage: 1 | 2 = decisionReviewStage) => reviewStage === 1 ? null : (
    <ContextualGuideTip
      tipId="decision-weight-floor-2026-09"
      stepId="go-no-go"
      title="Decision floor raised on 5 September 2026"
      body="A coloured verdict now requires at least 65% of decision weight to be measured (previously 60%). Sites between the two floors read “Decision pending” until more evidence is measured — nothing about their evidence changed, and the verdict returns as soon as the floor is met."
      uid={guideUid}
      progress={guideProgress}
      onProgressChange={setGuideProgress}
    />
  );

  /**
   * DECIDE — record / review the accountable decision (moved out of Path to GO
   * in Path to GO removal stage 1; the Path to GO Decide phase itself was
   * deleted in stage 3). Same state, same handlers, same role gate and same
   * lock that phase used: `pathToGoSharedState` is the
   * exact state `handleRecordPathToGoDecision` / `handleReviewPathToGoDecision`
   * gate on, and both handlers keep their own lock check; the server re-checks
   * lock, role and gate. The page's ranked document match feeds that state's
   * step copy and ranking only — phases and the positive-decision gate never
   * read it, which is why the server can re-derive the gate without running
   * the matcher (pinned in pathToGoOrchestrator.coverageMatch.test.ts).
   */
  const renderDecideSurface = (reviewStage?: 1 | 2) => (
    <DecideSurface
      key={JSON.stringify([workflowSite.id, workflowSite._ownerUid, reviewStage ?? 2])}
      decisionStage={reviewStage}
      validationReportIds={validationScoreReportIds}
      screeningEvidence={screeningDecisionEvidence}
      onRecordScreeningDecision={handleRecordScreeningDecision}
      state={pathToGoSharedState}
      composite={currentSiteComposite}
      bandConfig={decisionBandConfig}
      // Same hold as the Path to GO cockpit and Findings: until decision
      // analyses resolve, currentness is unknown, so the section must not
      // claim analyses are 'not yet current' or mount the record form.
      loading={!reportsLoaded || docsTabSedReadBlocked || decisionAnalysesPending}
      decisionRecords={scopedDecisionRecords}
      historyStatus={scopedDecisionHistoryStatus}
      onRetryHistory={() => setDecisionHistoryReloadNonce(nonce => nonce + 1)}
      onRecordDecision={handleRecordPathToGoDecision}
      onReviewDecision={
        canReviewDecisions
          ? handleReviewPathToGoDecision
          : undefined
      }
      decisionOwnerDefault={decisionOwnerDefault}
      decisionReadOnly={lockReadOnly}
      onRequestEditAccess={requestSiteEditAccess}
      onResolveStep={(step) => noteDecisionRoute(handleDecisionResolveStep(step))}
      prerequisiteRoutes={decidePrerequisiteRoutes}
      onOpenAudit={() => {
        setFocus({ kind: 'conflicts', id: '' });
        setActiveTab('audit');
        setOverviewDataConfidenceOpen(true);
      }}
      onOpenAnalyses={() => setActiveTab('reports')}
      sed={docsTabSed ?? null}
      onResolveInPlace={handleResolveInPlace}
      toast={(msg) => toast(msg)}
    />
  );

  /**
   * The Decision dossier's "Review and record decision" lands on Decide. Focus
   * goes to the rationale (the first thing to write), never to a radio — the
   * choice radios are visually hidden and come first in document order, so a
   * generic input selector focused an unchecked NO-GO / GO that Space would
   * then select. With no editable rationale (read-only, or the form held while
   * the history loads) the section heading takes focus instead.
   */
  const scrollToDecideSurface = () => {
    const target = document.getElementById(DECIDE_SURFACE_ID);
    if (target && typeof target.scrollIntoView === 'function') {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    const focusTarget =
      target?.querySelector<HTMLElement>('[data-testid="screening-decision-rationale"]:not([disabled]), [data-testid="decision-rationale"]:not([disabled])')
      ?? document.getElementById(DECIDE_SURFACE_TITLE_ID);
    focusTarget?.focus({ preventScroll: true });
  };
  // openDecisionWork('decide') switches to the decision tab and asks for the
  // Decide surface; scroll once that tab has rendered it.
  const handledDecideScrollRef = React.useRef(0);
  useEffect(() => {
    if (decideScrollRequest === handledDecideScrollRef.current) return;
    if (activeTab !== decisionHomeTab) return;
    handledDecideScrollRef.current = decideScrollRequest;
    scrollToDecideSurface();
  });

  // Stable handler identities for the memoised <FindingsDossier> (React.memo).
  // An inline closure per render is a changed prop, which re-renders the
  // dossier on every page render — the opposite of what the memo is for.
  // Each wrapper keeps ONE identity and always runs the latest closure.
  const handleRunEconomicScreen = useStableCallback(async (inputs: DossierHandlerArg<'onRunEconomicScreen'>) => {
    const resolvedFips = verifiedWorkforceProfileFips(workforceProfile);
    const snapshot = await screenEconomicContributionForSite(
      workflowSite,
      inputs,
      {
        ...(resolvedFips ? { resolvedFips } : {}),
        sed: docsTabSed,
      },
    );
    // P3.7 — the screen endpoint commits the snapshot inside a transaction
    // with location and input-hash revalidation; a second, plain client
    // write raced bootstrap hydration and could land a snapshot the SED
    // then treated as current. Only the user's inputs are written here;
    // the site listener carries the server's snapshot back.
    void snapshot;
    await updateSite(site.id, { economicContributionInputs: inputs });
  });
  const handleRunNextAction = useStableCallback((action: DossierHandlerArg<'onRunNextAction'>) => {
    runDecisionPlanAction(action.pathAction, action.targetPhase);
  });
  const handleDecisionReviewStageChange = useStableCallback((stage: DossierHandlerArg<'onDecisionReviewStageChange'>) => {
    setDecisionReviewOverride({ scope: decisionReviewScope, stage });
  });
  const handleOpenBriefRoute = useStableCallback((route: DossierHandlerArg<'onOpenBriefRoute'>) => {
    if (route.kind === 'phase') { openDecisionWork(route.phase); return; }
    if (route.kind === 'generation-workbench') { openGenerationWorkflow('equipment'); return; }
    const strategyRoute = route.kind === 'intent-field' && route.field === 'btmStrategy';
    setIntentInputView(route.kind === 'stage2-decision' || strategyRoute ? 'detailed' : 'screening');
    setIntentFieldFocus(route.kind === 'stage2-decision' ? stage2FocusField(route.id) : strategyRoute ? stage2FocusField('power-supply') : route.field);
    setActiveTab('intent');
  });
  const handleFocusAnalysisHandled = useStableCallback(() => setPendingFindingsAnalysisId(null));
  const handleReviewScreeningDecision = useStableCallback(() => {
    setDecisionReviewOverride({ scope: decisionReviewScope, stage: 1 });
    setActiveTab('decision');
    setDecideScrollRequest(request => request + 1);
  });
  const handleOpenAnalyses = useStableCallback((analysisId: DossierHandlerArg<'onOpenAnalyses'>) => {
    setPendingFindingsAnalysisId(analysisId);
    setActiveTab('reports');
  });
  const openPhaseFromSurface = useStableCallback((
    surface: DecisionWorkspaceSurface,
    phase: DossierHandlerArg<'onOpenPhase'>,
  ) => {
    // The decision is recorded on this tab's Decide section; the other
    // phases open the tab that owns their work (Path to GO removal).
    if (surface === 'decision' && phase === 'decide') {
      scrollToDecideSurface();
      return;
    }
    const opened = openDecisionWork(phase);
    if (surface === 'decision') noteDecisionRoute(opened);
  });
  // One identity per surface: which destination rendered the dossier is a
  // render-site constant, not state, so each wrapper can be fixed up front.
  const handleOpenPhaseBySurface: Record<DecisionWorkspaceSurface, (phase: DossierHandlerArg<'onOpenPhase'>) => void> = {
    analyses: useStableCallback((phase: DossierHandlerArg<'onOpenPhase'>) => openPhaseFromSurface('analyses', phase)),
    decision: useStableCallback((phase: DossierHandlerArg<'onOpenPhase'>) => openPhaseFromSurface('decision', phase)),
    stakeholder: useStableCallback((phase: DossierHandlerArg<'onOpenPhase'>) => openPhaseFromSurface('stakeholder', phase)),
  };
  const handleLaunchAnalysis = useStableCallback((typeId: DossierHandlerArg<'onLaunchAnalysis'>) => guardLockedAction(() => {
    setPickerInitialType(typeId);
    setWizardForceNew(true);
    setShowReportTypePicker(true);
  })());
  const handlePlanAction = useStableCallback((
    action: DossierHandlerArgs<'onPlanAction'>[0],
    analysisType: DossierHandlerArgs<'onPlanAction'>[1],
  ) => runDecisionPlanAction(action, undefined, analysisType));
  const handleCancelRun = useStableCallback((runId: string) => { void cancelReportGenerationJob(runId); });

  /**
   * The former Findings sub-tabs are now first-class site destinations. Keep
   * one shared wiring point so Analyses, Decision, and Stakeholder Brief read
   * the exact same report/SED/runtime state and cannot drift in their actions.
   */
  const renderDecisionWorkspaceSurface = (
    surface: DecisionWorkspaceSurface,
  ) => docsTabSedError ? null : (
    <div
      className="p-4 md:p-5"
      data-testid={surface === 'analyses' ? 'findings-tab-layout' : `${surface}-tab-layout`}
    >
      {surface === 'decision' ? (
        <div className="mb-3">{renderDecisionFloorTip()}</div>
      ) : null}
      <FindingsDossier
        surface={surface}
        site={findingsSite}
        analyses={findingsAnalyses}
        analysesState={docsTabSedLoading ? 'loading' : findingsAnalysesState}
        callReadyEnabled={callReadyFindingsEnabled}
        economicContributionEnabled={economicContributionEnabled}
        economicResearch={economicResearch}
        economicWorkforceProfile={workforceProfile}
        onRunEconomicScreen={handleRunEconomicScreen}
        derivedVerdict={derivedVerdict}
        pathToGo={pathToGoSharedState}
        journey={homeJourney}
        composite={currentSiteComposite}
        onRunNextAction={handleRunNextAction}
        briefDecisionStage={pathToGoSharedState.decisionStage}
        decisionReviewStage={surface === 'decision' ? decisionReviewStage : undefined}
        onDecisionReviewStageChange={surface === 'decision' ? handleDecisionReviewStageChange : undefined}
        onOpenBriefRoute={handleOpenBriefRoute}
        sed={docsTabSed}
        generationImpactEnabled={generationImpactEnabled}
        focusAnalysisId={surface === 'analyses' ? pendingFindingsAnalysisId : null}
        onFocusAnalysisHandled={handleFocusAnalysisHandled}
        decisionRecords={scopedDecisionRecords}
        briefScreening={surface === 'stakeholder' ? briefScreeningReview : undefined}
        onReviewScreeningDecision={surface === 'stakeholder' ? handleReviewScreeningDecision : undefined}
        onOpenAnalysis={handleOpenFindingsAnalysis}
        onOpenAnalyses={handleOpenAnalyses}
        onOpenPhase={handleOpenPhaseBySurface[surface]}
        phaseDestinationLabels={phaseDestinationLabels}
        onLaunchAnalysis={handleLaunchAnalysis}
        isDatacrateAdmin={isDatacrateAdmin}
        // The Decision-surface router: the typed plan handler, plus the two
        // kinds it does not route (engineering-source, none) — which used to
        // make "Resolve inputs · <analysis>" a dead click here.
        onPlanAction={handlePlanAction}
        onResolveInputs={openAnalysisInputs}
        refreshErrorByType={analysisRefreshErrorByType}
        autoRebindByType={analysisAutoRebindByType}
        refreshProgressByType={analysisRefreshProgressByType}
        onCancelRun={handleCancelRun}
        inflightByType={inflightRunByType}
      />
      {surface === 'decision' ? (
        <div className="mt-5">{renderDecideSurface(decisionReviewStage)}</div>
      ) : null}
    </div>
  );

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col">
      {/* Visible whenever generateTypedReport is waiting on the
          bootstrap pipeline to settle. The banner self-tears down when
          the store flips out of the wait stage — no host wiring needed. */}
      <BootstrapWaitBanner />
      {fromPortfolio && (
        <div className="px-6 pt-4">
          <Link
            to={`/customers/${fromPortfolio}/portfolio`}
            className="text-[10px] uppercase tracking-widest text-text-secondary hover:text-text-primary"
            data-testid="back-to-portfolio-link"
          >
            ← Back to Portfolio
          </Link>
        </div>
      )}
      {/* ═══ Site Header ═══ */}
      {/* The header's bottom rule was removed 2026-06-10 per William — only the
          tab strip's own line shows now (no double line above the content). */}
      <div className="bg-background p-6 space-y-3">
        {/* SITE-HOME — the site title, location, AC3 provenance chip, #81(b)
            contributor tags and #82 lock controls all moved to the Aurora
            Atrium Home hero (the lock control now sits in the Home hero's
            top-right). Only the inline rename EDITOR is kept here, shown when
            the Home hero's rename pencil sets `isEditingName`. */}
        {isEditingName && (
          <div className="flex items-center gap-2" data-testid="site-name-edit-row">
            <input
              type="text" value={editName} onChange={e => setEditName(e.target.value)}
              className="text-lg font-bold text-foreground border-b-2 border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 bg-transparent"
              autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveName()}
            />
            <button onClick={handleSaveName} className="text-success hover:text-success"><Check className="w-4 h-4" /></button>
            <button onClick={() => setIsEditingName(false)} className="text-text-secondary hover:text-text-primary"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* #82 — locked read-only banner: full-width, shown only when the lock
            is actively blocking THIS viewer. The holder / admins never see it
            (they retain write access). */}
        {lockReadOnly && (
          <div
            className="flex items-center gap-2 px-3 py-2 rounded-md border border-warning/40 bg-warning/10 text-warning text-[11px] uppercase tracking-widest"
            data-testid="site-lock-readonly-banner"
            role="status"
          >
            <Lock className="w-3.5 h-3.5 shrink-0" />
            <span>
              This site is locked for editing by {lockHolderLabel}. You have read-only access — use “Request edit access” to ask for the lock.
            </span>
          </div>
        )}

        {/* SITE-MAP-TAB-B — single-row active-label strip: only the active tab
            shows its label, inactive tabs collapse to icons (full name on
            hover via tooltip), count badges persist on collapsed icons. When
            the user is OFF the Home tab, the site name is pinned to the right
            so site identity stays visible without the old title header. */}
        <div className="flex items-center gap-4 min-w-0">
          <ResponsiveTabStrip
            className="-mb-3 flex-1 min-w-0"
            testId="site-tab-strip"
            ariaLabel="Site sections"
            collapseInactiveLabels
            tabs={tabs.map((tab, i) => ({
              id: tab.id,
              label: tab.label,
              shortLabel: SITE_TAB_SHORT[tab.id] ?? tab.label,
              icon: tab.icon,
              count: tab.count,
              testId: `site-tab-${tab.id}`,
              // Subtle Define/Analyze/Decide grouping — separator + label
              // render before the first tab of each group (mirrors Home cards).
              groupStart: Boolean(tab.group) && tab.group !== tabs[i - 1]?.group,
              groupLabel: tab.group,
              // Journey mini-map dot (derived; absent = neutral).
              status: tabStatuses[tab.id],
            }))}
            activeId={activeTab}
            onSelect={id => setActiveTab(id as Tab)}
            onTabRef={(id, el) => {
              if (el) tabButtonRefs.current.set(id as Tab, el);
              else tabButtonRefs.current.delete(id as Tab);
            }}
            getAriaCurrent={(_id, active) => (active ? 'page' : undefined)}
          />
          {activeTab !== 'home' && (
            <span
              className="hidden shrink-0 max-w-[16rem] truncate text-sm font-bold tracking-tight text-text-primary sm:inline"
              title={site.name}
              data-testid="site-header-name"
            >
              {site.name}
            </span>
          )}
        </div>

        {/* Post-bootstrap banner removed 2026-05-09 — its per-doc framing
             ("New document X · +78 more") didn't survive bulk uploads,
             and its Refresh button only forwarded the headline doc's id
             while silently dropping the rest. The two newer surfaces
             cover the same intent without the bulk-upload pathology:
             RefreshEvidencePanel (section-scoped, knows about every
             doc) for ad-hoc refresh, and PreflightFreshnessCard at
             report-gen time. */}

        {/* Documents_On_Site Prompt 13-C — auto-rerun suggestion. */}
        {/* AutoRerunBanner removed: do not show shared New evidence banner across tabs */}

        {/* Stale-evidence prompt after a location change. The collected
             SED (climate / grid / seismic / …) is point-keyed, so a new
             geocode invalidates it. We don't auto-recollect — surface the
             choice. Re-collect reuses the existing refreshSedSections
             action; dismiss leaves the SED untouched. */}
        {staleEvidenceAddress && (
          <div
            className="flex items-start gap-3 p-3 rounded-md border border-warning/40 bg-warning/10"
            data-testid="stale-evidence-banner"
          >
            <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-[10px] text-text-primary uppercase tracking-widest leading-relaxed">
                Site location changed to <span className="font-bold">{staleEvidenceAddress}</span>. Collected evidence (climate, grid, seismic, flood, …) is for the previous location. Re-collect evidence?
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="h-7 type-body uppercase tracking-widest font-bold"
                  onClick={guardLockedAction(() => { void handleReCollectEvidence(); })}
                  disabled={reCollecting || lockReadOnly}
                  title={lockReadOnly ? `Locked by ${lockHolderLabel} — read-only` : undefined}
                  data-testid="stale-evidence-recollect"
                >
                  {reCollecting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                  Re-collect evidence
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 type-body uppercase tracking-widest"
                  onClick={() => setStaleEvidenceAddress(null)}
                  disabled={reCollecting}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SL2b — the Reports sub-tab strip is gone; Documents and Site
          evidence are top-level tabs now (see the `tabs` array above). */}

      {/* ═══ Tab Content ═══ */}
      <div
        // `pb-overlay-chrome` (index.css) — fully-scrolled tab content must
        // clear the bottom-right floating dual-pill (founder QA r3 item 2;
        // proving case: the Intent wizard's Back/Next footer). The map tab
        // is full-bleed (overflow-hidden) and manages its own chrome.
        className={`flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-x-hidden ${
          activeTab === 'map' ? 'overflow-hidden' : 'overflow-y-auto pb-overlay-chrome'
        }`}
        data-testid="site-tab-content"
        data-active-tab={activeTab}
      >
        {docsTabSedError && activeTab !== 'evidence' && (
          <div role="alert" data-testid="site-page-evidence-read-error" className="m-4 rounded-lg border border-danger/40 bg-danger/10 p-4 type-body text-danger">
            <p>{docsTabSedError}</p>
            <p className="mt-1 text-text-secondary">Current evidence conclusions are unavailable until the saved evidence is read.</p>
            <Button className="mt-3" variant="outline" size="sm" onClick={() => setDocsTabSedReloadKey(k => k + 1)}>Reload evidence</Button>
          </div>
        )}
        {activeTab === 'home' && !docsTabSedError && (
          <SiteHomeTab
            site={findingsSite}
            composite={currentSiteComposite}
            siteReports={siteReports}
            recommendationReports={findingsAnalyses}
            recommendationDecisionStage={pathToGoSharedState.decisionStage}
            siteDocuments={siteDocuments}
            sed={docsTabSed ?? undefined}
            derivedVerdict={derivedVerdict}
            onNavigate={setActiveTab}
            onOpenPhase={openDecisionWork}
            onRunNextAction={(action) => runDecisionPlanAction(action.pathAction, action.targetPhase)}
            showEngineeringAnalysis={simulationComputeEnabled}
            showGenerationImpact={generationImpactEnabled}
            showAnalysesWorkspace={decisionFindingsEnabled}
            showDecisionSurfaces={splitDecisionSurfacesEnabled}
            analysisCount={findingsAnalysisTypeCount}
            onEditName={() => { setEditName(site.name); setIsEditingName(true); }}
            renameDisabled={lockReadOnly}
            renameTitle={lockReadOnly ? `Locked by ${lockHolderLabel} — read-only` : 'Rename site'}
            lockControls={<SiteLockControls site={site} />}
            onViewReport={handleViewReport}
            journey={homeJourney}
            tabStatuses={tabStatuses}
          />
        )}

        {activeTab === 'intent' && (
          <div className="p-4 md:p-5 space-y-4" data-testid="intent-tab-layout-root">
            <div data-testid="intent-tab-editor" id="overview-intent-editor" className="space-y-4">
              <SiteConfigurationPanel
                site={site}
                testIdPrefix="intent-tab-config"
                visibleSections={['intent', 'context']}
                hideShell
                flattenSections
                defaultExpandedSection="intent"
                intentStepper
                // The stepper's "Done" returns to Home, whose journey band
                // names the next action (the Path to GO hub it used to land
                // on is retired).
                onIntentStepperDone={() => setActiveTab('home')}
                // PERSIST-ON-CHANGE — thread the host lock so the panel's
                // debounced auto-save respects the same read-only gate the
                // rest of the page enforces (held by a teammate / no grant).
                lockReadOnly={lockReadOnly}
                onJumpToEvidenceTab={() => setActiveTab('evidence')}
                intentInputStage={intentInputView}
                onIntentInputStageChange={setIntentInputView}
                screeningNotice={stage2Decisions?.available ? (
                  <Stage2ReadyBanner
                    openCount={stage2Decisions.openCount}
                    onReview={() => setIntentInputView('detailed')}
                  />
                ) : undefined}
                renderScreeningHeader={({ intent, applyIntent }) => (
                  <IntentScreeningRecommendations
                    site={findingsSite} reports={findingsAnalyses} sed={docsTabSed}
                    analysesState={findingsAnalysesState} evidenceLoading={docsTabSedLoading}
                    evidenceUnavailable={Boolean(docsTabSedError)} decisionStage={pathToGoSharedState.decisionStage}
                    onReview={() => setActiveTab(splitDecisionSurfacesEnabled ? 'stakeholder' : 'reports')}
                    draftIntent={intent} onApplyIntent={applyIntent}
                    btmStrategy={site.typedPreferencesDefaults?.['power-availability']?.btmStrategy}
                    onSetStrategy={handleSetStage2Strategy} readOnly={lockReadOnly}
                    onChooseDemand={() => { setIntentInputView('detailed'); setIntentFieldFocus('powerRequirementMW'); }}
                  />
                )}
                renderDesignHeader={stage2Decisions ? ({ intent, applyIntent, activeStep, onSelectStep }) => (
                  <Stage2DecisionsPanel
                    set={stage2Decisions}
                    intent={intent}
                    applyIntent={applyIntent}
                    btmStrategy={site.typedPreferencesDefaults?.['power-availability']?.btmStrategy}
                    review={site.stage2DecisionReview ?? null}
                    onSetStrategy={handleSetStage2Strategy}
                    onSaveReview={handleSaveStage2Review}
                    onFocusField={setIntentFieldFocus}
                    onOpenGeneration={() => handlePathToGoAction({ kind: 'generation-impact', label: 'Open Generation & Impact', focus: 'scenario' })}
                    activeStep={activeStep}
                    onSelectStep={onSelectStep}
                    readOnly={lockReadOnly}
                  />
                ) : undefined}
              />
            </div>
          </div>
        )}

        {activeTab === 'map' && (
          <SiteMapTab
            site={site}
            siteDocuments={siteDocuments}
            siteReports={siteReports}
            verdict={derivedVerdict}
            onEditLocation={guardLockedAction(() => setLocationBoundaryEditorMode('location'))}
            onEditBoundary={guardLockedAction(() => setLocationBoundaryEditorMode('boundary'))}
            onUpdateKmz={guardLockedAction(() => setLocationBoundaryEditorMode('boundary-import'))}
            onEditLayers={guardLockedAction(openLayerEditorIfReady)}
            onUploadAreaData={guardLockedAction(() => setRegionalLayersDialogOpen(true))}
            onAddLayers={guardLockedAction(() => {
              setAddLayersInitialFiles(null);
              setAddLayersDialogOpen(true);
            })}
            onDropFiles={guardLockedAction((files: File[]) => {
              setAddLayersInitialFiles(files);
              setAddLayersDialogOpen(true);
            })}
            onExportKmz={() => { void handleExportKMZ(); }}
            onOpenGoogleEarth={handleOpenGoogleEarth}
            onOpenGenerationTab={() => setActiveTab('generation')}
          />
        )}

        {activeTab === 'documents' && (
          <div className="p-4 md:p-5 space-y-3">
            <ContextualGuideTip
              tipId="documents-tab"
              stepId="upload-documents"
              title="Docs vs regional baseline"
              body="Upload site-specific studies to upgrade requirement rows from regional baseline to verified-from-doc. Tag documents to checklist requirements before generating."
              uid={guideUid}
              progress={guideProgress}
              onProgressChange={setGuideProgress}
            />
            {/* SET-B3 — cross-tab focus breadcrumb. Only renders when a
                 ?focus=… URL param is active so the steady-state UI is
                 unchanged for everyone who hasn't crossed a bridge. */}
            <CrossTabFocusBreadcrumb
              focus={crossTabFocus}
              currentTab="documents"
              focusLabel={focusLabel}
              onBack={flipFocusTab}
              onClear={clearFocus}
            />
            {/* Documents-tab declutter (founder request, 2026-06-11): the
                 Aurora section header (DOCUMENTS eyebrow, "Document Library"
                 title, doc-count chip, Export-audit / Scan / Upload proxy
                 buttons) and the Document-checklist pill were removed. Every
                 capability stays reachable in the tab body or elsewhere:
                   • Upload  → library dropzone + empty-state landing card
                   • Scan    → automatic — the library map refreshes itself
                               (debounced) on every doc mutation (QA r3 item 4
                               removed the Library-Map card + manual button)
                   • Export  → admin-only button in the library's control row
                                (un-hidden in DocumentLibrary now that the
                                header proxy is gone)
                   • Checklist → Evidence tab CoverageMatrix (one row per
                                 checklist requirement); the doc count stays
                                 on the Documents tab chip in the tab bar. */}
            <Suspense fallback={<RouteLoadingSkeleton />}>
              <DocumentLibrary
                  documents={siteDocuments}
                  siteId={site.id}
                  open={true}
                  embedded={true}
                  onClose={() => {}}
                  onJumpToEvidence={jumpToEvidenceForDoc}
                  onJumpToVariable={jumpToVariable}
                  focusDocId={focus?.kind === 'doc' ? focus.id : null}
                  /* EDX-8 — `req:<id>` + `upload-for:<id>` receivers. Both
                       pre-apply the SED-contribution filter pill; the
                       upload-for variant additionally scrolls + ring-
                       highlights the dropzone so the eye lands on the
                       upload affordance. */
                  focusReqId={focus?.kind === 'req' ? focus.id : null}
                  focusUploadForId={focus?.kind === 'upload-for' ? focus.id : null}
                  /* CMU-2 — `replace:<docId>::<requirementId>` receiver.
                       The DocumentLibrary parses the composite id, opens
                       a replace-mode dropzone keyed on `<docId>`, and
                       soft-archives the old doc on a successful upload.
                       The `<requirementId>` half is forwarded to the
                       URE-2 intended-requirement plumbing so the new
                       version inherits the same Coverage-Matrix row
                       intent as the old. */
                  focusReplaceDocId={focus?.kind === 'replace' ? focus.id : null}
                  onFocusHandled={clearFocus}
                  onUpdateDocuments={async (updated) => {
                    // Documents are keyed by site, not by report. Persist the full
                    // updated array to `site.documents` (and Firestore) so uploads
                    // work pre-report and survive across all reports of this site.
                    await updateSiteDocuments(site.id, updated);
                  }}
                  // Documents_On_Site Prompt 8-C — feed the empty state's
                  // checklist with the report types this site already has, or
                  // feasibility as the sensible default for a brand-new site.
                  recommendedReportTypes={(() => {
                    const existing = [...new Set(siteReports.map(r => (r.reportType ?? 'feasibility') as ReportTypeId))];
                    return existing.length > 0 ? existing : (['feasibility'] as ReportTypeId[]);
                  })()}
                  onSkipUploads={() => setActiveTab('reports')}
                />
            </Suspense>
          </div>
        )}

        {activeTab === 'reports' && (
          decisionFindingsEnabled ? (
            renderDecisionWorkspaceSurface('analyses')
          ) : (
          <div className="p-4 md:p-5 space-y-4">
            {/* aurora-qa-3 (⑧) — the "Report Variables" button moved OUT of this
                 standalone top action row and now sits NEXT TO the Add-report
                 action in the reports list header (and in the empty-state header
                 below) so the two report-header actions group together. */}
            {/* "Documents that strengthen any report" panel moved to the
                 Site Evidence tab — it's a planning + freshness aid more
                 than a report-list affordance, and pairing it with the
                 SED variable browser groups the two evidence views. */}

            {/* Unified Grid Layout */}
            <div className="grid grid-cols-1 xl:grid-cols-12 gap-4" data-testid="reports-tab-layout-grid">
              {/* Left Column: Reports List / Grid + Branding + Archetype */}
              <div className="xl:col-span-12 space-y-3 min-w-0">
                {/* 1. Inflight runs on fresh site */}
                {siteReports.length === 0 && orphanInflightRuns.length > 0 && (
                  <div className="space-y-3" data-testid="reports-first-inflight">
                    {renderOrphanInflightCards()}
                  </div>
                )}

                {/* 2. Main content */}
                {siteReports.length === 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 w-full text-left" data-testid="reports-empty-state">
                    {getOfferableReportTypes(isDatacrateAdmin)
                      .filter(type => !inflightRunByType[type.id])
                      .map(type => (
                        <ReportTypeLaunchCard
                          key={type.id}
                          type={type}
                          testId={`empty-state-card-${type.id}`}
                          onClick={guardLockedAction(() => { setPickerInitialType(type.id); setWizardForceNew(true); setShowReportTypePicker(true); })}
                          actions={
                            <ReportPreferencesDialog
                              site={site}
                              customizedCount={overviewPrefsCustomizedCount}
                              onJumpToEvidence={() => setActiveTab('evidence')}
                              triggerMode="icon"
                              targetReportType={type.id as any}
                            />
                          }
                        />
                      ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* List header */}
                    <div className="flex items-center justify-between mb-3" data-testid="reports-list-header">
                      <div className="text-xs text-text-secondary" data-testid="reports-list-type-count">
                        {formatReportTypeCountLabel(reportTypeCount)}
                        {reportVersionCount > reportTypeCount && (
                          <span className="text-text-muted"> · {reportVersionCount} version{reportVersionCount === 1 ? '' : 's'}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {reportTypeCount > 3 && (
                          <Button
                            size="sm"
                            variant="mission-primary"
                            className="h-8 shrink-0"
                            onClick={guardLockedAction(() => { setPickerInitialType(undefined); setWizardForceNew(true); setShowReportTypePicker(true); })}
                            disabled={lockReadOnly}
                            title={lockReadOnly ? `Locked by ${lockHolderLabel} — read-only` : undefined}
                            data-testid="add-report-button"
                          >
                            <Plus className="w-3.5 h-3.5 mr-1.5" />
                            Add report
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Gallery Grid */}
                    <ReportGalleryGrid
                      lineagesByType={lineagesByType}
                      renderOrphanInflightCards={renderOrphanInflightCards}
                      inflightRunByType={inflightRunByType}
                      reportCardBranding={reportCardBranding}
                      isDatacrateAdmin={isDatacrateAdmin}
                      expandedLineageId={expandedLineageId}
                      setExpandedLineageId={setExpandedLineageId}
                      reportMenuLineageId={reportMenuLineageId}
                      setReportMenuLineageId={setReportMenuLineageId}
                      highlightedRerunLineageId={highlightedRerunLineageId}
                      handleViewReport={handleViewReport}
                      handleExportReport={handleExportReport}
                      setReportToDelete={setReportToDelete}
                      handleDeleteReport={handleDeleteReport}
                      openInflightWizard={openInflightWizard}
                      cancelReportGenerationJob={cancelReportGenerationJob}
                      onLaunchType={(typeId) => guardLockedAction(() => { setPickerInitialType(typeId); setWizardForceNew(true); setShowReportTypePicker(true); })()}
                      lockReadOnly={lockReadOnly}
                      lockHolderLabel={lockHolderLabel}
                      renderCardActions={(typeId) => (
                        <ReportPreferencesDialog
                          site={site}
                          customizedCount={overviewPrefsCustomizedCount}
                          onJumpToEvidence={() => setActiveTab('evidence')}
                          triggerMode="icon"
                          targetReportType={typeId as any}
                        />
                      )}
                      readinessGapInput={readinessGapInput}
                      onResolveGap={handleResolveGap}
                    />
                    {reportTypeCount <= 3 && (
                      <button
                        type="button"
                        onClick={guardLockedAction(() => { setPickerInitialType(undefined); setWizardForceNew(true); setShowReportTypePicker(true); })}
                        disabled={lockReadOnly}
                        title={lockReadOnly ? `Locked by ${lockHolderLabel} — read-only` : undefined}
                        className="w-full rounded-lg border-2 border-dashed border-primary/40 hover:border-primary hover:bg-primary/5 transition-colors px-4 py-4 text-left flex items-start gap-3 group disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="add-report-card"
                      >
                        <div className="w-9 h-9 shrink-0 rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-colors">
                          <Plus className="w-5 h-5 text-primary" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-bold text-foreground uppercase tracking-widest">Add another report</div>
                          <div className="type-meta leading-relaxed mt-0.5">
                            Environmental, Generation & Impact, Water Resource, Test Fit, Interconnection — run more report types on this site.
                          </div>
                        </div>
                      </button>
                    )}
                  </div>
                )}

                {/* 3. Branding controls */}
                <section
                  className="rounded-lg shadow-sm border border-border bg-card p-4 space-y-3"
                  data-testid="site-branding-controls"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
                        Report branding
                      </h3>
                      <p className="text-xs text-text-secondary mt-1">
                        Choose whether site reports default to internal branding or customer-ready dual-logo covers.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    <label className="space-y-1" data-testid="site-audience-default-control">
                      <span className="type-section text-text-secondary">
                        Audience default
                      </span>
                      <select
                        value={reportAudienceDefault}
                        onChange={guardLockedAction((e: React.ChangeEvent<HTMLSelectElement>) => { void handleAudienceDefaultChange(e.target.value as ReportAudienceMode); })}
                        disabled={lockReadOnly}
                        title={lockReadOnly ? `Locked by ${lockHolderLabel} — read-only` : undefined}
                        className="w-full rounded-md px-2 py-1.5 text-sm border border-border bg-background text-text-primary disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid="site-audience-default-select"
                      >
                        <option value="internal">{`Internal (${brandCompanyName} logo)`}</option>
                        <option value="customer">{`Customer (${brandCompanyName} + customer logo)`}</option>
                      </select>
                    </label>
                    <div className="space-y-1" data-testid="site-customer-logo-control">
                      <div className="type-section text-text-secondary">
                        Customer logo
                      </div>
                      <input
                        ref={customerLogoInputRef}
                        type="file"
                        accept="image/png,image/svg+xml,image/jpeg,image/jpg,image/webp,.svg,.png,.jpg,.jpeg,.webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          // #89 — backstop the upload behind the lock too, in case
                          // the picker is reached without the gated button.
                          if (file && !lockReadOnly) void handleCustomerLogoUpload(file);
                          e.currentTarget.value = '';
                        }}
                        data-testid="site-customer-logo-input"
                      />
                      <div className="rounded-md border border-border p-2 bg-background">
                        <div className="flex items-center justify-between gap-2">
                          <div className="text-sm text-text-secondary truncate" data-testid="site-customer-logo-url">
                            {site.customerLogoUrl ? 'Customer logo configured' : 'No customer logo uploaded'}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={guardLockedAction(() => customerLogoInputRef.current?.click())}
                              disabled={customerLogoUploading || lockReadOnly}
                              title={lockReadOnly ? `Locked by ${lockHolderLabel} — read-only` : undefined}
                              data-testid="site-customer-logo-upload"
                            >
                              {customerLogoUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                              Upload
                            </Button>
                            {site.customerLogoUrl && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={guardLockedAction(() => { void handleCustomerLogoRemove(); })}
                                disabled={customerLogoUploading || customerLogoRemoving || lockReadOnly}
                                title={lockReadOnly ? `Locked by ${lockHolderLabel} — read-only` : undefined}
                                data-testid="site-customer-logo-clear"
                              >
                                {customerLogoRemoving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
                                Remove
                              </Button>
                            )}
                          </div>
                        </div>
                        {customerLogoUploading && (
                          <div className="text-[11px] text-text-secondary mt-1" data-testid="site-customer-logo-progress">
                            {(customerLogoProgressPct ?? 0) < 40 ? 'Preparing' : 'Uploading'}… {customerLogoProgressPct ?? 0}%
                          </div>
                        )}
                        {site.customerLogoUrl && (
                          <LogoBackingChip className="mt-2">
                            <img
                              src={site.customerLogoUrl}
                              alt="Customer logo preview"
                              className="h-10 max-w-[180px] object-contain"
                              data-testid="site-customer-logo-preview"
                            />
                          </LogoBackingChip>
                        )}
                      </div>
                    </div>
                  </div>
                </section>

                <div className="pt-2">
                  <SiteArchetypeRow site={site} />
                </div>
              </div>
            </div>
          </div>
          )
        )}

        {/* Split Decision surfaces rolled back → no Decision tab. Decide then
            falls back under Analyses / Reports, so a decision is always
            recordable (Path to GO, which used to be the only other place,
            is gone). */}
        {activeTab === 'reports' && !splitDecisionSurfacesEnabled ? (
          <div className="space-y-3 px-4 pb-4 md:px-5 md:pb-5" data-testid="reports-decide-fallback">
            {renderDecisionFloorTip(2)}
            {renderDecideSurface()}
          </div>
        ) : null}

        {activeTab === 'decision' && splitDecisionSurfacesEnabled && !docsTabSedError
          ? renderDecisionWorkspaceSurface('decision')
          : null}

        {activeTab === 'stakeholder' && splitDecisionSurfacesEnabled && !docsTabSedError
          ? renderDecisionWorkspaceSurface('stakeholder')
          : null}

        <ReportPreferencesDialog
          site={workflowSite}
          triggerMode="hidden"
          targetReportType="power-availability"
          open={powerPreferencesOpen && !lockReadOnly}
          onOpenChange={setPowerPreferencesOpen}
          lockReadOnly={lockReadOnly}
          onJumpToEvidence={() => setActiveTab('evidence')}
        />

        {/* New report wizard — type select → preferences → generate.
            `initialReportType` (set by the empty-state cards) skips the type
            select step and lands the user on preferences for that type. */}
        <NewReportWizard
          open={showReportTypePicker}
          onClose={() => { setShowReportTypePicker(false); setPickerInitialType(undefined); setWizardForceNew(false); }}
          site={workflowSite}
          sed={docsTabSed}
          initialReportType={pickerInitialType}
          forceNew={wizardForceNew}
          onHandOffToReports={() => setActiveTab('reports')}
          onOpenSiteIntent={() => {
            setShowReportTypePicker(false);
            setPickerInitialType(undefined);
            setActiveTab('intent');
          }}
          onOpenCoverageMatrix={() => {
            setShowReportTypePicker(false);
            setPickerInitialType(undefined);
            setActiveTab('evidence');
          }}
          onOpenGenerationImpact={(stage) => {
            setShowReportTypePicker(false);
            setPickerInitialType(undefined);
            openGenerationWorkflow(stage);
          }}
          onOpenDecisionGuide={() => {
            setShowReportTypePicker(false);
            setPickerInitialType(undefined);
            openDecisionWork('decide');
          }}
          // Plan actions the wizard does not host itself (BOM, financial
          // inputs, audit, analysis progress / QA, cleared decision) close it
          // and open the tab that owns the work — the same typed router
          // Analyses uses (formerly: "go to Path to GO").
          onPlanAction={(action, analysisType) => {
            setShowReportTypePicker(false);
            setPickerInitialType(undefined);
            runDecisionPlanAction(action, undefined, analysisType);
          }}
          generationImpactEnabled={generationImpactEnabled}
          reportPlan={pathToGoSharedState.reportPlan}
        />

        {/* BoM→Simulation wizard — generates an editable, solvable canvas from
             this site's BoM (+ project intent) and saves it via the shared
             save recipe. Supersedes the blank-canvas + server-Python paths. */}
        {simulationComputeEnabled && bomSimulationGenerationEnabled && generateWizardOpen && (
          <GenerateSimulationWizard
            open={generateWizardOpen}
            site={site}
            simSite={buildSimSite()}
            onClose={() => setGenerateWizardOpen(false)}
            onGenerated={handleWizardGenerated}
          />
        )}

        {/* Workflow Prompt 7 — confirmation modal for the report-delete cascade. */}
        {reportToDelete && (
          <DeleteReportDialog
            report={reportToDelete}
            open={true}
            onClose={() => setReportToDelete(null)}
          />
        )}

        {/* Export-time audit warning for the gallery Export button — the same
             dialog, off the same predicate, as the report header's exports. */}
        {pendingAuditExportReport && (
          <ExportAuditWarningDialog
            report={pendingAuditExportReport}
            onCancel={() => setPendingAuditExportId(null)}
            onProceed={() => {
              const reportId = pendingAuditExportReport.id;
              setPendingAuditExportId(null);
              openPrintView(reportId);
            }}
          />
        )}

        {/* Site-level cascade-delete dialog (header menu → Delete site). */}
        {showSiteDelete && (
          <DeleteSiteDialog
            site={site}
            open={true}
            onClose={() => setShowSiteDelete(false)}
            onDeleted={() => { setShowSiteDelete(false); onSiteDeleted?.(); }}
          />
        )}

        {/* Location / boundary editor — opened from the ⋮ menu, the
             address-line pencil, or the Site Map tab action rail.
             Re-geocode the site, or redraw / re-import the boundary. A
             location change fires onLocationChanged → the stale-evidence
             prompt above. */}
        {locationBoundaryEditorMode && (
          <SiteLocationBoundaryEditor
            site={site}
            // KMU — translate the internal 'boundary-import' sentinel
            // back to the editor's two-prop external API.
            initialMode={locationBoundaryEditorMode === 'boundary-import' ? 'boundary' : locationBoundaryEditorMode}
            initialBoundaryView={locationBoundaryEditorMode === 'boundary-import' ? 'import' : 'draw'}
            open={true}
            onClose={() => setLocationBoundaryEditorMode(null)}
            onLocationChanged={(newAddress) => setStaleEvidenceAddress(newAddress)}
          />
        )}

        <RegionalLayersFetchDialog
          site={site}
          open={regionalLayersDialogOpen}
          onClose={() => setRegionalLayersDialogOpen(false)}
        />

        <AddMapLayersDialog
          site={site}
          open={addLayersDialogOpen}
          initialFiles={addLayersInitialFiles}
          onReviewStudyArea={() => setLocationBoundaryEditorMode('study')}
          onClose={() => {
            setAddLayersDialogOpen(false);
            setAddLayersInitialFiles(null);
          }}
          onOpenBoundaryImport={guardLockedAction(() => setLocationBoundaryEditorMode('boundary-import'))}
        />

        <SiteLayerEditor
          site={site}
          open={layerEditorOpen}
          onClose={() => setLayerEditorOpen(false)}
        />

        {activeTab === 'generation' && (
          <GenerationImpactWorkbench
            site={workflowSite}
            sed={docsTabSed}
            documents={site.documents ?? []}
            dams={generationDams}
            readOnly={lockReadOnly}
            fullStudio={studioRoute}
            onOpenFullStudio={() => navigate(studioFullPath(site.id))}
            onExitFullStudio={() => navigate(siteOverviewPath(site.id, 'generation'))}
            initialStage={generationWorkflowInitialStage}
            onWorkflowAuthorityChanged={() => useStore.getState().fetchSites()}
            onSedRebuildRequested={async () => {
              await useStore.getState().fetchSites();
              // Layout publication is outside the section-fingerprint dirty
              // set, so force a full rebuild to freeze the new publication.
              await useStore.getState().rebuildSiteSed(site.id, { force: true });
            }}
            onGenerateTestFit={() => {
              setPickerInitialType('test-fit');
              setWizardForceNew(true);
              setShowReportTypePicker(true);
            }}
            onOpenSiteMap={() => setActiveTab('map')}
            onOpenDocuments={(documentId) => {
              if (documentId) {
                jumpToDocumentsForDoc(documentId);
                return;
              }
              setFocus(null);
              setActiveTab('documents');
            }}
            onOpenBom={() => setActiveTab('supplychain')}
          />
        )}

        {activeTab === 'simulations' && (() => {
          // `allSims` is computed once in the component body (above) — see the
          // React #310 note there. This IIFE only renders the list.
          const loadCloudSim = async (sim: any) => {
            try {
              const cs = cloudSims.find(c => c.id === sim.cloudId);
              if (!cs) return;
              const { parseSavedCanvasArray, prepareSavedCanvasForLoad } = await import(
                '../../lib/sim/canvas/migrations/prepareSavedCanvasForLoad'
              );
              await ensureCanvasAssumptionBridge();
              let nodes = parseSavedCanvasArray<Node>(cs.nodes, 'nodes');
              let edges = parseSavedCanvasArray<Edge>(cs.edges, 'edges');
              let simState = cs.simState ? JSON.parse(cs.simState) : undefined;
              let parsedSite = cs.site ? JSON.parse(cs.site) : null;
              let eoGraphConfig: unknown = cs.eoGraphConfig;
              let eoInputSignature: unknown = cs.eoInputSignature;
              let eoCanvasExecutionConfig: unknown = cs.eoCanvasExecutionConfig;
              let solveMembership: unknown = cs.solveMembership;
              let eoStudyDefinition: unknown = cs.eoStudyDefinition;
              let eoTransientConfig: unknown = cs.eoTransientConfig;
              let legacySourceLabel: unknown = cs.sourceBoP;
              let repairMode = cs.repairMode === true;
              let eoCapability: unknown = cs.eoCapability;
              let contractVersion: unknown = cs.eoContractVersion;
              if (cs.tabs && cs.activeTabId) {
                const savedTabs = JSON.parse(cs.tabs);
                if (!Array.isArray(savedTabs)) throw new Error('Saved workspace tabs must be an array.');
                const savedTab = savedTabs.find((tab: any) => tab.id === cs.activeTabId);
                if (savedTab) {
                  nodes = parseSavedCanvasArray<Node>(savedTab.nodes, 'nodes');
                  edges = parseSavedCanvasArray<Edge>(savedTab.edges, 'edges');
                  simState ??= savedTab.simState;
                  parsedSite ??= savedTab.site ?? null;
                  eoGraphConfig ??= savedTab.eoGraphConfig;
                  eoInputSignature ??= savedTab.savedEoInputSignature ?? savedTab.eoInputSignature;
                  eoCanvasExecutionConfig ??= savedTab.eoCanvasExecutionConfig;
                  solveMembership ??= savedTab.solveMembership ?? savedTab.ignoredNodeIds;
                  eoStudyDefinition ??= savedTab.eoStudyDefinition ?? savedTab.eoStudySpec;
                  eoTransientConfig ??= savedTab.eoTransientConfig;
                  legacySourceLabel ??= savedTab.sourceBoP;
                  repairMode ||= savedTab.repairMode === true;
                  eoCapability ??= savedTab.eoCapability;
                  contractVersion ??= savedTab.eoContractVersion;
                }
              }
              const prepared = prepareSavedCanvasForLoad({
                // The user's own mutable save: settle "in use => accepted" on load.
                acceptInUse: true,
                nodes,
                edges,
                eoGraphConfig,
                eoInputSignature,
                eoCanvasExecutionConfig,
                solveMembership,
                eoStudyDefinition,
                eoTransientConfig,
                legacySourceLabel,
                persistedRepairMode: repairMode,
                persistedEoCapability: eoCapability,
                explicitContractVersion: contractVersion,
              });
              addTab(
                cs.name || 'Loaded Analysis',
                prepared.nodes,
                prepared.edges,
                simState,
                parsedSite,
                cs.id,
                cs.version || 1,
                false,
                prepared.eoGraphConfig,
                prepared.savedEoInputSignature,
                undefined,
                prepared.ignoredNodeIds,
                prepared.eoStudySpec,
                prepared.eoTransientConfig,
                undefined,
                prepared.eoCanvasExecutionConfig,
              );
              if (prepared.repairModeReason) {
                const loadedTabId = useStore.getState().activeTabId;
                useStore.getState().updateTab(loadedTabId, { sourceBoP: prepared.repairModeReason });
              }
              // After the repair marker: a protected source is never re-linked.
              linkOpenedCanvasToSite();
              setViewMode('simulation');
              // C7 — neutral route so the App URL-sync effects don't snap
              // viewMode back to siterag off the still-site-scoped URL.
              navigate('/editor');
              if (prepared.repairModeReason) {
                toast.warning(`Loaded ${cs.name || 'analysis'} in repair mode`, {
                  description: prepared.repairModeReason,
                });
              } else {
                toast.success(`Loaded: ${cs.name}`);
              }
            } catch { toast.error('Failed to load simulation'); }
          };

          // Open/focus a saved sim in the canvas: focus its open tab when one
          // exists (so we don't spawn a duplicate), else cloud-load it. C7 —
          // navigate to /editor so the App URL-sync effects keep
          // viewMode=simulation (loadCloudSim navigates internally).
          const openSim = (sim: { tabId?: string; cloudId?: string }) => {
            if (sim.tabId) { switchTab(sim.tabId); linkOpenedCanvasToSite(); setViewMode('simulation'); navigate('/editor'); }
            else void loadCloudSim(sim);
          };

          // The active editor tab is what RUN/SOLVE targets. Surface its name so
          // the user knows which saved model will solve when several are open.
          const activeSim = allSims.find(s => s.tabId && s.tabId === activeTabId);
          const activeModelName = activeSim?.name ?? null;

          const goToCanvas = () => {
            const target = activeSim ?? allSims[0];
            if (target) openSim(target);
            else void handleCreateSimulation();
          };

          return (
          <div className="p-4 md:p-5 grid grid-cols-1 xl:grid-cols-12 gap-4" data-testid="simulations-tab-layout-grid">
            <div className="xl:col-span-12">

              {activeModelName && (
                <div
                  className="mt-2 flex items-center gap-1.5 type-meta text-text-secondary"
                  data-testid="engineering-active-model-banner"
                >
                  <CheckCircle2 className="w-3 h-3 text-primary shrink-0" />
                  <span>RUN / SOLVE targets the active model:</span>
                  <span className="font-bold text-primary truncate">{activeModelName}</span>
                </div>
              )}
            </div>

            <div className="xl:col-span-8 space-y-4 min-w-0">
            {/* Saved model entry point. EO history stays scoped to each model inside the editor. */}
            <div
              className="rounded-lg border border-border bg-card shadow-card p-3 sm:p-4"
              data-testid="eo-models-on-canvas-hint"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="type-meta uppercase tracking-widest text-text-muted">
                      Simulation models
                    </span>
                    <span className="da-chip" data-testid="site-sim-count">{allSims.length}&nbsp;{allSims.length === 1 ? 'sim' : 'sims'}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 type-meta text-text-secondary">
                    <Info className="w-3 h-3 shrink-0 text-primary" />
                    <span>Review model readiness and solve validated graphs in the Simulation Editor.</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2 shrink-0">
                  {bomSimulationGenerationEnabled && (
                    <Button
                      size="sm"
                      variant="mission"
                      className="h-8"
                      onClick={openGenerateWizard}
                      disabled={lockReadOnly}
                      title={lockReadOnly ? `Locked by ${lockHolderLabel} — read-only` : undefined}
                      data-testid="site-generate-simulation"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Generate from BOM
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="mission-primary"
                    className="h-8"
                    onClick={goToCanvas}
                    data-testid="eo-open-simulation-editor"
                  >
                    <Activity className="w-3 h-3 mr-1" />
                    Open Sim Canvas
                  </Button>
                </div>
              </div>
            </div>
            {/* Historic execution drawers and report-focus links are retired. Saved canvas
                models remain available here and open in the EO editor for validation. */}
            {allSims.length === 0 ? (
              cloudSimsLoaded ? (
                <EmptyState
                  icon={Activity}
                  title="No simulations yet"
                  data-testid="engineering-empty-state"
                  action={bomSimulationGenerationEnabled ? (
                    <Button size="sm" variant="outline" className="h-8 type-body uppercase tracking-widest" onClick={openGenerateWizard} disabled={lockReadOnly} title={lockReadOnly ? `Locked by ${lockHolderLabel} — read-only` : undefined}>
                      <Play className="w-3 h-3 mr-1" /> Generate from BOM
                    </Button>
                  ) : undefined}
                >
                  <div className="w-full flex justify-center -my-6 sm:-my-4">
                    <UnifiedLoader phase="idle" variant="inline" />
                  </div>
                </EmptyState>
              ) : (
                <div className="text-center py-12 space-y-3 rounded-lg border border-dashed border-border">
                  <Activity className="w-10 h-10 text-text-secondary mx-auto" />
                  <div className="text-[10px] text-text-secondary uppercase tracking-widest">Loading...</div>
                </div>
              )
            ) : (
              <div className="space-y-2">
                {allSims.map((sim) => {
                  const cs = sim.cloudId ? cloudSims.find(c => c.id === sim.cloudId) : undefined;
                  return (
                    <EngineeringSimCard
                      key={sim.id}
                      sim={sim}
                      isActiveModel={!!sim.tabId && sim.tabId === activeTabId}
                      nodesJson={cs?.nodes}
                      edgesJson={cs?.edges}
                      result={null}
                      onOpen={openSim}
                      deleteCloudSim={deleteCloudSim}
                      renameCloudSim={renameCloudSim}
                      listSimBackups={listSimBackups}
                      restoreSimBackup={restoreSimBackup}
                      onChanged={() => void fetchCloudSims()}
                      notify={{ success: toast.success, error: toast.error }}
                    />
                  );
                })}
              </div>
            )}
            </div>

            <div className="xl:col-span-4 min-w-0">
            {latestReport ? (
              <div className="rounded-lg shadow-sm border border-border p-3 space-y-1">
                <div className="type-body font-bold uppercase tracking-widest text-text-secondary mb-2">Data Pre-loaded from Site</div>
                <div className="grid grid-cols-1 gap-2 type-meta">
                  {sitePreload.ambientTempC != null && (
                    <>
                      <div><span className="text-text-secondary">Ambient:</span> <span className="font-bold">{sitePreload.ambientTempC}°C</span></div>
                      <div><span className="text-text-secondary">Wet Bulb:</span> <span className="font-bold">{sitePreload.wetBulbTempC ?? 'n/a'}°C</span></div>
                      <div><span className="text-text-secondary">Peak:</span> <span className="font-bold">{sitePreload.peakTempC ?? 'n/a'}°C</span></div>
                      <div><span className="text-text-secondary">Humidity:</span> <span className="font-bold">{sitePreload.humidityPct ?? 'n/a'}%</span></div>
                      <div><span className="text-text-secondary">Water:</span> <span className="font-bold">{sitePreload.waterStress ?? 'n/a'}</span></div>
                    </>
                  )}
                  {sitePreload.elevationM != null && (
                    <div><span className="text-text-secondary">Elevation:</span> <span className="font-bold">{Math.round(sitePreload.elevationM)}m</span></div>
                  )}
                  {sitePreload.arcgis.nearestPipelineKm != null && (
                    <div><span className="text-text-secondary">Nearest pipeline:</span> <span className="font-bold">{sitePreload.arcgis.nearestPipelineKm.toFixed(1)} km</span></div>
                  )}
                  {sitePreload.arcgis.nearestPipelineMassFlow && (
                    <div><span className="text-text-secondary">Pipeline mass flow:</span> <span className="font-bold">{sitePreload.arcgis.nearestPipelineMassFlow.value} {sitePreload.arcgis.nearestPipelineMassFlow.unit}</span></div>
                  )}
                  {sitePreload.arcgis.nearestPipelineAvailabilityPct != null && (
                    <div><span className="text-text-secondary">Pipeline availability:</span> <span className="font-bold">{sitePreload.arcgis.nearestPipelineAvailabilityPct}%</span></div>
                  )}
                  {sitePreload.arcgis.nearestTransmissionKm != null && (
                    <div><span className="text-text-secondary">Nearest transmission:</span> <span className="font-bold">{sitePreload.arcgis.nearestTransmissionKm.toFixed(1)} km</span></div>
                  )}
                  <div><span className="text-text-secondary">ArcGIS layers:</span> <span className="font-bold">{sitePreload.arcgis.pipelineLayerCount} pipeline · {sitePreload.arcgis.electricalLayerCount} electrical · {sitePreload.arcgis.environmentalConstraintLayerCount} environmental</span></div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-border-subtle p-3 type-meta text-text-muted">
                No climate data yet — generate a report to pre-load site data into simulations.
              </div>
            )}
            </div>
          </div>
          );
        })()}

        {/* Financial Model Tab (FA pack — FA12) */}
        {activeTab === 'financial' && (() => {
          const fa = site.financialAssumptions ?? null;
          // IC10 — canonical projectIntent only. Legacy alias retired.
          const intent = site.projectIntent ?? ({} as import('../../types/siteRag').ProjectIntent);
          const siteContext = site.siteContext ?? emptySiteContext();
          // The canonical core feeding sensitivity + cross-check. Honors
          // scenarios — sensitivity slides on the canonical scenario, not
          // the bare base.
          const core = fa ? canonicalCore(fa) : null;
          const updatedBy = faAuth.currentUser?.email ?? faAuth.currentUser?.uid ?? 'unknown';
          // E3 — PATHWAYS LIVE UNDER THE SITE OWNER'S PATH, not the viewer's.
          // `users/{uid}/sites/{siteId}/pathways…` keyed by the VIEWER meant a
          // company-shared site resolved to a path that does not exist: every
          // save and load hit PERMISSION_DENIED, each viewer had a private silo
          // of the same site's comparisons, and a shared deep link resolved only
          // for its author. `siteOwnerUid` falls back to the current uid for a
          // site you own, so owner-only behaviour is unchanged.
          const pathwayUid = siteOwnerUid(site, faAuth.currentUser?.uid ?? '');

          const handleCommit = async (next: import('../../types/financialAssumptions').FinancialAssumptions): Promise<void> => {
            try {
              await updateSiteFinancialAssumptions(site.id, next);
              toast.success('Financial assumptions saved');
            } catch (err) {
              toast.error(`Save failed: ${(err as Error).message}`);
              // E5 - RETHROW. Swallowing the error made this resolve like a
              // success, so the extraction-review modal tore itself down and
              // the user's reviewed numbers were lost with only a toast behind
              // them. The caller decides what to keep open on failure.
              throw err;
            }
          };

          const handleScenarioUpdate = async (
            scenarios: import('../../types/financialAssumptions').FinancialScenario[],
          ): Promise<void> => {
            if (!fa) return;
            try {
              await updateSiteFinancialAssumptions(site.id, { ...fa, scenarios });
            } catch (err) {
              toast.error(`Scenario save failed: ${(err as Error).message}`);
            }
          };

          const handleCrossSync = async (patch: { capacityMW: number }): Promise<void> => {
            try {
              await updateSiteFinancialAssumptionsField(
                site.id,
                'capacityMW',
                patch.capacityMW,
                {
                  source: 'manual',
                  confidence: 'high',
                  extractedAt: new Date().toISOString(),
                },
              );
              toast.success(`Capacity synced to ${patch.capacityMW} MW`);
            } catch (err) {
              toast.error(`Sync failed: ${(err as Error).message}`);
            }
          };

          // aurora-qa-3 (⑪) — ONE persistent PathwayComparisonSection element,
          // kept mounted across the Pathways ↔ Cashflow sub-tabs so its internal
          // `pathways` state survives a sub-tab switch (the `view` prop just
          // changes which slices render). Mounted only when either of those two
          // sub-tabs is active.
          const pathwayView = financialSubTab === 'cashflow' ? 'cashflow-lifecycle' : 'pathways';
          const pathwaySection = (
            <Suspense fallback={<RouteLoadingSkeleton />}>
              <PathwayComparisonSection
                siteId={site.id}
                intent={intent}
                siteContext={siteContext}
                updatedBy={updatedBy}
                uid={pathwayUid}
                financialAssumptions={fa}
                initialComparisonId={pathwayCompareId}
                pinnedPathwayComparisonId={site.pinnedPathwayComparisonId}
                onPinnedComparisonChange={(comparisonId) => {
                  void updateSite(site.id, {
                    pinnedPathwayComparisonId: comparisonId ?? undefined,
                  });
                }}
                view={pathwayView}
              />
            </Suspense>
          );

          return (
            <div className="p-4 md:p-5 space-y-4" data-testid="financial-tab-root">
              {/* INTEGRATION — HEAD's tab-order/declutter commit removed
                  the per-tab SiteTabSectionHeaderFromMeta (and its import);
                  keep that removal. Founder QA round 3 (item 0): the
                  "Bottom line" cockpit strip (LCOE / NPV / IRR / payback /
                  decision band) was removed — it duplicated the panels
                  below with mostly-empty tiles on typical sites. */}
              <p className="max-w-prose text-sm text-text-muted">Set the inputs, review cashflow, then compare options. {isDataCenterFinance(fa) ? 'Option previews are unsaved; Pathways provides separate power benchmarks.' : 'Pathways compares power alternatives; saved scenarios remain available in Compare options.'}</p>
              <ResponsiveTabStrip
                variant="section"
                ariaLabel="Financial section tabs"
                testId="financial-subtabs"
                activeId={financialSubTab}
                onSelect={(id) => setFinancialSubTab(id as FinancialSubTab)}
                tabs={[
                  { id: 'assumptions', label: 'Inputs', icon: Settings2, testId: 'financial-subtab-assumptions' },
                  { id: 'cashflow', label: 'Cashflow', icon: Activity, testId: 'financial-subtab-cashflow' },
                  { id: 'scenarios', label: 'Compare options', shortLabel: 'Compare', icon: BarChart3, testId: 'financial-subtab-scenarios' },
                  { id: 'pathways', label: 'Pathways', icon: GitBranch, testId: 'financial-subtab-pathways' },
                ]}
              />

              {/* Keep the editor mounted across financial navigation so an
                  unsaved draft and its source/conflict state survive. */}
                <div className="space-y-4" hidden={financialSubTab !== 'assumptions'} aria-hidden={financialSubTab !== 'assumptions'} data-testid="financial-subpanel-assumptions">
                  <Suspense fallback={<RouteLoadingSkeleton />}>
                    <FinancialModelEntryPanel siteId={site.id} fa={fa} ownerUid={pathwayUid} scenarioId={site.demonstrationProvenance?.scenarioId} updatedBy={updatedBy} onCommit={handleCommit} />
                  </Suspense>
                  {!isDataCenterFinance(fa) && <CrossValidationStrip fa={core} intent={intent} onSync={handleCrossSync} />}
                </div>

              {/* PATHWAYS + CASHFLOW & LIFECYCLE — rendered from ONE shared
                  PathwayComparisonSection element kept in a SINGLE DOM position
                  across both sub-tabs, so its internal `pathways` state survives
                  a switch between them (the `view` prop changes which slices
                  render: 'pathways' → picker + compare + risk matrix;
                  'cashflow-lifecycle' → the cashflow + lifecycle charts). The
                  data-testid reflects the active sub-tab for the test suite. */}
              {/* E4 — HIDDEN, NOT UNMOUNTED. The section owns the `pathways`
                  state: the picked alternatives, every quick-edit override and
                  any loaded comparison. Unmounting it on a switch to the model
                  or scenarios sub-tab discarded all of that silently, so a user
                  who stepped away to check an assumption came back to a reset
                  comparison. Keeping it mounted also PRESERVES the 400-run
                  Monte-Carlo memo, which a remount would re-run. */}
              <div
                className={financialSubTab === 'pathways' || financialSubTab === 'cashflow' ? 'space-y-4' : 'hidden'}
                aria-hidden={financialSubTab !== 'pathways' && financialSubTab !== 'cashflow'}
                data-testid={financialSubTab === 'cashflow' ? 'financial-subpanel-cashflow' : 'financial-subpanel-pathways'}
              >
                {isDataCenterFinance(fa) && financialSubTab === 'cashflow' && <><div className="flex flex-wrap items-center justify-between gap-2"><h3 className="text-base font-semibold">Saved cashflow</h3><Button size="sm" variant="outline" onClick={() => setFinancialSubTab('assumptions')}>Edit inputs</Button></div><DataCenterFinancialSnapshot stamp={buildDataCenterFinancialAnalysis({ financialAssumptions: fa }).stamp} section="cashflowStream" presentation="compact" onEditInputs={() => setFinancialSubTab('assumptions')} /></>}
                {!fa && financialSubTab === 'cashflow' && <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-4"><p className="max-w-prose text-sm text-text-muted">Add project inputs to calculate your financial case. Power pathway benchmarks remain available below.</p><Button size="sm" variant="outline" onClick={() => setFinancialSubTab('assumptions')}>Add inputs</Button></div>}
                <div className="space-y-4" hidden={isDataCenterFinance(fa) && financialSubTab === 'cashflow'}>
                  {isDataCenterFinance(fa) && <p className="text-sm text-text-muted">Benchmark power pathways are separate comparisons; they do not price the capacity-fee model.</p>}{pathwaySection}
                </div>
              </div>

              {/* SCENARIOS & SENSITIVITY. */}
              {financialSubTab === 'scenarios' && (
                <div className="space-y-4" data-testid="financial-subpanel-scenarios">
                  {fa && !isDataCenterFinance(fa) && <ScenarioComparePanel
                    fa={fa}
                    site={site}
                    reports={siteReports}
                    onUpdate={handleScenarioUpdate}
                    onRecomputeComposite={async () => {
                      await computeCompositeForSite(site.id);
                    }}
                  />}
                  {fa && !isDataCenterFinance(fa) && <SensitivityPanel core={core} />}
                  {isDataCenterFinance(fa) && <DataCenterFinancialScenariosPanel financialAssumptions={fa} onEditAssumptions={() => setFinancialSubTab('assumptions')} />}
                  {!fa && (
                    <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border p-4" data-testid="financial-scenarios-empty">
                      <p className="max-w-prose text-sm text-text-muted">Add financial inputs before comparing options and sensitivity.</p>
                      <Button size="sm" variant="outline" onClick={() => setFinancialSubTab('assumptions')}>Add inputs</Button>
                    </div>
                  )}
                </div>
              )}
              {/* 6.1 — SETTLED 2026-08-20. Of the three modules once parked
                  here: `iraTaxCredits.ts` was in fact already wired (it backs
                  the SED incentives evidence), and `extractPpaTerms.ts` +
                  `precedentComparison.ts` were dormant with zero importers and
                  have been deleted under the no-dormant-code rule. Neither is
                  lost — both restore verbatim from 9cb2c600c if a PPA
                  term-sheet or precedent-cohort surface is ever built. There is
                  no longer a pending decision behind this comment. */}
            </div>
          );
        })()}

        {/* Supply Chain Tab — SCM4 5-tab panel replaces the prior
            "Coming Soon" placeholder. Renders empty-state CTAs internally
            when site.supplyChain is undefined; the BoM seeding write path
            lands with SCM-Data + SCM-Integration in parallel. */}
        {activeTab === 'supplychain' && (
          <SupplyChainPanel site={site} />
        )}

        {activeTab === 'audit' && (
          <SiteAuditTab
            site={site}
            siteReports={siteReports}
            siteDocuments={siteDocuments}
            onViewReport={handleViewReport}
            onJumpToDocuments={() => setActiveTab('documents')}
            onJumpToEvidence={() => setActiveTab('evidence')}
          />
        )}

        {activeTab === 'deepdive' && (
          <SiteDeepDiveTab
            siteComposite={currentSiteComposite}
            onViewReport={handleViewReport}
            siteDocuments={siteDocuments}
            onResolveWithheld={() => openDecisionWork()}
            // The read-side oracle above resolves stale persisted composites
            // synchronously. Only incomplete source hydration is a real
            // recomputing state now; once loaded, the numbers are current even
            // if their best-effort persistence is still in flight.
            recomputing={!reportsLoaded || docsTabSedReadBlocked}
          />
        )}

        {activeTab === 'roadmap' && (
          <div className="p-4 md:p-5 space-y-4">
            <RecommendationGraphView site={site} />
          </div>
        )}

        {activeTab === 'evidence' && (
          <div className="p-4 md:p-5 space-y-4 w-full min-w-0" data-testid="evidence-tab-layout-root">
            {/* Coverage-by-domain card removed 2026-06-10 per William. */}
            <ContextualGuideTip
              tipId="evidence-tab"
              stepId="build-sed"
              title="What is the SED?"
              body="The Site Evidence Document is the versioned factual record your reports read at generation time. Rebuild after major site or document changes so sedVersionUsed stays current."
              uid={guideUid}
              progress={guideProgress}
              onProgressChange={setGuideProgress}
            />
            {/* SET-B3 — cross-tab focus breadcrumb on the Site Evidence
                 side. Same component, currentTab='evidence' so the
                 Back-to copy reads "Back to Documents". */}
            <CrossTabFocusBreadcrumb
              focus={crossTabFocus}
              currentTab="evidence"
              focusLabel={focusLabel}
              onBack={flipFocusTab}
              onClear={clearFocus}
            />
            <SiteEvidenceTab
              // Merge the LIVE SED pointer (from the site-doc onSnapshot) into the
              // site prop so the tab's SED loader re-fires the moment a (first)
              // rebuild writes sedId/sedVersion/sedStoragePath — without this the
              // tab read the stale prop and the empty-state stuck until reload.
              site={{ ...workflowSite, sedId: liveSedId, sedVersion: liveSedVersion, sedStoragePath: liveSedStoragePath }}
              sedStatus={sedStatus}
              sedCompleteness={sedCompleteness}
              onCurrentEvidenceLoaded={handleCurrentEvidenceLoaded}
              siteReports={siteReports}
              coverageReportTypes={siteCoverageMatch.reportTypes}
              siteDocuments={siteDocuments}
              focus={crossTabFocus}
              onClearFocus={clearFocus}
              onOpenDocument={jumpToDocumentsForDoc}
              onOpenInDocuments={jumpToDocumentsForReq}
              onOpenUploadFor={jumpToDocumentsUploadFor}
              onReplaceDoc={jumpToDocumentsReplaceDoc}
              onAddSupportingDoc={jumpToDocumentsAddSupporting}
              onRetrySed={handleRetryBootstrap}
              onUploadClick={() => goToLibrary()}
              hasGeometry={(site.siteBoundary?.length ?? 0) >= 3 || (site.siteLayers?.length ?? 0) > 0}
              onExportKMZ={handleExportKMZ}
              onUpdateKMZ={handleUpdateKMZ}
              onOpenGoogleEarth={handleOpenGoogleEarth}
              onEditLayers={openLayerEditorIfReady}
              onOpenSiteIntent={(fieldKey) => {
                // A fact edit opens its owning step, including proposal fields
                // hidden by the recommendation path. Opening Intent alone keeps
                // the existing screening/Stage 2 overview destination.
                if (fieldKey || stage2Decisions?.available) setIntentInputView('detailed');
                setIntentFieldFocus(fieldKey ?? null);
                setActiveTab('intent');
              }}
              decisionStage={pathToGoSharedState.decisionStage?.currentStage}
              matchByType={siteCoverageMatch.matchByType}
              evidenceSteps={evidenceSteps}
              onResolveEvidenceStep={handleResolveEvidenceStep}
              onSecondaryEvidenceAction={handleSecondaryEvidenceAction}
              arrivalReturn={evidenceFromDecision
                ? {
                    label: splitDecisionSurfacesEnabled ? 'Decision' : (decisionFindingsEnabled ? 'Analyses' : 'Reports'),
                    onBack: () => setActiveTab(decisionHomeTab),
                  }
                : null}
            />
            {evidenceOutreachTarget && (
              <ContractorOutreachDialog
                checklistRowId={evidenceOutreachTarget.id}
                docTypeLabel={evidenceOutreachTarget.label}
                siteContext={outreachSiteContext(workflowSite)}
                onClose={() => setEvidenceOutreachTarget(null)}
              />
            )}
          </div>
        )}
      </div>

      {/* Document Library Modal — opened from header / overview shortcuts. Uses
          the same site-mode persistence path as the embedded Documents tab. */}
      {showLibrary ? (
        <Suspense fallback={<RouteLoadingSkeleton />}>
          <DocumentLibrary
            documents={siteDocuments}
            siteId={site.id}
            open={showLibrary}
            onClose={() => setShowLibrary(false)}
            onJumpToVariable={(sectionId, fieldKey) => {
              // DF-3 — close the modal first so the user lands on the
              // Site Evidence panel cleanly, then route through the same
              // bridge the embedded mount uses.
              setShowLibrary(false);
              jumpToVariable(sectionId, fieldKey);
            }}
            onUpdateDocuments={async (updated) => {
              await updateSiteDocuments(site.id, updated);
            }}
          />
        </Suspense>
      ) : null}
      <DataConfidenceDrawer
        open={overviewDataConfidenceOpen}
        composite={currentSiteComposite}
        siteId={site.id}
        onClose={() => setOverviewDataConfidenceOpen(false)}
      />
    </div>
  );
}

/**
 * Memoised: SiteRagEngine re-renders on store slices this page never reads
 * (report lists, dashboard state). With unchanged props — the engine passes
 * stable callbacks and the store hands back the same site object when its
 * data did not change — React skips this component entirely instead of
 * re-running its memo chain.
 */
export const SiteLandingPage = React.memo(SiteLandingPageImpl);

/* ────────────────────────────────────────────────────────────────────
 * SiteArchetypeRow — read + inline-edit site.archetypeOverride.
 *
 * Shows the resolved archetype (site override → company default) with
 * a pill calling out the source, plus a select dropdown that lets the
 * user pin a site-level archetype or fall back to "Inherit from
 * company". Persisted via updateSite. The wizard's per-report override
 * still beats whatever this row sets.
 * ──────────────────────────────────────────────────────────────────── */
/* ────────────────────────────────────────────────────────────────────
 * RecentActivityPanel — SO6.
 *
 * Collapsible timeline at the bottom of the Overview tab. Merges three
 * sources of activity into a single time-sorted list (latest 5):
 *
 *   - Report generations (siteReports)
 *   - Doc uploads        (siteDocuments)
 *   - SED rebuilds       (site.sedComputedAt — single entry)
 *
 * Default state: collapsed, header reads "▶ Recent activity · N events".
 * Each row is a button that routes the user to the appropriate tab /
 * report. Boundary edits are skipped (no audit log yet).
 * ──────────────────────────────────────────────────────────────────── */
type ActivityKind = 'report' | 'doc' | 'sed';
interface ActivityEntry {
  kind: ActivityKind;
  label: string;
  at: string;
  onClick: () => void;
}
const RecentActivityPanel: React.FC<{
  site: SiteEntity;
  siteReports: SiteRagReport[];
  siteDocuments: import('../../types/siteRag').SiteDocument[];
  onViewReport: (reportId: string) => void;
  onJumpToDocuments: () => void;
  onJumpToEvidence: () => void;
}> = ({ site, siteReports, siteDocuments, onViewReport, onJumpToDocuments, onJumpToEvidence }) => {
  const [open, setOpen] = React.useState(false);

  const events = React.useMemo<ActivityEntry[]>(() => {
    const out: ActivityEntry[] = [];
    for (const r of siteReports) {
      const typeId = (r.reportType ?? 'feasibility') as ReportTypeId;
      const shortLabel = REPORT_TYPE_REGISTRY[typeId]?.shortLabel ?? 'Report';
      out.push({
        kind: 'report',
        label: `${shortLabel} report v${r.version}.${r.minorVersion ?? 0} generated`,
        at: r.createdAt,
        onClick: () => onViewReport(r.id),
      });
    }
    for (const d of siteDocuments) {
      out.push({
        kind: 'doc',
        label: `${resolveSiteDocumentFileName(d, d.id)} uploaded`,
        at: d.uploadedAt,
        onClick: onJumpToDocuments,
      });
    }
    if (site.sedComputedAt) {
      out.push({
        kind: 'sed',
        label: 'Site Evidence rebuilt',
        at: site.sedComputedAt,
        onClick: onJumpToEvidence,
      });
    }
    return out
      .filter(e => typeof e.at === 'string' && e.at.length > 0)
      .sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime())
      .slice(0, 5);
  }, [site.sedComputedAt, siteReports, siteDocuments, onViewReport, onJumpToDocuments, onJumpToEvidence]);

  if (events.length === 0) return null;

  return (
    <div className="rounded-lg shadow-sm overflow-hidden border border-border" data-testid="overview-recent-activity">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-state-hover transition-colors"
        aria-expanded={open}
        data-testid="overview-recent-activity-toggle"
      >
        <span className="type-meta text-text-secondary">
          <span className="inline-block w-3">{open ? '▼' : '▶'}</span> Recent activity · {events.length} event{events.length === 1 ? '' : 's'}
        </span>
      </button>
      {open && (
        <ul className="border-t border-border divide-y divide-border" data-testid="overview-recent-activity-list">
          {events.map((e, i) => {
            const ts = new Date(e.at).getTime();
            const rel = Number.isFinite(ts) ? relativeTime(ts) : '';
            return (
              <li key={`${e.kind}-${i}`}>
                <button
                  type="button"
                  onClick={e.onClick}
                  className="w-full flex items-center justify-between gap-3 px-4 py-2 text-left hover:bg-state-hover transition-colors"
                  data-testid={`overview-recent-activity-row-${e.kind}-${i}`}
                >
                  <span className="text-xs text-foreground truncate">{e.label}</span>
                  <span className="shrink-0 type-body uppercase tracking-widest text-text-muted">{rel}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// RC5 — exported so the reader-profile regression test can mount the
// subcomponent without the full SiteLandingPage harness. Not part of
// the public surface; callers should keep going through SiteLandingPage.
export const SiteArchetypeRow: React.FC<{ site: SiteEntity }> = ({ site }) => {
  const updateSite = useStore(s => s.updateSite);
  const companyArchetype = useStore(s => s.companyArchetype);
  const [saving, setSaving] = React.useState(false);

  const siteOverride: CompanyArchetype | null = site.archetypeOverride ?? null;
  const effective: CompanyArchetype | null = siteOverride ?? (companyArchetype as CompanyArchetype | undefined) ?? null;
  const meta = effective ? COMPANY_ARCHETYPES[effective] : undefined;

  // RC1 — both the source pill and the dropdown's "Inherit" option now
  // resolve the inherited archetype to its actual label so the user can
  // see what they're inheriting *before* selecting. Keeps the wording
  // consistent across the two surfaces ("Inherited · Hyperscaler" vs.
  // the prior "Inherits from company · Hyperscaler").
  const sourcePillCopy = siteOverride
    ? `Site default · ${meta?.label ?? siteOverride}`
    : companyArchetype
      ? `Inherited · ${COMPANY_ARCHETYPES[companyArchetype as CompanyArchetype]?.label ?? companyArchetype}`
      : 'Neutral framing — no archetype set';
  const inheritOptionLabel = companyArchetype
    ? `Inherit from company · ${COMPANY_ARCHETYPES[companyArchetype as CompanyArchetype]?.label ?? companyArchetype}`
    : 'Inherit from company · (no company default set — neutral framing)';

  const handleChange = async (next: CompanyArchetype | null) => {
    setSaving(true);
    try {
      // Use undefined (not null) when clearing — Firestore + the
      // SiteEntity type both treat undefined as the absent state.
      await updateSite(site.id, { archetypeOverride: next ?? undefined });
    } catch (e) {
      console.warn('[SiteArchetypeRow] save failed:', (e as Error)?.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-lg shadow-sm border border-border p-4 space-y-2 min-w-0 overflow-hidden" data-testid="site-archetype-row">
      <div className="flex items-center justify-between gap-3 flex-wrap min-w-0">
        <div className="min-w-0">
          <h3 className="type-section text-text-secondary">Reader profile</h3>
          <div className="type-body text-text-muted mt-0.5">
            Frames every report against this site unless the wizard picks a different one.
          </div>
        </div>
        <span
          data-testid="site-archetype-source-pill"
          data-source={siteOverride ? 'site' : companyArchetype ? 'company' : 'none'}
          className={`type-meta font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border shrink-0 max-w-full truncate ${
            siteOverride
              ? 'bg-primary/15 text-primary border-primary/40'
              : 'bg-state-hover text-text-secondary border-border'
          }`}
          title={sourcePillCopy}
        >
          {sourcePillCopy}
        </span>
      </div>
      <div className="flex flex-col gap-1 min-w-0 sm:flex-row sm:items-center sm:gap-2">
        <label className="type-meta text-text-muted shrink-0">Site default</label>
        <select
          data-testid="site-archetype-select"
          className="h-7 w-full min-w-0 flex-1 rounded-md text-[10px] border border-border bg-background px-1"
          title={siteOverride ? (meta?.label ?? siteOverride) : inheritOptionLabel}
          value={siteOverride ?? ''}
          disabled={saving}
          onChange={(e) => {
            const v = e.target.value;
            void handleChange(v ? (v as CompanyArchetype) : null);
          }}
        >
          <option value="">{inheritOptionLabel}</option>
          {Object.values(COMPANY_ARCHETYPES).map((m) => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>
        {saving && <span className="type-body text-text-muted">Saving…</span>}
      </div>
      {/* RC2 — surface readerLens + risk + decisions + narrative shifts
          for the currently effective archetype so the user can see what
          this choice actually changes about their report. Collapsed by
          default to keep the row tight. */}
      <ArchetypeImpactDisclosure
        archetype={effective}
        defaultOpen={false}
        testIdPrefix="site-archetype-impact"
      />
    </div>
  );
};


/* ────────────────────────────────────────────────────────────────────
 * CrossTabFocusBreadcrumb — SET-B3.
 *
 * Renders a thin strip above the Documents / Site Evidence tab body
 * whenever `?focus=<kind>:<id>` is set. The strip carries:
 *   - a "Back to {otherTab}" button that flips the tab while keeping
 *     the focus context (so the user can pivot back without losing
 *     the audit thread),
 *   - the current focus label ("Focused on Borehole_Survey.pdf"),
 *   - a "Clear ×" affordance that strips the focus param.
 *
 * Browser back / forward work naturally because focus state lives in
 * the URL (`useUrlSearchParamSync('focus', …)` in SiteLandingPage).
 * The breadcrumb is purely a presentational mirror of that URL state.
 * ──────────────────────────────────────────────────────────────────── */
interface CrossTabFocusBreadcrumbProps {
  /** Active focus, parsed from `?focus=` — null hides the breadcrumb.
   *  EDX-3 extends the union with `req` / `upload-for` / `conflicts`
   *  (Coverage Matrix bridges + EvidenceConflictsPanel focus). */
  focus: { kind: 'doc' | 'variable' | 'req' | 'upload-for' | 'replace' | 'conflicts'; id: string } | null;
  /** Which tab the user is currently on — drives the "Back to {other}" copy. */
  currentTab: 'documents' | 'evidence';
  /** Human-readable label resolved from the focus id (e.g. doc filename). */
  focusLabel: string;
  /** Flip the active tab (B3 keeps focus); host typically wraps setActiveTab. */
  onBack: () => void;
  /** Strip the focus param. */
  onClear: () => void;
}
const CrossTabFocusBreadcrumb: React.FC<CrossTabFocusBreadcrumbProps> = ({
  focus,
  currentTab,
  focusLabel,
  onBack,
  onClear,
}) => {
  if (!focus) return null;
  const otherTabLabel = currentTab === 'documents' ? 'Site Evidence' : 'Documents';
  return (
    <div
      className="flex items-center gap-2 type-meta bg-state-hover border-l-2 border-primary px-3 py-2"
      data-testid="cross-tab-focus-breadcrumb"
      data-focus-kind={focus.kind}
      data-focus-id={focus.id}
    >
      <ChevronLeft className="w-3 h-3" aria-hidden />
      <button
        type="button"
        onClick={onBack}
        className="hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        data-testid="cross-tab-focus-back"
      >
        Back to {otherTabLabel}
      </button>
      <span aria-hidden>·</span>
      <span data-testid="cross-tab-focus-label">Focused on {focusLabel}</span>
      <button
        type="button"
        onClick={onClear}
        className="ml-auto text-text-muted hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
        data-testid="cross-tab-focus-clear"
        aria-label="Clear focus"
      >
        Clear ×
      </button>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────────────
 * SiteEvidenceTab — read/edit the site's SED.
 *
 * The site doc holds a pointer (sedId / sedStatus / sedCompleteness);
 * the actual SED JSON lives in GCS. We lazy-load the SED via
 * loadSedById on tab activation and render it through the existing
 * SEDVariableBrowser. Status pills handle the in-flight bootstrap
 * window and the rare missing-pointer case.
 * ──────────────────────────────────────────────────────────────────── */
/**
 * EV3 (2026-05-15) — compute the set of SED variable paths a given
 * report "cited", for the report-scoped variable-browser filter that
 * opens from the report header's Site Evidence breadcrumb.
 *
 * Pre-CT6 (no per-claim citation graph yet), this is an approximation
 * built from the data we DO have:
 *   1. `report.documentRefs` (with `userIncluded: true`) tells us
 *      which uploaded documents this specific report drew on.
 *   2. Each SED section carries `linkedDocumentIds[]` listing the
 *      uploaded documents linked to that section's evidence.
 *   3. INTERSECTION → if a SED section's linked docs overlap with the
 *      report's included docs, every variable under that section is
 *      considered "cited by this report".
 *
 * Returns an empty set when no overlap exists; the caller treats an
 * empty set as the legacy fallback (full SED + a banner explaining
 * no citation data is available for this report version).
 *
 * Post-CT6, this helper will switch to the precise path: parsing
 * `report.claims[].sources[]` for `sed:<path>` entries. The signature
 * stays the same so the caller doesn't have to change.
 */
function computeScopedVariablePaths(
  sed: SiteEvidenceDocument | null,
  report: SiteRagReport | undefined,
): Set<string> {
  if (!sed || !report) return new Set();

  // Collect the doc-ids this report explicitly included.
  const includedDocIds = new Set<string>();
  for (const ref of report.documentRefs ?? []) {
    if (ref.userIncluded !== false) includedDocIds.add(ref.docId);
  }
  // Legacy back-compat — pre-documentRefs reports carry a flat
  // `documents[]` array on the report itself. Treat every entry as
  // included (the old wizard had no exclusion affordance).
  for (const d of report.documents ?? []) {
    if (d.id) includedDocIds.add(d.id);
  }
  if (includedDocIds.size === 0) return new Set();

  // Walk every SED section. Sections whose linked docs overlap the
  // report's included set contribute all their leaf variables. The
  // variable-path shape mirrors `extractSEDVariables` exactly
  // (`<domain>.data.<key>`) — keeping the two in sync is enforced
  // by the SED schema (SED_SECTION_IDS).
  const out = new Set<string>();
  for (const domain of SED_SECTION_IDS) {
    const section = (sed as unknown as Record<string, { linkedDocumentIds?: string[]; supportingDocIds?: string[]; data?: Record<string, unknown> }>)[domain];
    if (!section || !section.data) continue;
    const linked = [
      ...(section.linkedDocumentIds ?? []),
      ...(section.supportingDocIds ?? []),
    ];
    const hits = linked.some(id => includedDocIds.has(id));
    if (!hits) continue;
    for (const [key, value] of Object.entries(section.data)) {
      // Mirror extractSEDVariables's skip-rules so the path set
      // matches the paths the browser actually renders.
      if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') continue;
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) continue;
      out.add(`${domain}.data.${key}`);
    }
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────
 * CollapsibleDocPanel — SET-Q9.
 *
 * Thin wrapper that mirrors the DocContentSection collapse pattern so
 * the two doc-related panels on the Site Evidence tab have a single
 * consistent collapse idiom (button + ▸/▾ arrow + space-y-2 frame,
 * collapsed by default). Keeps DocumentContentsPanel itself untouched
 * — that file is in flight for the D-series + later M-series merge.
 * ──────────────────────────────────────────────────────────────────── */
const CollapsibleDocPanel: React.FC<{
  title: string;
  subtitle?: string;
  testId?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, subtitle, testId, defaultOpen = false, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className="space-y-2" data-testid={testId ?? 'collapsible-doc-panel'}>
      <button
        type="button"
        className="flex items-center justify-between w-full text-left pb-1 border-b border-border-subtle focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : 'collapsible-doc-panel-toggle'}
      >
        <div className="space-y-0.5">
          <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
            {title}
          </div>
          {subtitle && (
            <div className="type-body text-text-muted">{subtitle}</div>
          )}
        </div>
        <span className="text-text-secondary">{open ? '▾' : '▸'}</span>
      </button>
      {open && children}
    </div>
  );
};

const SiteEvidenceTab: React.FC<{
  site: SiteEntity;
  sedStatus?: SiteEntity['sedStatus'];
  sedCompleteness?: number;
  /** Notify the page only after its exact current saved evidence was read. */
  onCurrentEvidenceLoaded?: (read: CurrentEvidenceReadIdentity) => void;
  /** Reports already produced (or planned) for this site. Drives
   *  `scopeReportTypes` — the report-type scope the CoverageMatrix and
   *  its ranked matcher walk. */
  siteReports: SiteRagReport[];
  /** Exact saved/planned union from the page's single matching run. */
  coverageReportTypes?: readonly ReportTypeId[];
  /** Site documents merged with any legacy report-attached docs (the
   *  same merged list the parent computes for the rest of the page). */
  siteDocuments: import('../../types/siteRag').SiteDocument[];
  /**
   * SET-M4 — props threaded from the parent so the toolbar (mounted
   * INSIDE this component as of M4 so it can see the local
   * `isRebuilding` / `loading` state) can fire Retry / Upload / KMZ
   * actions whose handlers live one level up.
   */
  onRetrySed?: () => void;
  onUploadClick?: () => void;
  hasGeometry?: boolean;
  onExportKMZ?: () => void;
  /** KMU — opens SiteLocationBoundaryEditor on the Import dropzone. */
  onUpdateKMZ?: () => void;
  /** Opens Google Earth Web at the site boundary centroid. */
  onOpenGoogleEarth?: () => void;
  /** Opens the SiteLayerEditor modal for map overlay layers. */
  onEditLayers?: () => void;
  /**
   * SET-B1 — focus receiver. Extended by EDX-3/EDX-8 to carry `req`,
   * `upload-for`, and `conflicts` kinds alongside the original
   * `doc` | `variable`. The tab dispatches per kind (doc → row
   * highlight, variable → SEDVariableBrowser scroll, req → no-op
   * here since req is a Documents-tab destination kind, conflicts →
   * EvidenceConflictsPanel focus). `clearFocus` is called after the
   * effect resolves so a refresh doesn't re-trigger.
   */
  focus?: { kind: 'doc' | 'variable' | 'req' | 'upload-for' | 'replace' | 'conflicts'; id: string } | null;
  onClearFocus?: () => void;
  /**
   * SET-B2 — Site Evidence → Documents bridge. Forwarded to
   * SEDVariableBrowser → VariableLineageDrawer so the doc rows in
   * each variable's lineage drawer become clickable. The host
   * (SiteLandingPage) writes `?focus=doc:<id>` + flips activeTab to
   * 'documents' on receipt.
   */
  onOpenDocument?: (docId: string) => void;
  /**
   * EDX-3 — CoverageMatrix bridges. `onOpenInDocuments(reqId)` writes
   * `?focus=req:<id>` and flips to the Documents tab; the receiver
   * (EDX-8) applies the SED-contribution filter pill. `onOpenUploadFor`
   * uses `upload-for:<id>` and opens the upload widget pre-tagged.
   * Both optional so legacy mounts (none today) still type-check.
   */
  onOpenInDocuments?: (requirementId: string) => void;
  onOpenUploadFor?: (requirementId: string) => void;
  /**
   * CMU-2 — populated-row `[Replace]` bridge. Writes
   * `?focus=replace:<docId>::<requirementId>` and flips to Documents.
   * The DocumentLibrary receiver opens a replace-mode dropzone keyed
   * on the existing docId; soft-archives the old doc on upload.
   */
  onReplaceDoc?: (docId: string, requirementId: string) => void;
  /**
   * CMU-3 — populated-row `[+ Add supporting]` bridge. Currently
   * shares the `upload-for` focus path with the missing-row Upload
   * button (the new doc lands tagged with the requirementId); the
   * distinct prop keeps the Coverage Matrix label semantics intact
   * and gives future hosts a hook for a divergent flow.
   */
  onAddSupportingDoc?: (requirementId: string) => void;
  /** Opens Site Project Intent editor on Overview tab. */
  onOpenSiteIntent?: (fieldKey?: string) => void;
  /**
   * EVIDENCE LEDGER — the ranked evidence worklist, ALREADY narrowed by
   * `selectEvidenceSteps` (the array the deleted Path to GO Evidence stage
   * rendered); passing it (rather than re-deriving a list here from
   * `documentChecklists`) is what stops the two surfaces reporting different
   * work for the same site. See `lib/pathToGo/evidenceSteps.ts`.
   */
  evidenceSteps?: readonly import('../../lib/pathToGo/nextStep').NextStep[];
  /** Resolve one ledger task with its primary verb. */
  onResolveEvidenceStep?: (
    step: import('../../lib/pathToGo/nextStep').NextStep,
    verb: import('../../lib/pathToGo/evidenceSteps').EvidenceVerbKind,
  ) => void;
  /** Secondary affordance for a ledger task (re-run, provider status, email). */
  onSecondaryEvidenceAction?: (
    step: import('../../lib/pathToGo/nextStep').NextStep,
    verb: import('../../lib/pathToGo/evidenceSteps').EvidenceVerbKind,
  ) => void;
  /**
   * Set when the user arrived from a Decision-tab route (a blocker or a
   * verification link): the ledger offers a way back. Null otherwise.
   */
  arrivalReturn?: { label: string; onBack: () => void } | null;
  /**
   * The site's decision stage. During early screening (1) the requirement
   * scope and document checklist lead with Stage 1 work and show Stage 2
   * documents as "needed later". Absent → the historical full planning set.
   */
  decisionStage?: 1 | 2;
  /**
   * The page-level ranked DCT match (`useSiteCoverageMatch`) — the SAME run
   * Path to GO's `coverageMatchByType` reads, so the CoverageMatrix, the Author
   * prefill and the Evidence ledger above them agree on which uploads count.
   * Required: a mount without it would silently drop Tier-3 matches here.
   */
  matchByType: Map<ReportTypeId, RankedChecklistMatch>;
}> = ({
  site,
  sedStatus,
  onCurrentEvidenceLoaded,
  decisionStage,
  matchByType,
  coverageReportTypes,
  // sedCompleteness stays on the prop contract (callers still pass it) but
  // is no longer read here — the provider-coverage line moved out with the
  // EvidenceReadinessHeader mount; the toolbar SedStatusPill is canonical.
  siteReports,
  siteDocuments,
  onRetrySed,
  onUploadClick,
  hasGeometry,
  onExportKMZ,
  onUpdateKMZ,
  onOpenGoogleEarth,
  onEditLayers,
  focus,
  onClearFocus,
  onOpenDocument,
  onOpenInDocuments,
  onOpenUploadFor,
  onReplaceDoc,
  onAddSupportingDoc,
  onOpenSiteIntent,
  evidenceSteps,
  onResolveEvidenceStep,
  onSecondaryEvidenceAction,
  arrivalReturn,
}) => {
  // This is an Evidence review view, independent from decision approval.
  // Keying an override to the site prevents it leaking across subject switches.
  const [evidenceReviewOverride, setEvidenceReviewOverride] = React.useState<{ siteId: string; stage: 1 | 2 } | null>(null);
  const evidenceReviewStage = decisionStage == null ? undefined
    : evidenceReviewOverride?.siteId === site.id ? evidenceReviewOverride.stage : decisionStage;

  // KSD4 — DocumentContentsPanel persists `includeInSed` flips via the
  // store action `updateSiteDocuments`. Scoped to this tab so the
  // wizard / dashboard surfaces don't take a dependency.
  const updateSiteDocuments = useStore(s => s.updateSiteDocuments);
  const persistSiteDocuments = React.useCallback(
    async (siteId: string, next: import('../../types/siteRag').SiteDocument[]) =>
      updateSiteDocuments(siteId, next),
    [updateSiteDocuments],
  );
  // A saved-review reread must not tear down the row/filter/scroll workspace.
  // Scope the retained body in render, before effects run, so a subject or
  // version switch can never paint the previous subject's evidence.
  const evidenceReaderUid = faAuth.currentUser?.uid ?? '';
  const evidenceOwnerUid = siteOwnerUid(site, evidenceReaderUid);
  const evidenceReadScope = JSON.stringify([
    site.id, evidenceOwnerUid, evidenceReaderUid,
    site.sedId, site.sedStoragePath, site.sedVersion,
  ]);
  const [sedSnapshot, setSedSnapshot] = React.useState<{
    scope: string;
    document: SiteEvidenceDocument;
  } | null>(null);
  const sed = sedSnapshot?.scope === evidenceReadScope ? sedSnapshot.document : null;
  /** Field currently being written, so its row disables rather than double-fires. */
  const [reviewBusyKey, setReviewBusyKey] = React.useState<string | null>(null);

  /**
   * Persist one extracted-field decision, then reload the SED so the panel
   * shows the result rather than the pre-decision state. A rejected value is
   * DELETED, so a stale render would keep showing data the user just removed.
   */
  const runFieldReview = React.useCallback(async (
    sectionId: string,
    fieldKey: string,
    decision: import('../../lib/coverage/extractedFieldReview').FieldReviewDecision | null,
    correctedValue?: import('../../types/siteEvidence').StructuredEvidenceValue,
  ) => {
    if (reviewBusyKey) return;
    setReviewBusyKey(fieldKey);
    try {
      const res = await useStore.getState().reviewExtractedField(
        site.id, sectionId, fieldKey, decision, correctedValue,
      );
      if (!res.ok) {
        toast.error(res.reason ?? 'The review could not be saved.');
        return;
      }
      toast.success(
        decision === null ? 'Review undone.'
          : decision === 'rejected' ? 'Marked wrong — the value was removed.'
            : decision === 'corrected' ? 'Value corrected.'
              : 'Confirmed against the document.',
      );
      setReloadKey(k => k + 1);
    } catch (e) {
      toast.error((e as Error)?.message ?? 'The review could not be saved.');
    } finally {
      setReviewBusyKey(null);
    }
  }, [site.id, reviewBusyKey]);
  type EvidencePane = 'actions' | 'coverage' | 'open-items' | 'variables' | 'context' | 'changes';
  const [activeEvidencePane, setActiveEvidencePane] = React.useState<EvidencePane>('coverage');
  const unifiedEvidencePanel = isUnifiedEvidencePanelEnabled();
  const EVIDENCE_TAB_META: Record<
    EvidencePane,
    { label: string; shortLabel: string; icon: LucideIcon; requiresSed: boolean }
  > = {
    coverage: { label: 'All evidence', shortLabel: 'Evidence', icon: Grid3x3, requiresSed: true },
    'open-items': { label: 'Open items', shortLabel: 'Open items', icon: ListChecks, requiresSed: true },
    variables: { label: 'Extracted variables', shortLabel: 'Variables', icon: Database, requiresSed: true },
    context: { label: 'Disagreements', shortLabel: 'Disagreements', icon: ArrowLeftRight, requiresSed: true },
    changes: { label: 'Recent changes', shortLabel: 'Changes', icon: History, requiresSed: true },
    actions: { label: 'Manage document', shortLabel: 'Manage', icon: Settings2, requiresSed: false },
  };
  const sedReady = Boolean(sed);

  // Field focus and per-report citation filtering belong to the values tab.
  const [focusVariablePath] = React.useState<string | null>(null);
  const [panelFocusVariablePath, setPanelFocusVariablePath] = React.useState<string | null>(null);
  const [panelVariablesExpanded, setPanelVariablesExpanded] = React.useState(false);
  const [focusRequirementId, setFocusRequirementId] = React.useState<string | null>(null);
  // Dedicated panels preserve each review position while switching tabs.
  const [requeryOpen, setRequeryOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const [authoringFor, setAuthoringFor] = React.useState<ChecklistItem | null>(null);
  /** Gap awaiting a waiver decision — drives WaiveGapDialog. */
  const [waivingGap, setWaivingGap] = React.useState<{ requirementId: string; label: string } | null>(null);

  /**
   * Record or clear a requirement waiver on the site.
   *
   * `category: null` REMOVES the waiver — the undo path. A recorded claim with
   * no way back is a trap: the first test waiver on this site had to be deleted
   * with a raw Firestore call because the UI offered no way out.
   */
  const writeWaiver = React.useCallback(async (
    requirementId: string,
    reason: string | null,
    category: import('./coverage/WaiveGapDialog').WaiveCategory | null,
  ): Promise<void> => {
    const next = { ...(site.requirementWaivers ?? {}) };
    if (category === null) {
      delete next[requirementId];
    } else {
      next[requirementId] = {
        requirementId,
        reason: reason ?? '',
        category,
        waivedAt: Date.now(),
        waivedBy: faAuth.currentUser?.email ?? faAuth.currentUser?.uid ?? 'unknown',
      };
    }
    await useStore.getState().updateSite(site.id, { requirementWaivers: next });
  }, [site.id, site.requirementWaivers]);

  // EDX-1 — DocumentContentsPanel + DocumentsInSED gone, so the
  // "Show extracted text" / Re-extract handlers that fed those rows
  // are removed. DocumentLibrary keeps its own viewer / re-extract
  // wiring; the Documents tab is the canonical surface for per-doc
  // affordances post-EDX.

  // EDX-3/D06 — the saved/planned report scope the CoverageMatrix walks. The
  // ranked DM3 match (`matchByType`) is computed ONCE by the page over this
  // same resolved scope supplied here; standalone callers retain the saved
  // fallback. The single run hydrates the
  // matrix cells as Firestore-cached scores come back.
  const scopeReportTypes = React.useMemo<ReportTypeId[]>(
    () => coverageReportTypes ? [...coverageReportTypes] : siteReportTypeScope(siteReports),
    [siteReports, coverageReportTypes],
  );
  const checklistItems = React.useMemo(
    () => getDocumentChecklistAcross(scopeReportTypes),
    [scopeReportTypes],
  );
  const checklistById = React.useMemo(
    () => new globalThis.Map(checklistItems.map(item => [item.id, item])),
    [checklistItems],
  );
  /**
   * Look a requirement up across EVERY active/beta report type.
   *
   * `checklistById` above is scoped to the types this site has already
   * produced, which is right for "what does this report still need". It is
   * wrong for authoring evidence about the SITE: a SED section exists whether
   * or not the owning report type has ever been run, so scoping the lookup
   * dead-ends the Author action on a requirement the site genuinely has.
   */
  const checklistItemFromAllTypes = React.useCallback((requirementId: string): ChecklistItem | null => {
    try {
      const all = getDocumentChecklistAcross(
        Object.values(REPORT_TYPE_REGISTRY)
          .filter(t => t.status === 'active' || t.status === 'beta')
          .map(t => t.id) as ReportTypeId[],
      );
      return all.find(i => i.id === requirementId) ?? null;
    } catch {
      return null;
    }
  }, []);

  /**
   * DC-1 — DAM hydration for the CoverageMatrix lineage chip strip.
   *
   * CAF (row 0.99256) wired the tier-attribution badges + chip strip
   * but the `dam` prop on `<CoverageMatrix>` was unfilled here, so
   * `RequirementLineage.counts.damSources` projected to zero on every
   * row and the chip strip rendered an empty "D" channel. DC-1 fills
   * that gap by lazy-loading the current DAM for the primary report
   * type via the same `loadCurrentDam` reader DamLineageChip uses
   * (Firestore-cached; no new fetcher).
   *
   * `loadCurrentDam` is a no-op when the DAM has never been built for
   * this report type, returning `null` — the chip strip stays dormant
   * in that case but the DAM-ref count is honest (0/0 rather than
   * "data missing"). The fetch is fire-and-forget; failures fall
   * through to `null` so the matrix renders unchanged.
   *
   * Re-runs only when `primaryReportType` changes (one chosen per
   * `scopeReportTypes[0] ?? 'feasibility'`). DamLineageChip handles
   * version-specific reads — for the chip-strip "how many refs" count
   * we only need the latest.
   */
  const [dam, setDam] = React.useState<DatacrateModel | null>(null);
  const primaryReportType: ReportTypeId = scopeReportTypes[0] ?? 'feasibility';
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await import('../../services/dam/damStorage');
        // loadCurrentDam accepts the broader ReportType union; cast
        // through ReportTypeId is safe (every ReportTypeId is a
        // ReportType).
        const loaded = await mod.loadCurrentDam(primaryReportType as Parameters<typeof mod.loadCurrentDam>[0]);
        if (!cancelled) setDam(loaded);
      } catch (e) {
        // Hydration is best-effort. A read failure means the chip
        // strip's DAM channel stays dormant — strictly no worse than
        // pre-DC-1 behaviour. Logged under the cancellation guard so
        // unmount during fetch doesn't spam the console.
        if (!cancelled) {
          console.debug('[CoverageMatrix] DAM hydration failed:', (e as Error)?.message);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [primaryReportType]);

  // EV3 (2026-05-15) — the `?filterReportId={id}` deep-link param is
  // read on mount and on every popstate so back/forward buttons
  // restore the right scope. Clicking the in-chip "Clear filter"
  // resets it.
  const [filterReportId, setFilterReportId] = React.useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('filterReportId');
  });
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPop = () => {
      setFilterReportId(new URLSearchParams(window.location.search).get('filterReportId'));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // Locate the report the filter is scoped to (if any). Reports are
  // already loaded into `siteReports` by the parent — no extra fetch
  // needed.
  const filterReport = React.useMemo(
    () => filterReportId ? siteReports.find(r => r.id === filterReportId) : undefined,
    [filterReportId, siteReports],
  );

  /**
   * EV3 — the report-scoped variable browser. Reached from a report header's
   * "Site Evidence" breadcrumb as `?filterReportId=<id>`, which narrows the
   * browser to the variables that report cited.
   *
   * This survived the peer-tab retirement: it is a live deep-linked feature,
   * not dormant code. The scope now applies to the SEDVariableBrowser folded
   * into the coverage pane instead of the removed `variables` pane, and the
   * deep-link auto-expands that disclosure (see the effect below) so the link
   * never lands on a collapsed section.
   *
   * `requirementScopedPaths` DID go: `handleViewRequirementVariables` now
   * expands the requirement in place rather than filtering a separate browser,
   * so nothing could set it.
   */
  const scopedVariablePaths = React.useMemo(
    () => filterReportId && sed ? computeScopedVariablePaths(sed, filterReport) : undefined,
    [filterReportId, sed, filterReport],
  );
  const activeVariableScope = scopedVariablePaths;

  // A scoped deep-link must open the disclosure it scopes.
  React.useEffect(() => {
    if (filterReportId) setActiveEvidencePane('coverage');
  }, [filterReportId]);

  /**
   * "View variables" from the requirement worksheet.
   *
   * The legacy branch routed to the `variables` peer tab with a scoping
   * filter. That pane no longer exists (variables fold into this one), so the
   * branch would have left the user on a redirected pane with no scope applied
   * — a dead affordance. There is now ONE behaviour: expand the requirement's
   * variables in place, which is where they already render.
   */
  const handleViewRequirementVariables = React.useCallback((requirementId: string, fieldPath?: string) => {
    setFocusRequirementId(requirementId);
    if (fieldPath) {
      setPanelFocusVariablePath(requirementFieldPathToVariablePath(fieldPath));
      setPanelVariablesExpanded(true);
    } else {
      setPanelVariablesExpanded(true);
    }
    setActiveEvidencePane('coverage');
  }, []);

  const handleClearReportFilter = React.useCallback(() => {
    if (typeof window !== 'undefined') {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('filterReportId');
        window.history.replaceState({}, '', url.toString());
      } catch { /* ignore */ }
    }
    setFilterReportId(null);
  }, []);

  // STL-1 (2026-05-18) — distinguish "stale deep-link to a deleted /
  // never-existed report" (Case B) from "legacy report on this site
  // that predates per-report citation tracking" (Case A). Case B used
  // to render a loud red-warning banner with copy "Report {id} could
  // not be found on this site." — alarming the user for a plumbing
  // artifact they can't act on. STL-1 silently strips the URL param
  // and falls through to the unscoped full-SED view instead.
  //
  // Race-condition guard: only treat as stale once the reports list
  // for this site has been hydrated (`siteReports.length > 0`). On a
  // first paint where the reports list is still loading both
  // `filterReport` and `siteReports` may be empty — auto-clearing
  // then would race the load and discard a valid deep-link.
  const isStaleLink = React.useMemo(
    () => Boolean(filterReportId) && !filterReport && siteReports.length > 0,
    [filterReportId, filterReport, siteReports.length],
  );

  // One-shot auto-clear ref. Re-arms only when the component remounts;
  // a fresh stale id in a future URL is auto-cleared the next time the
  // tab opens, but within a single mount the clear fires exactly once
  // (prevents render-loop spam if upstream state thrashes).
  const staleLinkClearedRef = React.useRef(false);
  React.useEffect(() => {
    if (!isStaleLink || staleLinkClearedRef.current) return;
    staleLinkClearedRef.current = true;
    const staleId = filterReportId; // capture before clear nukes state
    handleClearReportFilter();
    // Best-effort audit emit so admin sees aggregate stale-link rate.
    void (async () => {
      try {
        const { emitAuditEvent } = await import('../../lib/auditLogClient');
        void emitAuditEvent({
          action: 'site.evidence_tab.stale_link_cleared',
          target: { type: 'site', id: site.id, label: site.name },
          metadata: {
            staleReportId: staleId ?? '',
            siteName: site.name,
          },
        });
      } catch { /* audit is best-effort */ }
    })();
  }, [isStaleLink, filterReportId, site.id, site.name, handleClearReportFilter]);

  // Audit-log a best-effort "evidence_tab.opened_scoped" event when
  // the user lands here via a scoped deep-link. Fires once per
  // filterReportId change; admin telemetry only — never blocks UI.
  React.useEffect(() => {
    if (!filterReportId || !filterReport) return;
    void (async () => {
      try {
        const { emitAuditEvent } = await import('../../lib/auditLogClient');
        void emitAuditEvent({
          action: 'site.evidence_tab.opened_scoped',
          target: { type: 'site', id: site.id, label: site.name },
          metadata: {
            filterReportId,
            reportName: filterReport.siteName,
            reportType: filterReport.reportType ?? 'feasibility',
          },
        });
      } catch { /* audit is best-effort */ }
    })();
  }, [filterReportId, filterReport, site.id, site.name]);

  React.useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const sedId = site.sedId;
    const readOwnerUid = evidenceOwnerUid;
    setError(null);
    if (!sedId) {
      setSedSnapshot(null);
      setLoading(false);
      return;
    }
    setSedSnapshot(previous => previous?.scope === evidenceReadScope ? previous : null);
    setLoading(true);
    const timer = setTimeout(() => controller.abort(new Error(EVIDENCE_READ_TIMEOUT_MESSAGE)), EVIDENCE_READ_TIMEOUT_MS);
    (async () => {
      try {
        const { loadSedById } = await awaitAbortableRead(import('../../services/sedLoader'), controller.signal);
        // Pass site.sedStoragePath so the loader hits the
        // site-nested GCS path the bootstrap pipeline actually writes
        // to. Without this it reads from the legacy flat path and
        // silently 404s for any SED written since the site-nested
        // path landed.
        //
        // SED-VERSION-LABEL — thread `site.sedVersion` as the loader's
        // version key. The amend/top-up path (sedBootstrap.topUpSiteSed)
        // bumps `sed.version` and re-saves to the SAME sedId + storage
        // path, then writes the new `sedVersion` to the site doc. The
        // sedLoader cache is keyed on (uid, sedId, path, version); without
        // the version arg the post-bump load returned the STALE cached
        // doc (version=1) under the version-less key, so the CoverageMatrix
        // "SED v{n}" badge stuck at v1 while the activity feed (which reads
        // the live `site.sedVersion`) already showed v2. Passing the
        // version makes the bump a cache MISS → fresh GCS fetch → the
        // loaded doc's `.version` matches `site.sedVersion`. This loads the
        // ACTUAL current document, so the D0.1 drift invariant
        // (`report.sedVersionUsed === sed.version === site.sedVersion`) is
        // preserved — we never synthesize or read-prior-bump a version.
        const doc = await awaitAbortableRead(
          loadSedById(sedId, site.sedStoragePath, site.sedVersion, { signal: controller.signal }),
          controller.signal,
        );
        if (!doc) throw new Error('Saved evidence could not be loaded. Reload evidence to try again.');
        if (doc.id !== sedId || (site.sedVersion != null && doc.version !== site.sedVersion)) {
          throw new Error('The saved evidence read did not match the current document version. Reload evidence to try again.');
        }
        if (!cancelled && !controller.signal.aborted) {
          setSedSnapshot({ scope: evidenceReadScope, document: doc });
          onCurrentEvidenceLoaded?.({
            siteId: site.id,
            ownerUid: readOwnerUid,
            sedId,
            storagePath: site.sedStoragePath,
            version: site.sedVersion,
          });
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Saved evidence could not be loaded.');
      } finally {
        clearTimeout(timer);
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [site.id, evidenceOwnerUid, evidenceReadScope, site.sedId, site.sedStoragePath, site.sedVersion, reloadKey, onCurrentEvidenceLoaded]);

  // ──────────────────────────────────────────────────────────────────
  //  PRE-FOLLOWUP-AUTO-RETRY-SWEEPER (PA-1) — client-side sweeper.
  //
  //  Reads each section's `attemptResult` / `nextRetryAt` (stamped by
  //  PRE-3 + PA-2) and fires `refreshSedSections` for any section whose
  //  backoff has elapsed. Composes the existing section refresh
  //  pathway — no new fetcher, no new audit event, no new error
  //  surface.
  //
  //  Cost envelope:
  //    - One retry per section per session, tracked by
  //      `sweptThisSessionRef`. The ref persists across tab visits
  //      within a single tab mount; navigating away and back
  //      remounts the tab and resets the budget.
  //    - Per-visit fan-out cap (MAX_RETRIES_PER_VISIT = 3) clips
  //      simultaneous retries when more than 3 sections come due at
  //      once.
  //    - `nextRetryAt` itself is the wall-clock backoff (24h for
  //      empty, 1h for rate-limited, etc. — see
  //      `nextRetryFromBackoff`). The sweeper only honours
  //      already-elapsed timestamps; it never fires early.
  //
  //  Cancel-flag discipline: every async branch is gated on
  //  `!cancelled` before reading state, calling the store, or
  //  logging. Unmount during sweep results in zero
  //  console.* / refresh side-effects after the unmount tick.
  // ──────────────────────────────────────────────────────────────────
  const refreshSedSections = useStore(s => s.refreshSedSections);
  const sweptThisSessionRef = React.useRef<Set<SedSectionId>>(new Set());
  React.useEffect(() => {
    if (!sed) return;
    // A running build owns the evidence; a top-up now would be refused (or,
    // before the guard, race it). The sweep re-evaluates on the next SED.
    if (site.sedStatus === 'bootstrapping' || sedStatus === 'bootstrapping' || site.sedBootstrapLockStatus === 'locked') return;
    let cancelled = false;
    // Cancels provider work when the tab unmounts or the SED changes. A run
    // whose upload already started still finishes its pointer commit.
    const controller = new AbortController();
    (async () => {
      try {
        const allDue: RetryableSection[] = selectRetryableSections({
          sed,
          now: Date.now(),
          alreadyRetried: sweptThisSessionRef.current,
        });
        if (cancelled) return;
        const toSweep = applyPerVisitCap(allDue);
        if (toSweep.length === 0) return;
        // Stamp the session-Set BEFORE firing so a parallel re-render
        // can't double-trigger the same section. Mirrors the
        // optimistic-pin pattern used elsewhere in this tab.
        for (const row of toSweep) {
          sweptThisSessionRef.current.add(row.sectionId);
        }
        // One call per section so `refreshSedSections` can map each id
        // through `providersForSections` independently — keeps the
        // store action's audit-log emit per-section.
        for (const row of toSweep) {
          if (cancelled) return;
          try {
            const result = await refreshSedSections(site.id, [row.sectionId], { abortSignal: controller.signal });
            // The site's evidence moved under this tab: the remaining rows
            // were selected from a SED that is no longer current.
            if (result?.superseded) return;
            if (!cancelled) {
              // eslint-disable-next-line no-console
              console.info('[pre-auto-retry] swept', {
                sectionId: row.sectionId,
                provider: row.attemptedProvider,
                attemptResult: row.attemptResult,
                nextRetryAt: row.nextRetryAt,
              });
            }
          } catch (e) {
            if (!cancelled) {
              // eslint-disable-next-line no-console
              console.debug('[pre-auto-retry] section refresh failed', {
                sectionId: row.sectionId,
                message: (e as Error)?.message,
              });
            }
          }
        }
      } catch (e) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.debug('[pre-auto-retry] sweep aborted', {
            message: (e as Error)?.message,
          });
        }
      }
    })();
    return () => { cancelled = true; controller.abort(); };
    // Re-runs when a new SED loads (sed.id changes after rebuild /
    // section refresh) — that's the cue to re-evaluate whether any
    // sections still need sweeping. The session-Set guarantees we
    // never re-sweep the same section twice within one mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sed?.id, site.id, refreshSedSections]);

  // SED_Architecture_Unification SA3 — Rebuild SED moved here from the
  // Report viewer. The site owns the canonical SED, so this is the
  // dedicated surface for rebuilding it. Mirrors the SD2 pattern that
  // used to live on SEDSectionEditor (confirm gate + toast on click).
  const rebuildSiteSed = useStore(s => s.rebuildSiteSed);
  const [isRebuilding, setIsRebuilding] = React.useState(false);
  const [sedRebuildBlocked, setSedRebuildBlocked] = React.useState<SedRebuildBlockedState | null>(null);
  const [showForceRefreshPrompt, setShowForceRefreshPrompt] = React.useState(false);
  const handleRebuildSed = useCallback(async (forceRefresh = false) => {
    if (isRebuilding) return;
    // Failed bootstrap empty state has no sedId yet; route to the shared
    // retry entry point so "Retry rebuild" is always actionable.
    if (!site.sedId) {
      if (!onRetrySed) return;
      setIsRebuilding(true);
      try {
        await onRetrySed();
      } finally {
        setIsRebuilding(false);
      }
      return;
    }

    logAuditEvent({
      type: 'sed-rebuild-button-clicked',
      siteId: site.id,
      payload: { forceRefresh },
    });

    // R-32 freshness gate — skip rebuild if SED < 5 min old unless force.
    if (!forceRefresh) {
      const { evaluateSedBootstrapFreshness, resolveFreshnessThresholdMs } =
        await import('../../lib/sed/sedBootstrapCoordinator');
      const thresholdMs = resolveFreshnessThresholdMs(undefined, undefined);
      const { fresh, ageMs } = evaluateSedBootstrapFreshness(site, thresholdMs);
      if (fresh) {
        logAuditEvent({
          type: 'sed-rebuild-freshness-skipped',
          siteId: site.id,
          payload: { ageMs, thresholdMs },
        });
        setShowForceRefreshPrompt(true);
        return;
      }
    }

    setShowForceRefreshPrompt(false);
    setIsRebuilding(true);
    try {
      setSedRebuildBlocked(null);
      await rebuildSiteSed(site.id, { force: forceRefresh });
      toast.success('SED rebuild started…');
    } catch (e) {
      const { SedRebuildBlockedError } = await import('../../types/sedRebuild');
      if (e instanceof SedRebuildBlockedError) {
        logAuditEvent({
          type: 'sed-rebuild-blocked-shown',
          siteId: site.id,
          payload: {
            rebuildId: e.rebuildId,
            verifiedSectionsLost: e.diff.verifiedSectionsLost,
            completenessDelta: e.diff.completenessDelta,
          },
        });
        setSedRebuildBlocked({
          rebuildId: e.rebuildId,
          siteId: e.siteId,
          diff: e.diff,
          before: e.before,
          after: e.after,
        });
      } else {
        toast.error(`Rebuild failed: ${(e as Error)?.message ?? 'unknown error'}`);
      }
    } finally {
      setIsRebuilding(false);
    }
  }, [isRebuilding, site, onRetrySed, rebuildSiteSed]);

  // SDL-3 — Delete SED handler. Opens the confirmation modal; the
  // actual store call lands inside the dialog's `onConfirm` so the
  // dialog can render a busy state local to itself. Toast on success
  // mirrors the post-rebuild toast pattern.
  const deleteSiteSed = useStore(s => s.deleteSiteSed);
  const [showSedDelete, setShowSedDelete] = React.useState(false);
  const [isDeletingSed, setIsDeletingSed] = React.useState(false);
  // Pinned-report count for the cascade-safety preview. Reads from the
  // root reports list with a `.filter` outside the selector — Zustand
  // selector stability requires no derived shape inside the selector.
  const allReports = useStore(s => s.siteRag.reports);
  const reportsPinnedCount = React.useMemo(
    () => allReports.filter(r => r.siteId === site.id).length,
    [allReports, site.id],
  );
  // SCE-3 — wired into the `SedVsContextDiffTab` mounts below so each
  // diverged row can drop a typed-extractor-failure flag onto the
  // claim. Read-side surfaces (this diff tab, comparator, prompt
  // block) then filter the flagged row.
  const markClaimMisExtracted = useStore(s => s.markClaimMisExtracted);
  const adoptSedValueIntoContext = useStore(s => s.adoptSedValueIntoContext);
  const adoptContextValueIntoSed = useStore(s => s.adoptContextValueIntoSed);
  const handleAdoptSed = React.useCallback(async (row: SedContextDiffRow) => {
    try {
      await adoptSedValueIntoContext(site.id, row.identity, row.sedValue);
      setReloadKey(k => k + 1);
    } catch (e) {
      toast.error(`Failed to adopt SED value: ${(e as Error)?.message ?? 'unknown error'}`);
    }
  }, [adoptSedValueIntoContext, site.id]);
  const handleAdoptContext = React.useCallback(async (row: SedContextDiffRow) => {
    if (!row.claimId) return;
    try {
      await adoptContextValueIntoSed(site.id, row.identity, row.contextValue, row.claimId);
      setReloadKey(k => k + 1);
    } catch (e) {
      toast.error(`Failed to adopt context value: ${(e as Error)?.message ?? 'unknown error'}`);
    }
  }, [adoptContextValueIntoSed, site.id]);
  const docCount = site.documents?.length ?? 0;
  const rebuildActive = isRebuilding || sedStatus === 'bootstrapping';

  // SED-STALE-CARD — enriched "needs rebuild" signal for the coverage
  // console's version badge. The whole-SED `isStaleSed(site)` only fires
  // for SEDs built before the fixed data-quality cutoff (STALE_CUTOFF_DATE),
  // so a SED that's gone stale because (a) a source section is past its
  // per-section TTL, or (b) the user uploaded a document AFTER the SED was
  // last built, showed no "out of date" marker on the card. Reuse the
  // existing sedStaleness utilities — `computeSedSectionStatuses` (the same
  // per-section TTL classifier the Refresh-Evidence popover and preflight
  // freshness card consume) plus a documents-newer-than-SED check — instead
  // of inventing a new staleness model. Suppressed while a rebuild is in
  // flight so the tag doesn't flicker mid-rebuild.
  const sedNeedsRebuild = React.useMemo(() => {
    if (rebuildActive) return false;
    // 1. Data-quality cutoff staleness (legacy whole-SED check).
    if (isStaleSed(site)) return true;
    // 2. Any SED section past its per-section TTL.
    if (sed && computeSedSectionStatuses(sed).some(s => s.staleness === 'stale')) {
      return true;
    }
    // 3. Sources newer than the SED — a non-library document uploaded after
    //    the SED's last build means the evidence is no longer current.
    const builtAtMs = site.sedComputedAt ? Date.parse(site.sedComputedAt) : NaN;
    if (Number.isFinite(builtAtMs)) {
      const hasNewerDoc = siteDocuments.some(d => {
        if ((d.tags ?? []).includes('library-index')) return false;
        const up = d.uploadedAt ? Date.parse(d.uploadedAt) : NaN;
        return Number.isFinite(up) && up > builtAtMs;
      });
      if (hasNewerDoc) return true;
    }
    return false;
  }, [rebuildActive, site, sed, siteDocuments]);

  // NS-3 — admin-only gate for the DeleteSedSuggestionAlert. Same
  // source-of-truth selector pattern the RefreshEvidencePanel uses
  // (companyRole === 'admin'); see CLAUDE.md feedback on Zustand
  // selector stability — subscribing to the scalar root keeps this
  // free of derived shapes.
  const isAdmin = useStore(s => s.companyRole) === 'admin';
  // Generation/refresh COST telemetry is Datacrate internal — gate it on the
  // PLATFORM-admin predicate (platform `role` claim OR root-company admin), not
  // a tenant's own company-admin. `isAdmin` above (companyRole==='admin') still
  // owns the company-admin SED-delete suggestion below.
  const isCostAdmin = useIsDatacrateAdmin();

  const handleDeleteSed = () => {
    if (!site.sedId) return;
    setShowSedDelete(true);
  };
  const handleConfirmDeleteSed = async () => {
    if (!site.sedId) return;
    setIsDeletingSed(true);
    try {
      await deleteSiteSed(site.id);
      toast.success('SED deleted. Reports keep their snapshots.');
      setShowSedDelete(false);
    } catch (e) {
      toast.error(`Delete failed: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      setIsDeletingSed(false);
    }
  };

  // RC4 — auto-arm the rebuild flow when the user lands here from a
  // "Rebuild SED →" CTA toast. We check `?rebuild=1` once on mount; if
  // present, clear it from the URL so navigating back/forward doesn't
  // re-trigger, then call the same handler the button uses. The handler
  // confirms before doing anything, so the user still has a chance to
  // bail out.
  const armedFromToastRef = React.useRef(false);
  React.useEffect(() => {
    if (armedFromToastRef.current) return;
    if (typeof window === 'undefined') return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('rebuild') !== '1') return;
      armedFromToastRef.current = true;
      // Strip the rebuild param so a back/forward doesn't re-arm.
      params.delete('rebuild');
      const url = new URL(window.location.href);
      url.search = params.toString();
      window.history.replaceState({}, '', url.toString());
      // Fire the same confirm + rebuild path the button uses. We only
      // fire if the site already has a SED — the button is hidden for
      // new sites because the bootstrap is still running.
      if (site.sedId) {
        void handleRebuildSed();
      }
    } catch {
      // best-effort; the user can still click the button manually.
    }
    // We intentionally run this only on mount — re-running would re-fire
    // the confirm dialog every time site.sedId flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SET-Q1 — `completenessPct` no longer needed in header chips (single
  // source of truth is the toolbar `SedStatusPill`). The `sedBuiltAtMs`
  // freshness derivation left with the EvidenceReadinessHeader mount.

  /**
   * SET-B1 — Documents → Site Evidence bridge receiver. EDX-1 removed
   * the DocumentContentsPanel target (`data-testid="doc-contents-row-<id>"`)
   * that the original SET-B1 highlight aimed at. The receiver is kept
   * but now best-effort: it looks for any element carrying the doc id
   * on the Site Evidence tab (the SEDVariableBrowser's per-section
   * lineage drawer paints `data-doc-row-<id>` on its doc rows once
   * expanded). If nothing matches the URL still clears via
   * `onClearFocus` so back/forward doesn't re-fire stale highlights.
   * EDX-3 will retarget once the new top-level layout lands.
   */
  React.useEffect(() => {
    if (!focus || focus.kind !== 'doc') return;
    if (!sed) return;
    const docId = focus.id;
    let cleared = false;
    const handle = window.setTimeout(() => {
      try {
        const safe = CSS.escape(docId);
        const root = document.querySelector(
          `[data-testid="doc-contents-row-${safe}"], [data-doc-row-id="${safe}"]`,
        );
        if (!root) {
          // No target on this tab post-EDX. Clear so the URL param
          // doesn't get stuck across navigation.
          if (!cleared) {
            cleared = true;
            onClearFocus?.();
          }
          return;
        }
        root.scrollIntoView({ behavior: 'smooth', block: 'center' });
        root.classList.add('ring-2', 'ring-warning', 'ring-offset-2');
        window.setTimeout(() => {
          root.classList.remove('ring-2', 'ring-warning', 'ring-offset-2');
          if (!cleared) {
            cleared = true;
            onClearFocus?.();
          }
        }, 2800);
      } catch {
        // best-effort — focus is purely a visual nudge.
      }
    }, 250);
    return () => {
      window.clearTimeout(handle);
    };
  }, [focus, sed, onClearFocus]);

  const hasWaivedGaps = Object.keys(site.requirementWaivers ?? {}).length > 0;

  // Values and disagreements follow the evidence worksheet.
  const evidenceTabs = ([
    'coverage', 'actions',
  ] as EvidencePane[]).map(id => ({ id, ...EVIDENCE_TAB_META[id] }));

  // Decision and Path-to-GO links resolve directly to the worksheet or values.
  React.useEffect(() => {
    if (arrivalReturn) setActiveEvidencePane('coverage');
  }, [arrivalReturn]);

  // Keep deep-link/focus flows intact after moving heavy sections into
  // peer tabs. Variable/doc/conflict focus should land on the extracted
  // variables pane where those targets live — or the in-panel accordion
  // when the unified evidence panel flag is on.
  //
  // SKIPPED when the token resolved to a ledger task: the ledger has already
  // pinned and highlighted it above, so re-routing the pane underneath would
  // yank the user away from the card they were sent to.
  React.useEffect(() => {
    if (!focus || !sed) return;

    if (focus.kind === 'doc') {
      setActiveEvidencePane('coverage');
      return;
    }
    if (focus.kind === 'conflicts') {
      setActiveEvidencePane('coverage');
      return;
    }
    if (focus.kind === 'variable') {
      if (focus.id.includes('.')) {
        const path = focus.id.replace('.data.', '.');
        const dot = path.indexOf('.');
        const reqId = requirementIdForSedField(path.slice(0, dot), path.slice(dot + 1));
        if (reqId) setFocusRequirementId(reqId);
        setPanelFocusVariablePath(requirementFieldPathToVariablePath(path));
        setPanelVariablesExpanded(true);
        setActiveEvidencePane('coverage');
        return;
      }
      if (unifiedEvidencePanel) {
        setActiveEvidencePane('coverage');
        const dot = focus.id.indexOf('.');
        if (dot > 0) {
          const sectionId = focus.id.slice(0, dot);
          const fieldKey = focus.id.slice(dot + 1);
          const reqId = requirementIdForSedField(sectionId, fieldKey);
          if (reqId) setFocusRequirementId(reqId);
          setPanelFocusVariablePath(requirementFieldPathToVariablePath(focus.id));
          setPanelVariablesExpanded(true);
        } else if (focus.id) {
          // Path-to-GO steps deep-link with BARE ids (a checklist requirement
          // id from coverage rows, or a SED section/provider id from
          // preflight / sed-gap steps). Resolve to the owning requirement so
          // the Coverage row expands instead of landing pane-level with no
          // highlight. No match → pane-level focus, exactly as before.
          const reqId = requirementIdForBareFocusId(focus.id);
          if (reqId) setFocusRequirementId(reqId);
        }
      } else {
        // Same bare-id resolution as the unified branch above. Path-to-GO
        // steps deep-link with BARE ids, and a SED SECTION id has no row in
        // the Extracted Variables pane — sending it there lands the user on
        // a pane where the term they clicked appears zero times.
        //
        // The #1 GO-blocker `preflight:publicSentiment` did exactly that:
        // `?focus=variable:publicSentiment` routed here, and `publicSentiment`
        // is a provider SED section, not a variable row. It resolves cleanly
        // to the `community-feedback-summary` requirement
        // (`REQUIREMENT_TO_FIELDS` registers `publicSentiment.perTech` under
        // it), so route to Coverage and let the matrix's own deep-link
        // receiver select the row and expand its lane.
        //
        // Only BARE ids reroute. A dotted field path (`parcel.acres`) still
        // goes to Variables, which is where an individual field lives.
        const reqId = focus.id.includes('.')
          ? null
          : requirementIdForBareFocusId(focus.id);
        if (reqId) {
          setActiveEvidencePane('coverage');
          setFocusRequirementId(reqId);
        } else {
          setActiveEvidencePane('coverage');
        }
      }
    }
  }, [focus, sed, unifiedEvidencePanel]);

  // Redirect removed legacy panes to the evidence worksheet.
  React.useEffect(() => {
    if (
      activeEvidencePane === 'changes' || activeEvidencePane === 'open-items' || activeEvidencePane === 'variables' || activeEvidencePane === 'context'
    ) {
      setActiveEvidencePane('coverage');
    }
  }, [activeEvidencePane]);

  const handleDownloadSed = React.useCallback(() => {
    if (!sed) return;
    const blob = new Blob([JSON.stringify(sed, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeSite = site.name.replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '') || 'site';
    a.href = url;
    a.download = `SED_${safeSite}_v${site.sedVersion ?? 'current'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [sed, site.name, site.sedVersion]);

  return (
    <div className="space-y-6 w-full min-w-0" data-testid="site-evidence-tab-root">
      {/* Round4 readiness console — the gauge / status-mix / next-gap
          duties of the old EvidenceReadinessHeader moved INTO the
          CoverageMatrix console head (same SSOT rows, single sweep), so
          the header mount was removed as redundant. The Rebuild-SED
          button now renders on a slim toolbar above the sub-tab strip;
          the force-refresh prompt (sed-rebuild-freshness-prompt) renders
          directly beneath it. The RefreshEvidencePanel (hidden-trigger
          re-query popover) moved INTO the Actions pane next to its only
          opener (SiteEvidenceControlPanel.onOpenRequery) — its old
          always-rendered wrapper div sat as an invisible FIRST child of
          this space-y-6 root, which gave the tab a phantom ~24px top gap
          (founder live-QA, 2026-06-11). */}
      {/* SDL-3 — Delete SED confirmation modal. Mounted next to the
          actions menu (its trigger) so the dialog's busy flag stays scoped
          to this subtree. Reads `reportsPinnedCount` + `docCount` for
          the cascade-safety preview; both surface what survives a
          Delete SED rather than what is destroyed. */}
      <DeleteSedDialog
        site={site}
        open={showSedDelete}
        reportsPinnedCount={reportsPinnedCount}
        docCount={docCount}
        rebuildActive={rebuildActive}
        onConfirm={handleConfirmDeleteSed}
        onCancel={() => setShowSedDelete(false)}
      />
      {/* NS-3 SDL-FOLLOWUP-AUTO-SUGGEST-EMPTY-SED — admin-only Alert.
          Internal gates inside the component check role + hasSed +
          docCount + reportCount + dismissal, so we mount unconditionally
          and let the component decide whether to render. The CTA
          scrolls + focuses the SDL-1 Delete SED button on the
          toolbar above; non-destructive (Hard Rule 8) — the SDL-3
          dialog still confirms before any state change. */}
      <DeleteSedSuggestionAlert
        siteId={site.id}
        isAdmin={isAdmin}
        hasSed={Boolean(site.sedId)}
        docCount={docCount}
        reportCount={reportsPinnedCount}
      />
      {/* SA4 — in-flight rebuild banner. Persistent, aria-live so screen
          readers announce it, sourced from the per-site sedRebuildProgress
          field the bootstrap writes as each milestone lands. */}
      <SedRebuildBanner site={site} />
      <SedRebuildBlockedBanner
        site={site}
        blockedState={sedRebuildBlocked}
        onClearBlockedState={() => setSedRebuildBlocked(null)}
      />

      {!sedReady && !loading && !error && (
        <SedRebuildLandingCard
          sedStatus={sedStatus}
          isRebuilding={rebuildActive}
          onRebuild={() => void handleRebuildSed()}
        />
      )}

      {site.sedId && loading && !sed && (
        <div className="rounded-lg shadow-sm border border-border p-6 text-center type-caption">
          <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
          Loading evidence…
        </div>
      )}

      {site.sedId && loading && sed && (
        <div role="status" data-testid="site-evidence-reread-pending" className="rounded-lg border border-border bg-surface-1 p-3 type-body text-text-secondary">
          Updating saved evidence… Your review position is kept. Values below are from the last read until this update finishes.
        </div>
      )}

      {site.sedId && error && (
        <div role="alert" data-testid="site-evidence-read-error" className="rounded-lg shadow-sm border border-danger/40 bg-danger/10 p-4 type-body text-danger">
          <p>{error}</p>
          <p className="mt-1 text-text-secondary">{sed
            ? 'The update could not be read. Values below are from the last read and have not been verified against the saved update. Your review position is kept.'
            : 'Saved evidence has not been read; this does not mean evidence or constraints are absent.'}</p>
          {!sed && (
            <p className="mt-1 text-text-secondary">If reloading keeps failing, the saved evidence and the site record may disagree. Rebuilding collects the evidence again and replaces the saved copy.</p>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => setReloadKey(k => k + 1)}>Reload evidence</Button>
            {/* Forced: the freshness prompt only renders beside loaded evidence,
                so a non-forced rebuild would silently do nothing here. */}
            {!sed && (
              <Button
                variant="outline"
                size="sm"
                data-testid="site-evidence-read-error-rebuild"
                disabled={rebuildActive}
                onClick={() => void handleRebuildSed(true)}
              >
                Rebuild evidence
              </Button>
            )}
          </div>
        </div>
      )}


      {sedReady && (
      <section
        className="space-y-3 w-full min-w-0"
        data-testid="site-evidence-peer-tabs"
        aria-label="Site Evidence sections"
        aria-busy={loading}
        data-evidence-read-state={loading ? 'rereading' : error ? 'read-failed' : 'current'}
      >
          {/* Round4 — the readiness cockpit (gauge + status mix + next-gap)
              now lives INSIDE the CoverageMatrix console head on the
              coverage pane, derived from the same SSOT rows the lanes
              render. The old EvidenceReadinessHeader mount was removed as
              redundant; its Rebuild-SED card action has been moved to the
              CoverageMatrix next to the search bar. */}
          {/* Force-refresh prompt — was beside the floating button; now sits
              directly beneath the readiness card that hosts the Rebuild action.
              Same freshness-gate flow + telemetry. */}
          {showForceRefreshPrompt && (
            <div
              className="p-3 rounded-md border border-warning/40 bg-warning/5 text-xs text-text-primary space-y-2"
              data-testid="sed-rebuild-freshness-prompt"
            >
              <div>
                SED was rebuilt less than 5 minutes ago. Force a fresh rebuild anyway?
                This will re-fetch every provider{isCostAdmin ? ' — costs ~$0.30–0.80 per rebuild' : ''}.
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-[10px] uppercase tracking-widest"
                  onClick={() => { setShowForceRefreshPrompt(false); void handleRebuildSed(true); }}
                  data-testid="sed-rebuild-force-confirm"
                >
                  Force rebuild
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-[10px] uppercase tracking-widest"
                  onClick={() => setShowForceRefreshPrompt(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          <ResponsiveTabStrip
            ariaLabel="Site Evidence section tabs"
            tabs={evidenceTabs.map(tab => ({
              id: tab.id,
              label: tab.label,
              shortLabel: tab.shortLabel,
              icon: tab.icon,
              disabled: tab.requiresSed && !sedReady,
              title: tab.requiresSed && !sedReady ? 'Build or load a SED to use this tab.' : undefined,
              testId: `site-evidence-tab-${tab.id}`,
              tabDomId: `site-evidence-tab-${tab.id}`,
              controlsId: `site-evidence-panel-${tab.id}`,
            }))}
            activeId={activeEvidencePane}
            onSelect={id => setActiveEvidencePane(id as EvidencePane)}
          />

          {activeEvidencePane === 'actions' && (
            <div
              id="site-evidence-panel-actions"
              role="tabpanel"
              aria-labelledby="site-evidence-tab-actions"
              className="space-y-3 rounded-md border border-border-subtle bg-surface-1/30 p-4"
              data-testid="site-evidence-panel-actions"
            >
              <div>
                <h3 className="text-sm font-semibold text-text-primary">Evidence Controls</h3>
                <p className="text-xs text-text-muted mt-1">
                  Manage the evidence document, refresh its sources and review recorded waivers.
                </p>
              </div>
              {/* Re-query popover (hidden trigger) — mounted beside its only
                  opener so the anchor sits where the user clicked, and so
                  the mount never adds an invisible layout child to the tab
                  root (the old top-of-tab wrapper caused a phantom gap). */}
              <RefreshEvidencePanel
                site={site}
                isAdmin={isCostAdmin}
                {...(onRetrySed ? { onRetrySed } : {})}
                open={requeryOpen}
                onOpenChange={setRequeryOpen}
                hideTrigger
              />
              <SiteEvidenceControlPanel
                site={site}
                sedStatus={sedStatus}
                hasGeometry={hasGeometry ?? false}
                onRetrySed={onRetrySed}
                onOpenRequery={() => setRequeryOpen(true)}
                onReloadSed={() => setReloadKey(k => k + 1)}
                isReloading={loading}
                isRebuilding={isRebuilding}
                onDeleteSed={handleDeleteSed}
                isDeletingSed={isDeletingSed}
                onDownloadSed={handleDownloadSed}
                onExportKMZ={onExportKMZ ?? (() => undefined)}
                onUpdateKMZ={onUpdateKMZ ?? (() => undefined)}
                onOpenGoogleEarth={onOpenGoogleEarth}
                onEditLayers={onEditLayers}
              />
            {/* ── Waived gaps ──────────────────────────────────────────────────
                Every waiver, with its reason, who recorded it and when — plus the
                way back. A waiver is an attested claim about what is NOT known, so
                burying it would defeat the point: a reader has to be able to see
                what was dispositioned and on what grounds. It also has to be
                reversible; the first waiver recorded on this site had to be removed
                with a raw Firestore call because nothing in the UI could undo it. */}
              {hasWaivedGaps && (
              <section
                className="rounded-lg border border-border bg-surface-1 p-4 sm:p-5 space-y-3"
                data-testid="waived-gaps-section"
                aria-label="Waived evidence gaps"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="type-section text-text-primary">Waived gaps</h3>
                  <span className="type-body text-text-muted" data-testid="waived-gaps-count">
                    {Object.keys(site.requirementWaivers ?? {}).length} recorded
                  </span>
                </div>
                <p className="type-body text-text-muted">
                  These are attested findings that the evidence cannot be obtained — not
                  hidden rows. They stay on the record and can be undone.
                </p>
                <ul className="space-y-2" data-testid="waived-gaps-list">
                  {Object.values(site.requirementWaivers ?? {}).map(w => {
                    const item = checklistById.get(w.requirementId)
                      ?? checklistItemFromAllTypes(w.requirementId);
                    return (
                      <li
                        key={w.requirementId}
                        className="rounded-md border border-border-subtle bg-background p-3"
                        data-testid={`waived-gap-${w.requirementId}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="type-body font-semibold text-text-primary break-words">
                              {item?.label ?? w.requirementId}
                            </div>
                            <div className="type-body text-text-muted">
                              {WAIVE_CATEGORY_LABELS[w.category] ?? w.category}
                              {' · '}
                              {w.waivedBy}
                              {w.waivedAt ? ` · ${new Date(w.waivedAt).toLocaleDateString()}` : ''}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 shrink-0"
                            data-testid={`waived-gap-undo-${w.requirementId}`}
                            onClick={() => {
                              void (async () => {
                                try {
                                  await writeWaiver(w.requirementId, null, null);
                                  toast.success('Waiver removed — the gap is back on the worklist.');
                                } catch (e) {
                                  toast.error((e as Error)?.message ?? 'Could not remove the waiver.');
                                }
                              })();
                            }}
                          >
                            Undo waiver
                          </Button>
                        </div>
                        {w.reason && (
                          <p className="mt-2 type-body text-text-secondary break-words">{w.reason}</p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>

              )}

            </div>
          )}

          {(
            <div
              id="site-evidence-panel-coverage"
              role="tabpanel"
              aria-labelledby="site-evidence-tab-coverage"
              className="min-w-0 rounded-md border border-border-subtle bg-surface-1/30 p-3 sm:p-4"
              data-testid="site-evidence-panel-coverage"
              /* Always mounted (hidden when another tab is active): the
                 Coverage Matrix reports the ledger's fill summary and hosts
                 the deep-link receivers, so it must exist before its tab is
                 shown. */
              hidden={activeEvidencePane !== 'coverage'}
            >

              {sed && filterReportId && filterReport && <details open className="mb-3 border-b border-border-subtle pb-3"
                data-testid="report-source-details">
                <summary className="cursor-pointer text-xs text-text-secondary">Sources linked to this report</summary>
                {scopedVariablePaths && scopedVariablePaths.size === 0 && <p className="mt-2 text-xs text-text-muted"
                  data-testid="sed-variable-filter-no-data-banner">
                  No field-level citation map is available for this report. Showing all site evidence for inspection.
                </p>}
                <SEDVariableBrowser sed={sed} siteId={site.id} showSourceBadges={false}
                  onReloadSed={() => setReloadKey(k => k + 1)} scopedVariablePaths={activeVariableScope}
                  focusVariablePath={focusVariablePath} reportLabel={`${filterReport.siteName} v${filterReport.version}.${filterReport.minorVersion ?? 0}`}
                  onClearFilter={handleClearReportFilter} siteContext={site.siteContext ?? null}
                  documents={siteDocuments} onOpenDocument={onOpenDocument} />
              </details>}
              <CoverageMatrix
                renderTopicSourceDetails={requirementId => {
                  const requirement = checklistById.get(requirementId) ?? checklistItemFromAllTypes(requirementId);
                  if (!sed || !requirement) return null;
                  const scope = evidenceSourceScope({ requirement, stage: evidenceReviewStage, sed });
                  return <div className="space-y-3" data-testid="coverage-topic-source-details">
                    <EvidenceConflictsPanel sed={sed} siteId={site.id} sedStoragePath={site.sedStoragePath}
                      documents={siteDocuments} scopedFieldPaths={scope.fieldPaths} scopedSectionIds={scope.sectionIds}
                      focusOnMount={focus?.kind === 'conflicts'} onResolved={() => setReloadKey(k => k + 1)}
                      onViewVariable={(sectionId, fieldKey) => handleViewRequirementVariables(requirementId, `${sectionId}.${fieldKey}`)} />
                    <SedVsContextDiffTab site={site} sed={sed} scopedFieldPaths={scope.fieldPaths}
                      scopedSectionIds={scope.sectionIds} onAdoptSed={handleAdoptSed} onAdoptContext={handleAdoptContext}
                      onMarkMisExtracted={async claimId => { await markClaimMisExtracted(site.id, claimId); setReloadKey(k => k + 1); }} />
                  </div>;
                }}
                documents={siteDocuments}
                siteId={site.id}
                sed={sed ?? undefined}
                isStaleSed={sedNeedsRebuild}
                sedRebuiltAt={site.lastBootstrapAt}
                supplyChainPlan={site.supplyChainPlan}
                environmentalPreferences={site.typedPreferencesDefaults?.environmental}
                typedPreferences={site.typedPreferencesDefaults}
                onOpenSiteIntent={onOpenSiteIntent}
                scopeReportTypes={scopeReportTypes}
                decisionStage={evidenceReviewStage}
                onEvidenceStageChange={stage => setEvidenceReviewOverride({ siteId: site.id, stage })}
                matchByType={matchByType}
                disconnectedLinks={site.userDisconnectedLinks ?? []}
                onOpenInDocuments={(reqId) => onOpenInDocuments?.(reqId)}
                onOpenUploadFor={(reqId) => onOpenUploadFor?.(reqId)}
                onReplaceDoc={onReplaceDoc}
                onAddSupportingDoc={onAddSupportingDoc}
                authoredClaims={site.siteContext?.claims ?? []}
                coverageByRequirement={site.coverage}
                dam={dam}
                priorReports={siteReports}
                onViewVariables={handleViewRequirementVariables}
                panelFocusVariablePath={panelFocusVariablePath}
                forceExpandRequirementVariables={panelVariablesExpanded}
                focusRequirementId={focusRequirementId}
                siteContext={site.siteContext ?? null}
                projectIntent={site.projectIntent}
                onOpenDocument={onOpenDocument}
                onReloadSed={() => setReloadKey(k => k + 1)}
                unifiedEvidencePanel={unifiedEvidencePanel}
                hostOwnsAuthorDialog
                onOpenAuthorFor={(reqId) => {
                  setAuthoringFor(checklistById.get(reqId) ?? checklistItemFromAllTypes(reqId));
                }}
                onRebuildSed={site.sedId ? () => void handleRebuildSed(true) : undefined}
                isRebuilding={rebuildActive}
                hideReadinessConsole={false}
              />

            </div>
          )}



          {waivingGap && (
            <WaiveGapDialog
              gapLabel={waivingGap.label}
              requirementId={waivingGap.requirementId}
              onCancel={() => setWaivingGap(null)}
              onConfirm={async (reason, category) => {
                try {
                  await writeWaiver(waivingGap.requirementId, reason, category);
                  toast.success('Waiver recorded. It stays visible in the evidence and can be undone.');
                  setWaivingGap(null);
                } catch (e) {
                  toast.error((e as Error)?.message ?? 'Could not record the waiver.');
                }
              }}
            />
          )}
          {authoringFor && (
            <AuthorEvidenceDialog
              siteId={site.id}
              requirementId={authoringFor.id}
              requirementLabel={authoringFor.label}
              schema={checklistById.get(authoringFor.id)?.authoredSchema
                ?? checklistItemFromAllTypes(authoringFor.id)?.authoredSchema
                ?? authoringFor.authoredSchema ?? []}
              decisionStage={evidenceReviewStage}
              existing={(site.siteContext?.claims ?? []).find(
                c => c.requirementId === authoringFor.id,
              )}
              prefillFromLineage={getRequirementLineage(authoringFor.id, {
                site: {
                  siteContext: { claims: site.siteContext?.claims ?? [] },
                  documents: siteDocuments,
                },
                sed: sed ?? undefined,
                reportType: scopeReportTypes[0] ?? 'feasibility',
                scopeReportTypes,
                dam: dam ?? undefined,
                matchByType,
                priorReports: siteReports,
                requirementLabel: authoringFor.label,
                checklistItem: authoringFor,
              })}
              sed={sed ?? undefined}
              documents={siteDocuments}
              onClose={() => setAuthoringFor(null)}
            />
          )}
      </section>
      )}
    </div>
  );
};

/* SET-M2 — `DocContentSection` removed. Its responsibility (per-doc
 * `DocContentCard` list under a collapsible header) is now hosted by
 * the Cards tab inside `DocumentsInSED`. The cards themselves are
 * unchanged. */

/* ────────────────────────────────────────────────────────────────────
 * AutoRerunBanner — Documents_On_Site Prompt 13-C.
 *
 * When a freshly-uploaded doc satisfies a previously-missing checklist
 * slot AND the latest report of that type is more than 7 days old,
 * surface a passive banner suggesting a regenerate. Click runs
 * generateTypedReport with mode='update' (the disambiguation step
 * resolves the parent report id when the user confirms).
 * ──────────────────────────────────────────────────────────────────── */
const AutoRerunBanner: React.FC<{ site: SiteEntity; reports: SiteRagReport[] }> = ({ site, reports }) => {
  const [dismissed, setDismissed] = React.useState<Set<string>>(() => {
    try {
      const raw = sessionStorage.getItem(`auto-rerun-dismiss-${site.id}`);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch { return new Set(); }
  });
  const candidates = React.useMemo(
    () => detectAutoRerunCandidates({ site, reports }).filter(c => !dismissed.has(c.reportId)),
    [site, reports, dismissed],
  );
  // Rerun Reimagined RR7 — fast-path modal.
  // The banner click previously routed straight to the full wizard
  // (mode='update'). Now it opens a one-click confirmation modal whose
  // Regenerate CTA fires generateTypedReport with the parent's saved
  // typedPreferences / documentRefs / archetypeOverride directly,
  // skipping the wizard entirely. The "Edit before running" link in
  // the modal preserves the legacy wizard path for users who want to
  // override config.
  // React #300 fix — these hooks (and useNavigate below) must run on
  // every render, so they are hoisted ABOVE the early return. None of
  // them depend on `candidates`/`top`.
  const [fastPathOpen, setFastPathOpen] = React.useState(false);
  const [fastPathRunning, setFastPathRunning] = React.useState(false);
  // RWX-5 — third rerun entry point (per pass-2 #11). The
  // AutoRerunBanner is one of the doors into the rerun surface
  // (ReportHeaderStrip RERUN button, this banner; the report page's
  // ReportAlertBanner "Rerun →" CTA went with the report-page banners on
  // 2026-10-13). Pre-RWX, this entry skipped the wizard entirely via
  // the fast-path modal; post-RWX, "Edit before running" routes to
  // the new canonical /sites/<id>/reports/<id>/rerun URL so the user
  // gets one consistent rerun surface regardless of entry point.
  const rerunBannerNavigate = useNavigate();
  if (candidates.length === 0) return null;
  const top = candidates[0]!;
  const meta = REPORT_TYPE_REGISTRY[top.reportType];
  const docName = top.triggeringDocs[0]?.fileName ?? 'document';

  const handleDismiss = () => {
    setDismissed(prev => {
      const next = new Set(prev);
      next.add(top.reportId);
      try { sessionStorage.setItem(`auto-rerun-dismiss-${site.id}`, JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };
  const openFastPath = () => setFastPathOpen(true);
  const handleEdit = () => {
    setFastPathOpen(false);
    // RWX-5 — canonical path. Navigate to the rerun route so the
    // browser back-button returns here. The legacy CustomEvent path
    // is preserved as a fallback for hosts that haven't migrated to
    // the BrowserRouter shell (the dashboard's standalone preview
    // surfaces, where useNavigate would no-op silently).
    try {
      rerunBannerNavigate(`/sites/${site.id}/reports/${top.reportId}/rerun`);
      return;
    } catch { /* fall through to event */ }
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('open-new-report-wizard', {
        detail: { siteId: site.id, reportTypeId: top.reportType, mode: 'update', parentReportId: top.reportId },
      }));
    }
  };
  const handleFastPathRegenerate = async () => {
    setFastPathRunning(true);
    try {
      const state = useStore.getState();
      // RED-ERROR-CLEAR FIX (#8) — clear any stale global "Generation Failed"
      // banner the instant the user regenerates, before dispatch.
      state.clearGenerationError();
      const parent = state.siteRag.reports.find(r => r.id === top.reportId);
      if (!parent) throw new Error('Parent report not found');
      // Pull typedPreferences / documentRefs / archetypeOverride from
      // the parent so the fast-path rerun reproduces the framing —
      // identical defaults to the FeasibilityRerunPanel handleRerun
      // pipeline.
      const defaults = getRerunDefaults(parent);
      const documentSelection = defaults.documentRefs && defaults.documentRefs.length > 0
        ? { documentRefs: defaults.documentRefs, documentSelectionMode: 'mixed' as const }
        : undefined;
      // v1.3.4 (#24) — auto-rerun is a REFRESH of the existing report (new
      // docs arrived), not an A/B fork. It must `update` the same lineage —
      // bump the version + stamp `parentReportId` — so it converges into the
      // single definitive card, never a parallel "Alt" root. ('add-another'
      // here was the root cause of the stray `<type> v1.0 ALT #2` entries:
      // runTypedReportGeneration only links the parent for update/replace, so
      // 'add-another' minted a fresh orphan root of the same type.)
      await state.generateTypedReport(
        site.id,
        top.reportType,
        defaults.typedPreferences ?? {},
        'update',
        top.reportId,
        defaults.archetypeOverride,
        documentSelection,
      );
      // Public Sentiment held for site evidence: the store's notice explains
      // it and nothing has started, so no "started" toast or navigation.
      // Waiting runs start automatically, so the banner is done; a hold keeps it.
      const sentimentHold = useStore.getState().publicSentimentEvidenceDisposition({ siteId: site.id, reportTypeId: top.reportType });
      if (sentimentHold) {
        setFastPathOpen(false);
        if (sentimentHold === 'waiting') handleDismiss();
        return;
      }
      const rerunTargetReportId = state.siteRag.reports
        .filter((r) => (
          r.siteId === site.id
          && (r.reportType ?? 'feasibility') === top.reportType
          && (r.parentReportId === top.reportId || r.id === top.reportId)
        ))
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]?.id ?? top.reportId;
      rerunBannerNavigate(`/sites/${site.id}?tab=reports&rerunReportId=${rerunTargetReportId}`);
      toast.success('Fast-path rerun started');
      setFastPathOpen(false);
      handleDismiss();
    } catch (e) {
      toast.error(`Fast-path rerun failed: ${(e as Error).message}`);
    } finally {
      setFastPathRunning(false);
    }
  };

  return (
    <div
      className="mt-2 -mb-1 px-3 py-2 rounded-md border border-info/40 bg-info-subtle flex items-center gap-2 text-[10px]"
      data-testid="auto-rerun-banner"
    >
      <span className="text-blue-700 font-semibold uppercase tracking-widest type-body">New evidence</span>
      <span className="text-text-primary truncate flex-1">
        <span className="font-semibold">{docName}</span> strengthens the existing
        {' '}<span className="font-semibold">{meta?.shortLabel ?? top.reportType}</span> report ({top.ageDays}d old) — regenerate?
      </span>
      <Button
        variant="outline"
        size="sm"
        className="h-6 type-body uppercase tracking-widest"
        onClick={openFastPath}
        data-testid="auto-rerun-banner-regenerate"
      >
        <RefreshCw className="w-3 h-3 mr-1" /> Regenerate
      </Button>
      <button
        onClick={handleDismiss}
        className="text-text-muted hover:text-text-primary"
        title="Dismiss"
        aria-label="Dismiss"
      >
        <X className="w-3 h-3" />
      </button>
      <RerunFastPathModal
        open={fastPathOpen}
        title={`Regenerate ${meta?.shortLabel ?? top.reportType} report?`}
        summary={`${docName} strengthens this report (${top.ageDays}d old). One-click regenerates against the current evidence.`}
        details={top.triggeringDocs.map(d => `New document: ${d.fileName}`)}
        primaryLabel="Regenerate"
        editLabel="Edit before running"
        onClose={() => setFastPathOpen(false)}
        onRegenerate={() => void handleFastPathRegenerate()}
        onEdit={handleEdit}
        running={fastPathRunning}
      />
    </div>
  );
};

/* DataReadinessPill extracted to ./DataReadinessPill.tsx so
 * SiteRagDashboard can reuse the same chip on each top-ranked site
 * row. Imported above. */

/* PostBootstrapDocBanner removed 2026-05-09. The banner's per-doc
 * framing didn't survive bulk uploads (showed only the headline doc
 * with "+N more" buried in muted text; the Refresh button forwarded
 * just the headline doc's id and silently dropped the rest), and its
 * intent is now covered by RefreshEvidencePanel + PreflightFreshnessCard.
 * findPostBootstrapSuggestions also deleted in the same commit since
 * this was its only production caller. */
